import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Billboard, Line, OrbitControls, Text } from '@react-three/drei'
import * as THREE from 'three'
import { storage } from '../firebase'
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage'
import coverImage from '../assets/Cover.png'
import constructionImage from '../assets/Construction.png'
import deliveryImage from '../assets/Delivery.png'
import pickupImage from '../assets/Pickup.png'
import deliveryCoverImage from '../assets/DeliveryCover.png'
import pickupCoverImage from '../assets/PickupCover.png'
import tileImage from '../assets/Tile.png'
import UploadImage from '../assets/Upload.png'
import rectangularBaseImage from '../assets/landing/rectangular-base.png'
import triangularBaseImage from '../assets/landing/triangular-base.png'

const STEP_TOTAL = 8
const MM_MIN = 300
const MM_MAX = 1800
const COMBINED_LIMIT_MAX = 1200
const LEG_HEIGHT_MIN = 18
const LEG_HEIGHT_MAX = 50
const CUTOUT_FEE = 40
const DELIVERY_FEE = 55
const SPLIT_BASE_FEE = 100
const CUSTOM_QUOTE_MESSAGE = 'Dimensions exceeding 1800mm require a custom quote. Please contact our sales team at sales@equipsy.com.au for personalized pricing and ordering.'
const SETBACK_WARNING_MESSAGE = 'Setback exceeds 50mm. Large setbacks may compromise the stability of the base\'s edge. Please confirm this is required for your site.'
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
const GOOGLE_PLACES_AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete'
const GOOGLE_PLACES_DETAILS_URL = 'https://places.googleapis.com/v1/places'
const SAVE_DRAFT_URL = 'https://australia-southeast1-equipsy-au.cloudfunctions.net/saveShowerBaseDraft'
const CAPTURE_LEAD_URL = 'https://australia-southeast1-equipsy-au.cloudfunctions.net/captureShowerBaseLead'
const SUBMIT_ORDER_URL = 'https://australia-southeast1-equipsy-au.cloudfunctions.net/submitShowerBaseOrder'

const stepLabels = [
  'Base Dimensions',
  'Leg Setback Distance',
  'Leg Heights',
  'Drainage Information',
  'Cut-out Information',
  'Fabrication Details',
  'Additional Information',
  'Contact Details',
]

const footerStepLabelOverrides = {
  1: 'STEP 2: LEG SETBACK',
  2: 'STEP 3: LEG HEIGHTS',
  3: 'STEP 4: DRAINAGE',
  4: 'STEP 5: CUT-OUTS',
  5: 'STEP 6: FABRICATION',
  6: 'STEP 7: ADDITIONAL INFO',
  7: 'CONTACT DETAILS',
}

const getStepIconType = step => {
  if (step === 1 || step === 2 || step === 3) return 'ruler'
  if (step === 4) return 'droplet'
  if (step === 5) return 'cut'
  if (step === 6) return 'cut'
  if (step === 7) return 'upload'
  if (step === 8) return 'user'
  return 'grid'
}

const mobileAccordionClosedLabels = {
  1: 'Step 1: Dimensions',
  2: 'Step 2: Leg Setback',
  3: 'Step 3: Leg Heights',
  4: 'Step 4: Drain Measurements',
  5: 'Step 5: Cut-outs',
  6: 'Step 6: Fabrication Details',
  7: 'Step 7: Additional Information',
  8: 'Step 8: Contact Details',
}

const getPreviewHelpTitle = (currentStep, stepOneSummaryOpen) => {
  if (stepOneSummaryOpen) return 'Understanding Shower Base Inserts & Leg Supports'
  if (currentStep === 1) return 'Not sure about your shower dimensions?'
  if (currentStep === 2) return 'See how to measure your leg setbacks'
  if (currentStep === 3) return 'Measuring for leg heights'
  if (currentStep === 4) return 'Position your drain correctly'
  if (currentStep === 5) return 'Measuring for custom cut-outs'
  if (currentStep === 6) return 'See how to configure multi-piece bases'
  return 'Review and submit your order'
}

const getPreviewHelpDescription = (currentStep, stepOneSummaryOpen) => {
  if (stepOneSummaryOpen) return 'Learn about how our custom shower base inserts work, the purpose of adjustable leg supports, and how to achieve a perfectly level installation even on sloped shower floors.'
  if (currentStep === 1) return 'Get a better understanding of how to measure your shower base and support legs.'
  if (currentStep === 2) return 'Watch how to correctly set the distance for your outer support legs.'
  if (currentStep === 3) return 'Learn how to calculate each leg height for a perfectly level shower base.'
  if (currentStep === 4) return 'Provide exact drain dimensions and coordinates for the drainage opening.'
  if (currentStep === 5) return 'A 30-second guide to measuring width and depth for fixtures and pipes.'
  if (currentStep === 6) return 'Review split direction, installation order, and finger hole placement.'
  return 'Check every section before requesting an invoice or proceeding to payment.'
}

const getPreviewHelpKey = (currentStep, stepOneSummaryOpen) => {
  if (stepOneSummaryOpen) return 'step-summary'
  if (currentStep === 1) return 'base-dimensions'
  if (currentStep === 2) return 'leg-setbacks'
  if (currentStep === 3) return 'leg-heights'
  if (currentStep === 4) return 'drain-position'
  if (currentStep === 5) return 'custom-cutouts'
  if (currentStep === 6) return 'multi-piece-bases'
  return ''
}

const orderHelpContent = {
  'step-summary': {
    title: 'Understanding Shower Base Inserts & Leg Supports',
    description: 'Learn about how our custom shower base inserts work, the purpose of adjustable leg supports, and how to achieve a perfectly level installation even on sloped shower floors.',
    thumbnail: constructionImage,
    videoEmbedUrl: 'https://www.youtube.com/embed/GCF4UmzAMgM',
    sections: [
      {
        title: 'Why This Matters',
        items: [
          'Our system automatically generates a custom support grid based on your unique dimensions to prevent sagging.',
          'Because shower floors slope toward the drain, you will likely need different leg heights across the grid to keep your shower base perfectly level.',
          'Your longest legs will usually go where the floor is lowest, often closest to the drain.',
        ],
      },
      {
        title: "What You'll Need",
        items: ['Tools: Tape measure, long spirit level, and a pencil.'],
      },
      {
        title: 'How to Prepare & Plan',
        ordered: true,
        items: [
          'Look at the grid layout generated by your custom dimensions.',
          'Check the position of your floor drain relative to the base.',
          'Plan your leg setbacks so no support legs sit directly on or too close to the drain opening.',
          'Prepare to measure individual leg heights to match the specific slope of your floor.',
        ],
      },
    ],
    note: "Tip: Marking the grid layout lightly on your actual floor with a pencil makes the next step much easier.",
  },
  'base-dimensions': {
    title: 'Not sure about your shower dimensions?',
    description: 'Get a better understanding of how to measure your shower base and configure the right number of support legs.',
    thumbnail: constructionImage,
    videoEmbedUrl: 'https://www.youtube.com/embed/Qx9uNOx1omU',
    sections: [
      {
        title: 'Measurement Tips',
        items: [
          'Measure the internal width and depth of your shower space.',
          'Width is the measurement from left to right, parallel to the wall with the screen.',
          'Depth is the measurement from front to back, perpendicular to the screen wall.',
          'Double-check all measurements before submitting your order.',
        ],
      },
    ],
  },
  'square-corners': {
    title: 'How to identify square corners',
    description: 'Watch how to check if your shower corners are exactly 90° to ensure the base aligns perfectly with your walls.',
    thumbnail: coverImage,
    videoEmbedUrl: 'https://www.youtube.com/embed/Zu_HnblfqT0',
    sections: [
      {
        title: 'Why This Matters',
        items: [
          'A square corner is a true 90° angle.',
          'It acts as the anchor point for manufacturing your custom shower.',
          'Finding this spot lets us adjust for walls that are crooked or out of square.',
        ],
      },
      {
        title: 'Tools',
        items: ['Square corner ruler.'],
      },
      {
        title: 'How to Measure',
        ordered: true,
        items: [
          'Place your square ruler into each corner of the shower area.',
          'Find the corner that most closely matches a true 90-degree angle.',
          'Mark that specific corner as the square corner in the wizard.',
        ],
      },
    ],
    note: 'Tip: You only need one reliable square corner for us to calculate the rest.',
  },
  'rounded-corners': {
    title: 'How to measure rounded corners',
    description: 'Watch how to measure the rounded corners of your shower base.',
    thumbnail: tileImage,
    videoEmbedUrl: 'https://www.youtube.com/embed/lqOU4z8_Jw0',
    sections: [
      {
        title: 'Why This Matters',
        items: [
          'We need the exact corner radius to manufacture a perfect fit.',
          'This ensures the custom piece aligns seamlessly with the curve of your walls or base.',
        ],
      },
      {
        title: 'Tools',
        items: ['Tape measure and a straight edge such as a long ruler or level.'],
      },
      {
        title: 'How to Measure',
        ordered: true,
        items: [
          'Use your straight edge to find the point where the flat wall begins to curve.',
          'Imagine where the two straight walls would meet to form a sharp corner.',
          'Measure from that imaginary corner point back to the start of the curve to find the radius.',
          'Double-check the arc length if you want extra precision.',
        ],
      },
    ],
    note: 'Tip: Think of the radius as the distance from the edge of a circle straight to its centre point.',
  },
  'leg-setbacks': {
    title: 'See how to measure your leg setbacks',
    description: 'Watch how to correctly set the distance for your outer support legs.',
    thumbnail: constructionImage,
    sections: [
      {
        title: 'Why This Matters',
        items: [
          'Setback is the distance from the outer edge of your shower base to the edge of the support legs.',
          'Setting this correctly ensures the internal support structure clears any existing floor ridges or curbing.',
          'Keeping setbacks under 50mm is critical for base stability.',
        ],
      },
      {
        title: "What You'll Need",
        items: ['Tools: Blue masking tape, tape measure, pencil, and a calculator.'],
      },
      {
        title: 'How to Measure & Calculate',
        ordered: true,
        items: [
          'Check your floor for any curbing or ridges that the new base needs to clear.',
          'Use blue masking tape to mark exactly where each leg will sit on your floor.',
          'Keep your setback distance at or below 50mm for maximum stability.',
          'Calculate your leg spacing using: total width or depth minus total setbacks, divided by the number of rows or columns.',
        ],
      },
    ],
    note: 'Quick example: a 1300mm width with 50mm setbacks on each side leaves 1200mm open space, so a 4-column grid places legs 300mm apart.',
  },
  'leg-heights': {
    title: 'Measuring for leg heights',
    description: 'Learn how to calculate each leg height for a perfectly level shower base.',
    thumbnail: constructionImage,
    sections: [
      {
        title: 'Why This Matters',
        items: [
          'Each support leg needs a precise individual height because shower floors slope toward the drain.',
          'Your measurements tell our system the exact vertical distance needed for each grid coordinate.',
        ],
      },
      {
        title: "What You'll Need",
        items: ['Tools: Laser level, tape measure, and your blue masking tape marks from the setback step.'],
      },
      {
        title: 'How to Measure',
        ordered: true,
        items: [
          'Set up your laser level at a fixed height that clears the highest point of the floor.',
          'At each marked point, measure straight up from the floor to the laser line.',
          'Keep the tape measure perfectly vertical for an accurate reading.',
          'Add 15mm for the base thickness to get the total value to enter for that coordinate.',
        ],
      },
      {
        title: 'Important Math Rules',
        items: [
          'Total value entered equals the floor-to-laser measurement plus 15mm base thickness.',
          'The total entered height for any point must be at least 18mm.',
          'The tallest measurements are usually closest to the drain.',
        ],
      },
    ],
  },
  'drain-position': {
    title: 'Position your drain',
    description: 'Provide your drain\'s exact diameter and position so we can customize the base for optimal drainage.',
    thumbnail: deliveryCoverImage,
    sections: [
      {
        title: 'Why This Matters',
        items: [
          'Pinpointing your drain ensures your custom base arrives with a perfectly aligned plumbing cutout.',
          'It lets the system automatically move support legs out of the way to keep the drain path clear.',
        ],
      },
      {
        title: "What You'll Need",
        items: ['Tool: Tape measure.'],
      },
      {
        title: 'How to Measure',
        ordered: true,
        items: [
          'Measure the total width and depth of the physical drain opening.',
          'Measure from the front edge of the shower area straight to the centre of the drain.',
          'Measure from the left edge of the shower area straight to the centre of the drain.',
          'Check the 3D preview to confirm the cutout looks correct and clears all support legs.',
        ],
      },
    ],
    note: 'Note: Always measure to the centre point of the drain opening for the front and left distance fields.',
  },
  'custom-cutouts': {
    title: 'Measuring for custom cut-outs',
    description: 'A 30-second guide to measuring width and depth for fixtures and pipes.',
    thumbnail: coverImage,
    sections: [
      {
        title: 'Why This Matters',
        items: [
          'We build a safety buffer into every cut-out to prevent glass or fixtures from binding, rubbing, or cracking against the base.',
          'Adding a 10mm buffer creates a 5mm clearance gap on all sides.',
        ],
      },
      {
        title: "What You'll Need",
        items: ['Tool: Tape measure.'],
      },
      {
        title: 'How to Measure & Calculate',
        ordered: true,
        items: [
          'Select the cut-out side where the door or fixture will sit.',
          'Measure the real width and add 10mm.',
          'Measure the real depth and add 10mm.',
          'Choose the left or right reference edge when needed.',
          'Measure from that chosen edge to the exact centre point of the door frame or fixture position.',
        ],
      },
      {
        title: 'The Buffer Rule',
        items: [
          'Your measurement plus 10mm equals the final number to enter.',
          'Example: 10mm glass thickness plus 10mm buffer equals a 20mm final width.',
        ],
      },
    ],
  },
  'multi-piece-bases': {
    title: 'See how to configure multi-piece bases',
    description: 'Watch how to set the installation order and finger hole placement for bases created in two pieces.',
    thumbnail: pickupCoverImage,
    sections: [
      {
        title: 'Why This Matters',
        items: [
          'Splitting a large base into two pieces makes transport and installation easier.',
          'Setting the correct installation sequence ensures the pieces lock together properly.',
          'Finger holes are added to the removable front piece for lifting and maintenance access.',
        ],
      },
      {
        title: "What You'll Need",
        items: ['No tools needed. This is a digital configuration step.'],
      },
      {
        title: 'How to Configure',
        ordered: true,
        items: [
          'Check the box to split your base into two pieces.',
          'Select which piece will be installed first. Piece 1 always sits at the back of the shower floor.',
          'Review the front piece, which carries the removable finger-hole access.',
          'Add extra finger holes on the front, rear, left, or right if you want more grip points.',
        ],
      },
    ],
    note: "Tip: The back piece installed first doesn't need finger holes because the front piece is removed first for cleaning or maintenance.",
  },
}

const useMediaQuery = query => {
  const getMatches = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(query).matches
  }

  const [matches, setMatches] = useState(getMatches)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined

    const mediaQuery = window.matchMedia(query)
    const onChange = event => setMatches(event.matches)

    setMatches(mediaQuery.matches)
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onChange)
      return () => mediaQuery.removeEventListener('change', onChange)
    }

    mediaQuery.addListener(onChange)
    return () => mediaQuery.removeListener(onChange)
  }, [query])

  return matches
}

const PREVIEW_CAMERA_PRESETS = {
  default: {
    position: [0.94, 0.74, 1.08],
    target: [0, -0.02, 0],
  },
  bird: {
    position: [0, 1.46, 0.02],
    target: [0, 0, 0],
  },
  legHeights: {
    position: [0, -1.42, 0.02],
    target: [0, -0.22, 0],
  },
}

const sideOptions = ['Front', 'Left', 'Right']
const squareCornerOptions = ['Front Left', 'Front Right', 'Rear Left', 'Rear Right']
const squareCornerSelectorOptions = squareCornerOptions.map(option => ({
  label: `${option} (SQ)`,
  value: option,
}))
const cutoutTypes = [
  {
    id: 'screen-door',
    title: 'Shower Screen Door',
    description: 'For hinged or pivot shower doors that require cut-out space.',
    image: coverImage,
    mediaType: 'cover',
  },
  {
    id: 'screen',
    title: 'Shower Screen',
    description: 'For fixed glass panels or frameless shower screens.',
    image: tileImage,
    mediaType: 'photo',
  },
  {
    id: 'pipes',
    title: 'Pipes & Fixtures',
    description: 'For plumbing access, waste pipes, or bathroom fixtures.',
    mediaType: 'dark',
  },
  {
    id: 'corner-channel',
    title: 'Corner Channel/Profile',
    description: 'For corner trim, edge profiles, or wall channels.',
    image: tileImage,
    mediaType: 'photo',
  },
]

const cutoutFieldHelpText = {
  'corner-channel': {
    width: 'Measure the width of channel/notch + 5mm and enter',
    depth: 'Measure the depth of channel/notch + 5mm and enter',
  },
  pipes: {
    width: "Add 10mm to your fixture's width to ensure a 5mm gap on both the left and right sides. Enter the final total here.",
    depth: "Add 10mm to your fixture's depth to ensure a 5mm gap on both the left and right sides. Enter the final total here.",
  },
  screen: {
    width: 'Measure the width of the shower screen + 5mm and enter',
    depth: 'Measure the depth of the shower screen + 5mm and enter',
  },
  'screen-door': {
    width: 'Final width = door thickness + 10mm. This provides a 5mm buffer on both sides for a safe fit.',
    depth: 'Final depth = door thickness + 10mm. This provides a 5mm buffer on both sides for a safe fit.',
  },
}

const roundedCornerFields = [
  { label: 'Front Left Corner', key: 'frontLeft' },
  { label: 'Front Right Corner', key: 'frontRight' },
  { label: 'Rear Left Corner', key: 'rearLeft' },
  { label: 'Rear Right Corner', key: 'rearRight' },
]

const defaultSetbacks = () => ({
  front: 30,
  rear: 30,
  left: 30,
  right: 30,
})

const defaultDrain = () => ({
  width: 80,
  depth: 150,
  fromFront: 425,
  fromLeft: 400,
  note: '',
})

const defaultFabrication = () => ({
  splitBase: false,
  installationOrder: '',
  fingerHolesSingle: [],
  fingerHolesBase1: [],
  fingerHolesBase2: [],
})

const landingShapeTags = ['Rectangular', 'Square', 'Rounded Corners', 'Custom Sizes']
const createPlacesSessionToken = () => `sbo-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
const createDraftId = () => `sbo-draft-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
const getAddressComponent = (components, type) => {
  const match = (components || []).find(component => Array.isArray(component?.types) && component.types.includes(type))
  return match?.shortText || match?.longText || ''
}

const createCutout = (type = 'screen-door') => ({
  id: uid('cutout'),
  type,
  side: 'Front',
  hand: 'Left',
  screenPlacement: 'Side',
  width: 150,
  depth: 150,
  edgeOffset: 250,
  corner: 'Front Left',
  doorCenterOffset: 250,
  fixtureOffsetFromFront: 180,
  fixtureOffsetFromLeft: 180,
  leftEdgeEnabled: true,
  rightEdgeEnabled: false,
  leftEdgeDistance: 150,
  rightEdgeDistance: 150,
})

const ButtonChevron = ({ direction = 'right' }) => (
  <span className={`order-button-chevron${direction === 'left' ? ' left' : ''}`} aria-hidden="true">
    <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.244078 0.244078C0.569515 -0.0813592 1.09715 -0.0813592 1.42259 0.244078L7.25592 6.07741C7.58136 6.40285 7.58136 6.93049 7.25592 7.25592L1.42259 13.0893C1.09715 13.4147 0.569515 13.4147 0.244078 13.0893C-0.0813592 12.7638 -0.0813592 12.2362 0.244078 11.9107L5.48816 6.66667L0.244078 1.42259C-0.0813592 1.09715 -0.0813592 0.569515 0.244078 0.244078Z"
        fill="currentColor"
      />
    </svg>
  </span>
)

const iconPaths = {
  ruler: (
    <>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_13556_75833)">
          <path d="M17.7483 12.7488C17.9347 12.9345 18.0826 13.1553 18.1836 13.3984C18.2845 13.6415 18.3364 13.9021 18.3364 14.1653C18.3364 14.4285 18.2845 14.6891 18.1836 14.9322C18.0826 15.1753 17.9347 15.396 17.7483 15.5818L15.5818 17.7483C15.396 17.9347 15.1753 18.0826 14.9322 18.1836C14.6891 18.2845 14.4285 18.3364 14.1653 18.3364C13.9021 18.3364 13.6415 18.2845 13.3984 18.1836C13.1553 18.0826 12.9345 17.9347 12.7488 17.7483L2.24978 7.24929C1.87528 6.87297 1.66504 6.36367 1.66504 5.83276C1.66504 5.30185 1.87528 4.79255 2.24978 4.41623L4.41623 2.24978C4.79255 1.87528 5.30185 1.66504 5.83276 1.66504C6.36367 1.66504 6.87297 1.87528 7.24929 2.24978L17.7483 12.7488Z" stroke="#0B9FF9" stroke-width="1.6665" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M12.082 10.4167L13.7487 8.75" stroke="#0B9FF9" stroke-width="1.6665" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M9.58203 7.91667L11.2487 6.25" stroke="#0B9FF9" stroke-width="1.6665" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M7.08301 5.41667L8.74967 3.75" stroke="#0B9FF9" stroke-width="1.6665" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M14.582 12.9147L16.2487 11.248" stroke="#0B9FF9" stroke-width="1.6665" stroke-linecap="round" stroke-linejoin="round" />
        </g>
        <defs>
          <clipPath id="clip0_13556_75833">
            <rect width="19.998" height="19.998" fill="white" />
          </clipPath>
        </defs>
      </svg>

    </>
  ),
  grid: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  droplet: (
    <>
      <path d="M12 3.75C12 3.75 6.5 10.04 6.5 13.75C6.5 16.79 8.96 19.25 12 19.25C15.04 19.25 17.5 16.79 17.5 13.75C17.5 10.04 12 3.75 12 3.75Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9.25 14.5C9.25 15.98 10.44 17.25 11.92 17.25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  cut: (
    <>
      <path d="M6 9C7.65685 9 9 7.65685 9 6C9 4.34315 7.65685 3 6 3C4.34315 3 3 4.34315 3 6C3 7.65685 4.34315 9 6 9Z" stroke="#0B9FF9" strokeWidth="1.99997" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.11914 8.12109L11.9991 12.0011" stroke="#0B9FF9" strokeWidth="1.99997" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19.9991 4L8.11914 15.88" stroke="#0B9FF9" strokeWidth="1.99997" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.99994 21C7.65679 21 8.99994 19.6569 8.99994 18C8.99994 16.3431 7.65679 15 5.99994 15C4.34308 15 2.99994 16.3431 2.99994 18C2.99994 19.6569 4.34308 21 5.99994 21Z" stroke="#0B9FF9" strokeWidth="1.99997" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14.7988 14.8008L19.9988 20.0008" stroke="#0B9FF9" strokeWidth="1.99997" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  baseDimensions: (<>
    <g transform="translate(4.5 4.5)">
      <path d="M0.833008 1.66665C0.833008 1.44563 0.920805 1.23367 1.07709 1.07739C1.23337 0.921111 1.44533 0.833313 1.66634 0.833313H4.99967C5.22069 0.833313 5.43265 0.921111 5.58893 1.07739C5.74521 1.23367 5.83301 1.44563 5.83301 1.66665V7.49998C5.83301 7.72099 5.74521 7.93295 5.58893 8.08923C5.43265 8.24552 5.22069 8.33331 4.99967 8.33331H1.66634C1.44533 8.33331 1.23337 8.24552 1.07709 8.08923C0.920805 7.93295 0.833008 7.72099 0.833008 7.49998V1.66665ZM9.16634 1.66665C9.16634 1.44563 9.25414 1.23367 9.41042 1.07739C9.5667 0.921111 9.77866 0.833313 9.99967 0.833313H13.333C13.554 0.833313 13.766 0.921111 13.9223 1.07739C14.0785 1.23367 14.1663 1.44563 14.1663 1.66665V7.49998C14.1663 7.72099 14.0785 7.93295 13.9223 8.08923C13.766 8.24552 13.554 8.33331 13.333 8.33331H9.99967C9.77866 8.33331 9.5667 8.24552 9.41042 8.08923C9.25414 7.93295 9.16634 7.72099 9.16634 7.49998V1.66665ZM0.833008 10.8333C0.833008 10.6123 0.920805 10.4003 1.07709 10.2441C1.23337 10.0878 1.44533 9.99998 1.66634 9.99998H4.99967C5.22069 9.99998 5.43265 10.0878 5.58893 10.2441C5.74521 10.4003 5.83301 10.6123 5.83301 10.8333V13.3333C5.83301 13.5543 5.74521 13.7663 5.58893 13.9226C5.43265 14.0788 5.22069 14.1666 4.99967 14.1666H1.66634C1.44533 14.1666 1.23337 14.0788 1.07709 13.9226C0.920805 13.7663 0.833008 13.5543 0.833008 13.3333V10.8333ZM9.16634 10.8333C9.16634 10.6123 9.25414 10.4003 9.41042 10.2441C9.5667 10.0878 9.77866 9.99998 9.99967 9.99998H13.333C13.554 9.99998 13.766 10.0878 13.9223 10.2441C14.0785 10.4003 14.1663 10.6123 14.1663 10.8333V13.3333C14.1663 13.5543 14.0785 13.7663 13.9223 13.9226C13.766 14.0788 13.554 14.1666 13.333 14.1666H9.99967C9.77866 14.1666 9.5667 14.0788 9.41042 13.9226C9.25414 13.7663 9.16634 13.5543 9.16634 13.3333V10.8333Z" stroke="#155DFC" stroke-width="1.66664" stroke-linecap="round" stroke-linejoin="round" />
    </g>
  </>),

  box: (
    <>
      <path d="M12 3.75L19 7.5V16.5L12 20.25L5 16.5V7.5L12 3.75Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 10.25L19 7.5M12 10.25L5 7.5M12 10.25V20.25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  upload: (
    <>
      <path d="M12 15.5V5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8 9.5L12 5.5L16 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 17.5V18.25C5 18.94 5.56 19.5 6.25 19.5H17.75C18.44 19.5 19 18.94 19 18.25V17.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.25" r="3.25" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5.5 18.75C6.64 16.42 9.06 15 12 15C14.94 15 17.36 16.42 18.5 18.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  summary: (
    <>
      <path d="M7.25 5H16.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M7.25 9.5H14.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M7.25 14H16.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M7.25 18.5H12.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <rect x="4.5" y="3.5" width="15" height="17" rx="3" stroke="currentColor" strokeWidth="1.8" />
    </>
  ),
  entry: (
    <>
      <g clip-path="url(#clip0_13616_4087)">
        <path d="M9.16313 16.6602H1.66602" stroke="#0B9FF9" stroke-width="1.66602" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M9.16406 3.80054V17.2595C9.1641 17.386 9.19296 17.5109 9.24845 17.6246C9.30395 17.7383 9.38462 17.8379 9.48434 17.9158C9.58406 17.9937 9.70022 18.0478 9.82399 18.0741C9.94776 18.1004 10.0759 18.0982 10.1987 18.0675L15.8282 16.6606V4.63355C15.8281 4.26203 15.7039 3.9012 15.4752 3.60839C15.2465 3.31558 14.9266 3.1076 14.5661 3.01751L11.2341 2.18449C10.9886 2.12312 10.7323 2.11849 10.4847 2.17096C10.2371 2.22343 10.0047 2.33162 9.8052 2.48731C9.60567 2.643 9.44423 2.8421 9.33314 3.0695C9.22205 3.2969 9.16423 3.54745 9.16406 3.80054Z" stroke="#0B9FF9" stroke-width="1.66602" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M9.16311 3.33203H6.66407C6.22221 3.33203 5.79845 3.50756 5.48601 3.82C5.17357 4.13244 4.99805 4.5562 4.99805 4.99806V16.6602" stroke="#0B9FF9" stroke-width="1.66602" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M11.6621 9.99609H11.6704" stroke="#0B9FF9" stroke-width="1.66602" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M18.3272 16.6602H15.8281" stroke="#0B9FF9" stroke-width="1.66602" stroke-linecap="round" stroke-linejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_13616_4087">
          <rect width="19.9923" height="19.9923" fill="white" />
        </clipPath>
      </defs>
    </>
  ),
  corner: (
    <>
      <path d="M14.1667 2.50195H5.83333C3.99238 2.50195 2.5 3.99434 2.5 5.83529V14.1686C2.5 16.0096 3.99238 17.502 5.83333 17.502H14.1667C16.0076 17.502 17.5 16.0096 17.5 14.1686V5.83529C17.5 3.99434 16.0076 2.50195 14.1667 2.50195Z" stroke="#0B9FF9" stroke-width="1.66667" />
      <path d="M5.00041 6.25277C5.69099 6.25277 6.25081 5.69294 6.25081 5.00236C6.25081 4.31178 5.69099 3.75195 5.00041 3.75195C4.30983 3.75195 3.75 4.31178 3.75 5.00236C3.75 5.69294 4.30983 6.25277 5.00041 6.25277Z" fill="#0B9FF9" />
      <path d="M15.0004 6.25277C15.691 6.25277 16.2508 5.69294 16.2508 5.00236C16.2508 4.31178 15.691 3.75195 15.0004 3.75195C14.3098 3.75195 13.75 4.31178 13.75 5.00236C13.75 5.69294 14.3098 6.25277 15.0004 6.25277Z" fill="#0B9FF9" />
      <path d="M5.00041 16.2528C5.69099 16.2528 6.25081 15.6929 6.25081 15.0024C6.25081 14.3118 5.69099 13.752 5.00041 13.752C4.30983 13.752 3.75 14.3118 3.75 15.0024C3.75 15.6929 4.30983 16.2528 5.00041 16.2528Z" fill="#0B9FF9" />
      <path d="M15.0004 16.2528C15.691 16.2528 16.2508 15.6929 16.2508 15.0024C16.2508 14.3118 15.691 13.752 15.0004 13.752C14.3098 13.752 13.75 14.3118 13.75 15.0024C13.75 15.6929 14.3098 16.2528 15.0004 16.2528Z" fill="#0B9FF9" />
    </>
  ),
  fingerHoles: (
    <>
      <g clip-path="url(#clip0_12458_52061)">
        <path d="M14.9974 9.16732V5.00065C14.9974 4.55862 14.8218 4.1347 14.5092 3.82214C14.1967 3.50958 13.7728 3.33398 13.3307 3.33398C12.8887 3.33398 12.4648 3.50958 12.1522 3.82214C11.8397 4.1347 11.6641 4.55862 11.6641 5.00065" stroke="#0B9FF9" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M11.6693 8.33268V3.33268C11.6693 2.89065 11.4937 2.46673 11.1811 2.15417C10.8686 1.84161 10.4446 1.66602 10.0026 1.66602C9.56058 1.66602 9.13665 1.84161 8.82409 2.15417C8.51153 2.46673 8.33594 2.89065 8.33594 3.33268V4.99935" stroke="#0B9FF9" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M8.33333 8.75065V5.00065C8.33333 4.55862 8.15774 4.1347 7.84518 3.82214C7.53262 3.50958 7.10869 3.33398 6.66667 3.33398C6.22464 3.33398 5.80072 3.50958 5.48816 3.82214C5.17559 4.1347 5 4.55862 5 5.00065V11.6673" stroke="#0B9FF9" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M14.9994 6.66667C14.9994 6.22464 15.175 5.80072 15.4876 5.48816C15.8002 5.17559 16.2241 5 16.6661 5C17.1081 5 17.5321 5.17559 17.8446 5.48816C18.1572 5.80072 18.3328 6.22464 18.3328 6.66667V11.6667C18.3328 13.4348 17.6304 15.1305 16.3801 16.3807C15.1299 17.631 13.4342 18.3333 11.6661 18.3333H9.99943C7.6661 18.3333 6.24943 17.6167 5.00777 16.3833L2.00777 13.3833C1.72105 13.0658 1.56742 12.6501 1.57871 12.2225C1.58999 11.7948 1.76531 11.3878 2.06837 11.0858C2.37143 10.7838 2.77902 10.6099 3.20674 10.6002C3.63447 10.5904 4.04957 10.7455 4.3661 11.0333L5.83277 12.5" stroke="#0B9FF9" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_12458_52061">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </>
  ),
}

const StepGlyph = ({ type, className = '', size = 24 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {iconPaths[type] || iconPaths.grid}
  </svg>
)

const SectionHintIcon = ({ text, label = 'Section help' }) => (
  <span className="order-section-hint-icon">
    <span className="order-section-hint-wrap">
      <button
        type="button"
        className="order-quick-apply-help-trigger"
        aria-label={label}
      >
        ?
      </button>
      <span className="order-quick-apply-help-tooltip" role="tooltip">
        {text}
      </span>
    </span>
  </span>
)

const SectionChevron = ({ open = true }) => (
  <span className={`order-section-chevron${open ? ' is-open' : ''}`} aria-hidden="true">
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 8.75L7 4.75L11 8.75"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
)

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const preventScrollValueChange = event => {
  event.currentTarget.blur()
}

const toNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatMoney = value => `$${Number(value || 0).toFixed(2)}`

const uid = prefix => `${prefix}-${Math.random().toString(36).slice(2, 9)}`

const sanitizeForTransport = value => {
  if (value === undefined) return null
  if (value === null) return null
  if (Array.isArray(value)) return value.map(sanitizeForTransport)
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, nested]) => typeof nested !== 'function' && !(nested instanceof File))
        .map(([key, nested]) => [key, sanitizeForTransport(nested)])
    )
  }
  if (typeof value === 'number' && !Number.isFinite(value)) return null
  return value
}

const toUploadPayload = upload => ({
  id: upload.id,
  name: upload.name,
  size: upload.size,
  type: upload.type || '',
  previewUrl: upload.previewUrl || '',
  url: upload.url || '',
  storagePath: upload.storagePath || '',
  status: upload.status || 'pending',
})

const waitForPaint = () => new Promise(resolve => {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    resolve()
    return
  }
  window.requestAnimationFrame(() => window.requestAnimationFrame(resolve))
})

const priceTable = [
  { code: 'SBI-CUST1', wMin: 300, wMax: 950, dMin: 300, dMax: 950, cols: 3, rows: 3, legs: 9, price: 575 },
  { code: 'SBI-CUST2', wMin: 951, wMax: 1200, dMin: 300, dMax: 950, cols: 5, rows: 3, legs: 15, price: 675 },
  { code: 'SBI-CUST3', wMin: 300, wMax: 950, dMin: 951, dMax: 1200, cols: 3, rows: 5, legs: 15, price: 675 },
  { code: 'SBI-CUST4', wMin: 1201, wMax: 1500, dMin: 300, dMax: 950, cols: 5, rows: 3, legs: 15, price: 750 },
  { code: 'SBI-CUST4', wMin: 300, wMax: 950, dMin: 1201, dMax: 1500, cols: 3, rows: 5, legs: 15, price: 750 },
  { code: 'SBI-CUST5', wMin: 951, wMax: 1200, dMin: 951, dMax: 1200, cols: 5, rows: 4, legs: 20, price: 800, when: ({ width, depth }) => width >= depth },
  { code: 'SBI-CUST5', wMin: 951, wMax: 1200, dMin: 951, dMax: 1200, cols: 4, rows: 5, legs: 20, price: 800, when: ({ width, depth }) => depth > width },
  { code: 'SBI-CUST6', wMin: 1201, wMax: 1500, dMin: 951, dMax: 1200, cols: 5, rows: 4, legs: 20, price: 890 },
  { code: 'SBI-CUST6', wMin: 951, wMax: 1200, dMin: 1201, dMax: 1500, cols: 4, rows: 5, legs: 20, price: 890 },
  { code: 'SBI-CUST7', wMin: 1501, wMax: 1800, dMin: 300, dMax: 950, cols: 7, rows: 3, legs: 21, price: 1100 },
  { code: 'SBI-CUST7', wMin: 300, wMax: 950, dMin: 1501, dMax: 1800, cols: 3, rows: 7, legs: 21, price: 1100 },
  { code: 'SBI-CUST8', wMin: 1501, wMax: 1800, dMin: 951, dMax: 1200, cols: 7, rows: 4, legs: 28, price: 1200 },
  { code: 'SBI-CUST8', wMin: 951, wMax: 1200, dMin: 1501, dMax: 1800, cols: 4, rows: 7, legs: 28, price: 1200 },
]

const summaryReferenceRows = [
  {
    id: '9-3x3',
    width: '300 ≤ W ≤ 950',
    depth: '300 ≤ D ≤ 950',
    legs: '9 (3×3)',
    matches: ({ width, depth, cols, rows }) => width >= 300 && width <= 950 && depth >= 300 && depth <= 950 && cols === 3 && rows === 3,
  },
  {
    id: '15-5x3-a',
    width: '950 < W ≤ 1200',
    depth: '300 ≤ D ≤ 950',
    legs: '15 (5×3)',
    matches: ({ width, depth, cols, rows }) => width > 950 && width <= 1200 && depth >= 300 && depth <= 950 && cols === 5 && rows === 3,
  },
  {
    id: '15-3x5-a',
    width: '300 ≤ W ≤ 950',
    depth: '950 < D ≤ 1200',
    legs: '15 (3×5)',
    matches: ({ width, depth, cols, rows }) => width >= 300 && width <= 950 && depth > 950 && depth <= 1200 && cols === 3 && rows === 5,
  },
  {
    id: '15-5x3-b',
    width: '1200 < W ≤ 1500',
    depth: '300 ≤ D ≤ 950',
    legs: '15 (5×3)',
    matches: ({ width, depth, cols, rows }) => width > 1200 && width <= 1500 && depth >= 300 && depth <= 950 && cols === 5 && rows === 3,
  },
  {
    id: '15-3x5-b',
    width: '300 ≤ W ≤ 950',
    depth: '1200 < D ≤ 1500',
    legs: '15 (3×5)',
    matches: ({ width, depth, cols, rows }) => width >= 300 && width <= 950 && depth > 1200 && depth <= 1500 && cols === 3 && rows === 5,
  },
  {
    id: '20-4or5',
    width: '950 < W ≤ 1200',
    depth: '950 < D ≤ 1200',
    legs: '20 (5×4) or (4×5)',
    matches: ({ width, depth }) => width > 950 && width <= 1200 && depth > 950 && depth <= 1200,
  },
  {
    id: '20-5x4',
    width: '1200 < W ≤ 1500',
    depth: '950 < D ≤ 1200',
    legs: '20 (5×4)',
    matches: ({ width, depth, cols, rows }) => width > 1200 && width <= 1500 && depth > 950 && depth <= 1200 && cols === 5 && rows === 4,
  },
  {
    id: '20-4x5',
    width: '950 < W ≤ 1200',
    depth: '1200 < D ≤ 1500',
    legs: '20 (4×5)',
    matches: ({ width, depth, cols, rows }) => width > 950 && width <= 1200 && depth > 1200 && depth <= 1500 && cols === 4 && rows === 5,
  },
  {
    id: '21-7x3',
    width: '1500 < W ≤ 1800',
    depth: '300 ≤ D ≤ 950',
    legs: '21 (7×3)',
    matches: ({ width, depth, cols, rows }) => width > 1500 && width <= 1800 && depth >= 300 && depth <= 950 && cols === 7 && rows === 3,
  },
  {
    id: '21-3x7',
    width: '300 ≤ W ≤ 950',
    depth: '1500 < D ≤ 1800',
    legs: '21 (3×7)',
    matches: ({ width, depth, cols, rows }) => width >= 300 && width <= 950 && depth > 1500 && depth <= 1800 && cols === 3 && rows === 7,
  },
  {
    id: '28-7x4',
    width: '1500 < W ≤ 1800',
    depth: '950 < D ≤ 1200',
    legs: '28 (7×4)',
    matches: ({ width, depth, cols, rows }) => width > 1500 && width <= 1800 && depth > 950 && depth <= 1200 && cols === 7 && rows === 4,
  },
  {
    id: '28-4x7',
    width: '950 < W ≤ 1200',
    depth: '1500 < D ≤ 1800',
    legs: '28 (4×7)',
    matches: ({ width, depth, cols, rows }) => width > 950 && width <= 1200 && depth > 1500 && depth <= 1800 && cols === 4 && rows === 7,
  },
]

const matchPricingTier = ({ width, depth }) => {
  const averageWidth = (width.front + width.rear) / 2
  const averageDepth = (depth.left + depth.right) / 2
  return priceTable.find(row =>
    averageWidth >= row.wMin &&
    averageWidth <= row.wMax &&
    averageDepth >= row.dMin &&
    averageDepth <= row.dMax &&
    (!row.when || row.when({ width: averageWidth, depth: averageDepth }))
  ) || priceTable[0]
}

const getBaseShape = dimensions => {
  const rectangular = dimensions.frontWidth === dimensions.rearWidth && dimensions.leftDepth === dimensions.rightDepth
  if (rectangular) return 'Rectangle'
  return 'Trapezoid (Irregular)'
}

const buildLegPositions = ({ cols, rows, setbacks, dimensions }) => {
  const geometry = createBaseGeometry(dimensions)
  const averageDepth = (dimensions.leftDepth + dimensions.rightDepth) / 2
  const frontRatio = clamp(toNumber(setbacks.front) / Math.max(averageDepth, 1), 0, 0.92)
  const rearRatio = clamp(1 - (toNumber(setbacks.rear) / Math.max(averageDepth, 1)), 0.08, 1)
  const points = []

  for (let row = 0; row < rows; row += 1) {
    const rowRatio = rows === 1 ? 0.5 : row / (rows - 1)
    const sectionRatio = frontRatio + rowRatio * Math.max(rearRatio - frontRatio, 0)
    const leftBoundary = interpolateLine(geometry.frontLeft, geometry.rearLeft, sectionRatio)
    const rightBoundary = interpolateLine(geometry.frontRight, geometry.rearRight, sectionRatio)
    const spanX = rightBoundary.x - leftBoundary.x
    const spanY = rightBoundary.y - leftBoundary.y
    const rowLength = Math.max(Math.hypot(spanX, spanY), 1)
    const leftInsetRatio = clamp(toNumber(setbacks.left) / rowLength, 0, 0.45)
    const rightInsetRatio = clamp(toNumber(setbacks.right) / rowLength, 0, 0.45)
    const innerStart = interpolateLine(leftBoundary, rightBoundary, leftInsetRatio)
    const innerEnd = interpolateLine(rightBoundary, leftBoundary, rightInsetRatio)

    for (let col = 0; col < cols; col += 1) {
      const colRatio = cols === 1 ? 0.5 : col / (cols - 1)
      const point = interpolateLine(innerStart, innerEnd, colRatio)
      points.push({
        id: `${String.fromCharCode(65 + row)}${col + 1}`,
        xPercent: ((point.x - geometry.minX) / Math.max(geometry.width, 1)) * 100,
        yPercent: ((point.y - geometry.minY) / Math.max(geometry.depth, 1)) * 100,
        model: point,
      })
    }
  }

  return points
}

const getLegHeightsCompletion = (legHeights, positions) => {
  const ids = positions.map(position => position.id)
  const allEntered = ids.length > 0 && ids.every(id => String(legHeights[id] ?? '').trim() !== '')
  const hasOutOfRangeValue = ids.some(id => {
    const rawValue = String(legHeights[id] ?? '').trim()
    if (!rawValue) return false
    const numeric = toNumber(rawValue)
    return numeric < LEG_HEIGHT_MIN || numeric > LEG_HEIGHT_MAX
  })

  return {
    allEntered,
    hasOutOfRangeValue,
    allValid: allEntered && !hasOutOfRangeValue,
  }
}

const createBaseGeometry = dimensions => {
  const frontWidth = toNumber(dimensions.frontWidth)
  const rearWidth = toNumber(dimensions.rearWidth)
  const leftDepth = toNumber(dimensions.leftDepth)
  const rightDepth = toNumber(dimensions.rightDepth)
  const rearShift = (frontWidth - rearWidth) / 2

  const frontLeft = { x: 0, y: 0 }
  const frontRight = { x: frontWidth, y: 0 }
  const rearRight = { x: rearShift + rearWidth, y: rightDepth }
  const rearLeft = { x: rearShift, y: leftDepth }
  const points = [frontLeft, frontRight, rearRight, rearLeft]
  const xs = points.map(point => point.x)
  const ys = points.map(point => point.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const width = maxX - minX
  const depth = maxY - minY
  const span = Math.max(width, depth, 1)

  return {
    frontLeft,
    frontRight,
    rearRight,
    rearLeft,
    minX,
    maxX,
    minY,
    maxY,
    width,
    depth,
    span,
  }
}

const interpolateLine = (start, end, ratio) => ({
  x: start.x + (end.x - start.x) * ratio,
  y: start.y + (end.y - start.y) * ratio,
})

const isLegNearDrain = (position, drain, averageWidth, averageDepth) => {
  const drainX = (toNumber(drain.fromLeft) / Math.max(averageWidth, 1)) * 100
  const drainY = (toNumber(drain.fromFront) / Math.max(averageDepth, 1)) * 100
  return Math.abs(position.xPercent - drainX) < 8 && Math.abs(position.yPercent - drainY) < 8
}

const getDimensionFieldErrors = dimensions => {
  const errors = {}

  Object.entries(dimensions).forEach(([key, value]) => {
    const numeric = toNumber(value)
    if (numeric < MM_MIN) {
      errors[key] = `Minimum ${MM_MIN}mm`
    } else if (numeric > MM_MAX) {
      errors[key] = 'Custom quote required'
    }
  })

  return errors
}

const getFulfilmentAddressSummary = fulfilment => {
  if (fulfilment.method !== 'Delivery') return 'Pickup / Click & Collect'

  return [
    fulfilment.address,
    [fulfilment.suburb, fulfilment.state, fulfilment.postcode].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ') || '-'
}

const getAverageWidth = dimensions => (toNumber(dimensions.frontWidth) + toNumber(dimensions.rearWidth)) / 2

const getDrainDistanceFromRight = (dimensions, drain) => {
  const averageWidth = getAverageWidth(dimensions)
  return Math.max(0, Math.round(averageWidth - toNumber(drain.fromLeft)))
}

const getConstructionLabel = fabrication => fabrication.splitBase ? '2 Piece' : '1 Piece'

const formatFingerHoleList = values => values.length ? values.join(', ') : 'None'

const getEffectiveFabrication = fabrication => {
  if (!fabrication.splitBase || !fabrication.installationOrder) return fabrication

  const inactiveKey = fabrication.installationOrder === 'Base 1 First' ? 'fingerHolesBase1' : 'fingerHolesBase2'
  const activeKey = fabrication.installationOrder === 'Base 1 First' ? 'fingerHolesBase2' : 'fingerHolesBase1'
  const defaultSide = fabrication.installationOrder === 'Base 1 First' ? 'Right Side' : 'Left Side'

  return {
    ...fabrication,
    [inactiveKey]: [],
    [activeKey]: fabrication[activeKey].length > 0 ? fabrication[activeKey] : [defaultSide],
  }
}

const getFabricationReviewRows = fabrication => {
  const resolvedFabrication = getEffectiveFabrication(fabrication)

  if (!resolvedFabrication.splitBase) {
    return [
      { label: 'Construction', value: '1 Piece' },
      { label: 'Finger Holes', value: formatFingerHoleList(resolvedFabrication.fingerHolesSingle) },
    ]
  }

  const base1InstalledFirst = resolvedFabrication.installationOrder === 'Base 1 First'
  const base2InstalledFirst = resolvedFabrication.installationOrder === 'Base 2 First'

  return [
    { label: 'Construction', value: '2 Piece' },
    { label: 'Installed First', value: resolvedFabrication.installationOrder || '-' },
    { label: 'Base 1', value: base1InstalledFirst ? 'Installed 1st - No holes' : formatFingerHoleList(resolvedFabrication.fingerHolesBase1) },
    { label: 'Base 2', value: base2InstalledFirst ? 'Installed 1st - No holes' : formatFingerHoleList(resolvedFabrication.fingerHolesBase2) },
  ]
}

const getCutoutDistanceValue = cutout => (
  cutout.leftEdgeDistance || cutout.rightEdgeDistance || cutout.fixtureOffsetFromLeft || cutout.fixtureOffsetFromFront || 0
)

const getCutoutReviewLabel = cutout => {
  const map = {
    'screen-door': 'Shower Screen Door',
    screen: 'Shower Screen',
    pipes: 'Pipes & Fixtures',
    'corner-channel': 'Corner Channel/Profile',
  }
  return map[cutout.type] || 'Cut-out'
}

const getSliderFillStyle = (value, min, max) => {
  const numeric = clamp(toNumber(value), min, max)
  const progress = ((numeric - min) / Math.max(max - min, 1)) * 100
  return {
    '--range-progress': `${progress}%`,
  }
}

const cutoutLabelByType = {
  'screen-door': 'SCREEN CUT-OFF',
  screen: 'SHOWER SCREEN',
  pipes: 'PIPE/FIXTURE CUT-OUT',
  'corner-channel': 'CORNER CUTOUT',
}

const getPreferredOffset = (cutout, span, fallback = 180) => {
  if (cutout.leftEdgeEnabled) {
    return clamp(toNumber(cutout.leftEdgeDistance), 0, span)
  }

  if (cutout.rightEdgeEnabled) {
    return clamp(span - toNumber(cutout.rightEdgeDistance), 0, span)
  }

  if (cutout.hand === 'Right') {
    return clamp(span - Math.max(toNumber(cutout.edgeOffset), fallback), 0, span)
  }

  return clamp(Math.max(toNumber(cutout.edgeOffset), fallback), 0, span)
}

const normalizeVector2 = (x, y) => {
  const length = Math.max(Math.hypot(x, y), 1)
  return { x: x / length, y: y / length }
}

const getPolygonCentroid2D = points => {
  if (!points?.length) return { x: 0, z: 0 }
  const totals = points.reduce((accumulator, point) => ({
    x: accumulator.x + point.x,
    z: accumulator.z + point.z,
  }), { x: 0, z: 0 })
  return {
    x: totals.x / points.length,
    z: totals.z / points.length,
  }
}

const buildCutoutOutlinePoints = (center, width, depth, rotation = 0) => {
  const widthHalf = width / 2
  const depthHalf = depth / 2
  const corners = [
    [-widthHalf, -depthHalf],
    [widthHalf, -depthHalf],
    [widthHalf, depthHalf],
    [-widthHalf, depthHalf],
    [-widthHalf, -depthHalf],
  ]

  return corners.map(([offsetX, offsetY]) => ({
    x: center.x + (offsetX * Math.cos(rotation)) - (offsetY * Math.sin(rotation)),
    y: center.y + (offsetX * Math.sin(rotation)) + (offsetY * Math.cos(rotation)),
  }))
}

const getInwardNormalForSegment = (start, end, referencePoint) => {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const length = Math.max(Math.hypot(dx, dy), 1)
  const candidateA = { x: -dy / length, y: dx / length }
  const candidateB = { x: dy / length, y: -dx / length }
  const midpoint = {
    x: (start.x + end.x) / 2,
    y: (start.y + end.y) / 2,
  }
  const dotA = (referencePoint.x - midpoint.x) * candidateA.x + (referencePoint.y - midpoint.y) * candidateA.y
  return dotA >= 0 ? candidateA : candidateB
}

const getEdgeAnchoredCutoutPlacement = ({ geometry, side, offsetAlongEdge, widthHalf, depthHalf }) => {
  const centroid = {
    x: (geometry.frontLeft.x + geometry.frontRight.x + geometry.rearRight.x + geometry.rearLeft.x) / 4,
    y: (geometry.frontLeft.y + geometry.frontRight.y + geometry.rearRight.y + geometry.rearLeft.y) / 4,
  }
  const segments = {
    Front: [geometry.frontLeft, geometry.frontRight],
    Rear: [geometry.rearLeft, geometry.rearRight],
    Left: [geometry.frontLeft, geometry.rearLeft],
    Right: [geometry.frontRight, geometry.rearRight],
  }
  const [start, end] = segments[side] || segments.Front
  const dx = end.x - start.x
  const dy = end.y - start.y
  const length = Math.max(Math.hypot(dx, dy), 1)
  const tangent = { x: dx / length, y: dy / length }
  const inwardNormal = getInwardNormalForSegment(start, end, centroid)
  const clampedDistance = clamp(offsetAlongEdge, widthHalf, length - widthHalf)
  const edgeMidpoint = {
    x: start.x + tangent.x * clampedDistance,
    y: start.y + tangent.y * clampedDistance,
  }

  return {
    center: {
      x: edgeMidpoint.x + inwardNormal.x * depthHalf,
      y: edgeMidpoint.y + inwardNormal.y * depthHalf,
    },
    outwardNormal: {
      x: -inwardNormal.x,
      y: -inwardNormal.y,
    },
    rotation: Math.atan2(tangent.y, tangent.x),
  }
}

const buildCutoutOverlay = (cutout, geometry) => {
  const width = Math.max(24, toNumber(cutout.width))
  const depth = Math.max(16, toNumber(cutout.depth))
  const widthHalf = width / 2
  const depthHalf = depth / 2
  const widthSpan = geometry.maxX - geometry.minX
  const depthSpan = geometry.maxY - geometry.minY
  const clampFrontRearX = value => clamp(value, geometry.minX + widthHalf, geometry.maxX - widthHalf)
  const clampLeftRightY = value => clamp(value, geometry.minY + widthHalf, geometry.maxY - widthHalf)
  let center = {
    x: geometry.minX + widthSpan / 2,
    y: geometry.minY + depthSpan / 2,
  }
  let drawWidth = width
  let drawDepth = depth
  let rotation = 0
  let labelRotation = 0
  let labelNormal = { x: 0, y: 1 }

  if (cutout.type === 'screen-door') {
    if (cutout.side === 'Front' || cutout.side === 'Rear') {
      const placement = getEdgeAnchoredCutoutPlacement({
        geometry,
        side: cutout.side,
        offsetAlongEdge: getPreferredOffset(cutout, widthSpan, 220),
        widthHalf,
        depthHalf,
      })
      center = placement.center
      rotation = placement.rotation
      labelNormal = placement.outwardNormal
    } else {
      const placement = getEdgeAnchoredCutoutPlacement({
        geometry,
        side: cutout.side,
        offsetAlongEdge: getPreferredOffset(cutout, depthSpan, 220),
        widthHalf,
        depthHalf,
      })
      center = placement.center
      rotation = placement.rotation
      labelNormal = placement.outwardNormal
    }
  } else if (cutout.type === 'screen') {
    if (cutout.screenPlacement === 'Corner') {
      const cornerMap = {
        'Front Left': { center: { x: geometry.minX + widthHalf, y: geometry.minY + depthHalf }, normal: { x: -1, y: -1 } },
        'Front Right': { center: { x: geometry.maxX - widthHalf, y: geometry.minY + depthHalf }, normal: { x: 1, y: -1 } },
        'Rear Left': { center: { x: geometry.minX + widthHalf, y: geometry.maxY - depthHalf }, normal: { x: -1, y: 1 } },
        'Rear Right': { center: { x: geometry.maxX - widthHalf, y: geometry.maxY - depthHalf }, normal: { x: 1, y: 1 } },
      }
      const cornerPlacement = cornerMap[cutout.corner] || cornerMap['Front Left']
      center = cornerPlacement.center
      labelNormal = cornerPlacement.normal
    } else if (cutout.side === 'Front' || cutout.side === 'Rear') {
      const edgeOffset = cutout.hand === 'Right'
        ? widthSpan - widthHalf - 34
        : widthHalf + 34
      const placement = getEdgeAnchoredCutoutPlacement({
        geometry,
        side: cutout.side,
        offsetAlongEdge: edgeOffset,
        widthHalf,
        depthHalf,
      })
      center = placement.center
      rotation = placement.rotation
      labelNormal = placement.outwardNormal
    } else {
      const edgeOffset = cutout.hand === 'Right'
        ? depthSpan - widthHalf - 34
        : widthHalf + 34
      const placement = getEdgeAnchoredCutoutPlacement({
        geometry,
        side: cutout.side,
        offsetAlongEdge: edgeOffset,
        widthHalf,
        depthHalf,
      })
      center = placement.center
      rotation = placement.rotation
      labelNormal = placement.outwardNormal
    }
  } else if (cutout.type === 'pipes') {
    if (cutout.side === 'Front' || cutout.side === 'Rear') {
      center = {
        x: clampFrontRearX(geometry.minX + Math.max(widthHalf, toNumber(cutout.fixtureOffsetFromLeft))),
        y: cutout.side === 'Front'
          ? geometry.minY + depthHalf
          : geometry.maxY - depthHalf,
      }
      labelNormal = { x: 0, y: cutout.side === 'Front' ? -1 : 1 }
    } else {
      rotation = Math.PI / 2
      center = {
        x: cutout.side === 'Left'
          ? geometry.minX + depthHalf
          : geometry.maxX - depthHalf,
        y: clampLeftRightY(geometry.minY + Math.max(widthHalf, toNumber(cutout.fixtureOffsetFromFront))),
      }
      labelNormal = { x: cutout.side === 'Left' ? -1 : 1, y: 0 }
    }
  } else if (cutout.type === 'corner-channel') {
    const cornerMap = {
      'Front Left': { center: { x: geometry.minX + widthHalf, y: geometry.minY + depthHalf }, normal: { x: -1, y: -1 } },
      'Front Right': { center: { x: geometry.maxX - widthHalf, y: geometry.minY + depthHalf }, normal: { x: 1, y: -1 } },
      'Rear Left': { center: { x: geometry.minX + widthHalf, y: geometry.maxY - depthHalf }, normal: { x: -1, y: 1 } },
      'Rear Right': { center: { x: geometry.maxX - widthHalf, y: geometry.maxY - depthHalf }, normal: { x: 1, y: 1 } },
    }
    const cornerPlacement = cornerMap[cutout.corner] || cornerMap['Front Left']
    center = cornerPlacement.center
    labelNormal = cornerPlacement.normal
  }

  const normalizedLabelNormal = normalizeVector2(labelNormal.x, labelNormal.y)
  const labelDistance = Math.max(drawWidth, drawDepth) / 2 + 64

  return {
    id: cutout.id,
    label: cutoutLabelByType[cutout.type] || 'CUT-OUT',
    center,
    drawWidth,
    drawDepth,
    rotation,
    labelRotation,
    labelPosition: {
      x: center.x + normalizedLabelNormal.x * labelDistance,
      y: center.y + normalizedLabelNormal.y * labelDistance,
    },
    outlinePoints: buildCutoutOutlinePoints(center, drawWidth, drawDepth, rotation),
  }
}

const buildBaseMeshGeometry = geometry => {
  const thickness = 15
  const topY = thickness / 2
  const bottomY = -thickness / 2
  const centerX = (geometry.minX + geometry.maxX) / 2
  const centerZ = (geometry.minY + geometry.maxY) / 2
  const points = [
    [geometry.frontLeft.x - centerX, topY, centerZ - geometry.frontLeft.y],
    [geometry.frontRight.x - centerX, topY, centerZ - geometry.frontRight.y],
    [geometry.rearRight.x - centerX, topY, centerZ - geometry.rearRight.y],
    [geometry.rearLeft.x - centerX, topY, centerZ - geometry.rearLeft.y],
    [geometry.frontLeft.x - centerX, bottomY, centerZ - geometry.frontLeft.y],
    [geometry.frontRight.x - centerX, bottomY, centerZ - geometry.frontRight.y],
    [geometry.rearRight.x - centerX, bottomY, centerZ - geometry.rearRight.y],
    [geometry.rearLeft.x - centerX, bottomY, centerZ - geometry.rearLeft.y],
  ]
  const positions = new Float32Array(points.flat())
  const indices = [
    0, 1, 2, 0, 2, 3,
    4, 7, 6, 4, 6, 5,
    0, 4, 5, 0, 5, 1,
    1, 5, 6, 1, 6, 2,
    2, 6, 7, 2, 7, 3,
    3, 7, 4, 3, 4, 0,
  ]
  const meshGeometry = new THREE.BufferGeometry()
  meshGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  meshGeometry.setIndex(indices)
  meshGeometry.computeVertexNormals()
  return meshGeometry
}

const buildBaseOutlineGeometry = geometry => {
  const topY = 8
  const centerX = (geometry.minX + geometry.maxX) / 2
  const centerZ = (geometry.minY + geometry.maxY) / 2
  const ordered = [geometry.frontLeft, geometry.frontRight, geometry.rearRight, geometry.rearLeft, geometry.frontLeft]
  const points = ordered.flatMap(point => [point.x - centerX, topY, centerZ - point.y])
  const outline = new THREE.BufferGeometry()
  outline.setAttribute('position', new THREE.Float32BufferAttribute(points, 3))
  return outline
}

const buildTopShapeGeometry = points => {
  const shape = new THREE.Shape()
  shape.moveTo(points[0].x, points[0].z)
  points.slice(1).forEach(point => shape.lineTo(point.x, point.z))
  shape.closePath()
  return new THREE.ShapeGeometry(shape)
}

const createTriangleGeometry = () => {
  const shape = new THREE.Shape()
  shape.moveTo(0, 18)
  shape.lineTo(18, -12)
  shape.lineTo(-10, -12)
  shape.closePath()
  return new THREE.ShapeGeometry(shape)
}

const getSplitBaseSectionMap = (localCorners, splitVertical) => {
  const midFront = {
    x: (localCorners.frontLeft.x + localCorners.frontRight.x) / 2,
    z: (localCorners.frontLeft.z + localCorners.frontRight.z) / 2,
  }
  const midRear = {
    x: (localCorners.rearLeft.x + localCorners.rearRight.x) / 2,
    z: (localCorners.rearLeft.z + localCorners.rearRight.z) / 2,
  }
  const midLeft = {
    x: (localCorners.frontLeft.x + localCorners.rearLeft.x) / 2,
    z: (localCorners.frontLeft.z + localCorners.rearLeft.z) / 2,
  }
  const midRight = {
    x: (localCorners.frontRight.x + localCorners.rearRight.x) / 2,
    z: (localCorners.frontRight.z + localCorners.rearRight.z) / 2,
  }

  if (splitVertical) {
    return {
      base1: {
        polygon: [localCorners.frontLeft, midFront, midRear, localCorners.rearLeft],
        sides: {
          'Front Side': [localCorners.frontLeft, midFront],
          'Rear Side': [localCorners.rearLeft, midRear],
          'Left Side': [localCorners.frontLeft, localCorners.rearLeft],
          'Right Side': [midFront, midRear],
        },
      },
      base2: {
        polygon: [midFront, localCorners.frontRight, localCorners.rearRight, midRear],
        sides: {
          'Front Side': [midFront, localCorners.frontRight],
          'Rear Side': [midRear, localCorners.rearRight],
          'Left Side': [midFront, midRear],
          'Right Side': [localCorners.frontRight, localCorners.rearRight],
        },
      },
    }
  }

  return {
    base1: {
      polygon: [midLeft, midRight, localCorners.rearRight, localCorners.rearLeft],
      sides: {
        'Front Side': [midLeft, midRight],
        'Rear Side': [localCorners.rearLeft, localCorners.rearRight],
        'Left Side': [midLeft, localCorners.rearLeft],
        'Right Side': [midRight, localCorners.rearRight],
      },
    },
    base2: {
      polygon: [localCorners.frontLeft, localCorners.frontRight, midRight, midLeft],
      sides: {
        'Front Side': [localCorners.frontLeft, localCorners.frontRight],
        'Rear Side': [midLeft, midRight],
        'Left Side': [localCorners.frontLeft, midLeft],
        'Right Side': [localCorners.frontRight, midRight],
      },
    },
  }
}

const getOutwardNormal = (start, end) => {
  const dx = end.x - start.x
  const dz = end.z - start.z
  const length = Math.max(Math.hypot(dx, dz), 1)
  let nx = -dz / length
  let nz = dx / length
  const midpointX = (start.x + end.x) / 2
  const midpointZ = (start.z + end.z) / 2
  if (midpointX * nx + midpointZ * nz < 0) {
    nx *= -1
    nz *= -1
  }
  return { nx, nz, angle: Math.atan2(dz, dx) }
}

const normalizeParallelTextAngle = angle => {
  let next = angle
  if (next > Math.PI / 2) next -= Math.PI
  if (next < -Math.PI / 2) next += Math.PI
  return next
}

const getParallelTextRotationForSegment = (start, end) => (
  normalizeParallelTextAngle(-Math.atan2(end.z - start.z, end.x - start.x))
)

const getInsetPointFromSegment = (start, end, inset) => {
  const midpoint = {
    x: (start.x + end.x) / 2,
    z: (start.z + end.z) / 2,
  }
  const outward = getOutwardNormal(start, end)
  return {
    x: midpoint.x - outward.nx * inset,
    z: midpoint.z - outward.nz * inset,
  }
}

const buildArrowPolyline2D = (start, end, headLength = 12, headWidth = 8) => {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const length = Math.max(Math.hypot(dx, dy), 1)
  const ux = dx / length
  const uy = dy / length
  const px = -uy
  const py = ux

  return [
    [start.x + (ux * headLength) + (px * headWidth), start.y + (uy * headLength) + (py * headWidth)],
    [start.x, start.y],
    [start.x + (ux * headLength) - (px * headWidth), start.y + (uy * headLength) - (py * headWidth)],
  ]
}

const getDimensionGuideLabel = (sideKey, value) => {
  const labels = {
    front: 'Front Width',
    rear: 'Rear Width',
    left: 'Left Depth',
    right: 'Right Depth',
  }
  return `${labels[sideKey] || 'Dimension'}: ${value}mm`
}

const TopDownText = ({ position, rotation = 0, fontSize = 36, color = '#111827', children, anchorX = 'center' }) => (
  <Text
    position={position}
    rotation={[-Math.PI / 2, 0, rotation]}
    fontSize={fontSize}
    color={color}
    anchorX={anchorX}
    anchorY="middle"
    material-side={THREE.DoubleSide}
  >
    {children}
  </Text>
)

const PDF_PREVIEW_EXPORT_SCALE = 0.62

const PreviewCaptureBridge = ({ controlsRef, exportScale = 1, onCaptureReady }) => {
  const { gl, scene, camera } = useThree()

  useEffect(() => {
    if (typeof onCaptureReady !== 'function') return undefined

    const capture = async () => {
      const captureCamera = camera.clone()
      const target = controlsRef.current?.target?.clone?.() || new THREE.Vector3(0, 0, 0)

      if (captureCamera.isPerspectiveCamera && exportScale > 0 && exportScale !== 1) {
        const offset = captureCamera.position.clone().sub(target)
        captureCamera.position.copy(target.clone().add(offset.multiplyScalar(1 / exportScale)))
      } else if (captureCamera.isOrthographicCamera && exportScale > 0 && exportScale !== 1) {
        captureCamera.zoom *= exportScale
      }

      captureCamera.lookAt(target)
      captureCamera.updateProjectionMatrix()

      gl.render(scene, captureCamera)
      return gl.domElement.toDataURL('image/png')
    }

    onCaptureReady(capture)
    return () => onCaptureReady(null)
  }, [camera, controlsRef, exportScale, gl, onCaptureReady, scene])

  return null
}

const PreviewCameraController = ({ controlsRef, presetKey, resetToken }) => {
  const { camera } = useThree()

  useEffect(() => {
    const preset = PREVIEW_CAMERA_PRESETS[presetKey] || PREVIEW_CAMERA_PRESETS.default
    camera.position.set(...preset.position)
    camera.lookAt(...preset.target)
    camera.updateProjectionMatrix()
    if (controlsRef.current) {
      controlsRef.current.target.set(...preset.target)
      controlsRef.current.update()
    }
  }, [camera, controlsRef, presetKey, resetToken])

  return null
}

const PreviewCameraOrientation = ({ controlsRef, onUpsideDownChange }) => {
  const { camera } = useThree()
  const lastValueRef = useRef(null)

  useFrame(() => {
    const targetY = controlsRef.current?.target?.y ?? 0
    const isUpsideDown = camera.position.y < targetY - 0.02
    if (lastValueRef.current === isUpsideDown) return
    lastValueRef.current = isUpsideDown
    onUpsideDownChange(isUpsideDown)
  })

  return null
}

const buildInitialState = () => ({
  mode: 'landing',
  currentStep: 1,
  stepOneSummaryOpen: false,
  reviewLegHeightsExpanded: false,
  mobileSectionOpen: 1,
  showLockModal: false,
  reviewEditTarget: null,
  paymentRequested: false,
  invoiceRequested: false,
  submittedReference: '',
  draftId: createDraftId(),
  orderId: '',
  lastSavedAt: '',
  isSavingDraft: false,
  submitError: '',
  isSubmittingOrder: false,
  isCapturingLead: false,
  isStartingCheckout: false,
  contactSubmitAttempted: false,
  activeCutoutId: null,
  dimensions: {
    frontWidth: 950,
    rearWidth: 950,
    leftDepth: 950,
    rightDepth: 900,
  },
  entrySides: ['Front'],
  roundedCornersEnabled: false,
  roundedCorners: {
    quickApply: '',
    frontLeftA: '',
    frontLeftB: '',
    frontLeftNotRounded: false,
    frontRightA: '',
    frontRightB: '',
    frontRightNotRounded: false,
    rearLeftA: '',
    rearLeftB: '',
    rearLeftNotRounded: false,
    rearRightA: '',
    rearRightB: '',
    rearRightNotRounded: false,
  },
  squareCorners: [],
  setbacks: defaultSetbacks(),
  legHeights: {},
  quickHeight: '50',
  focusedLeg: '',
  drain: defaultDrain(),
  cutoutsEnabled: false,
  cutouts: [
    createCutout('screen-door'),
  ],
  fabrication: defaultFabrication(),
  uploads: [],
  additionalInfo: '',
  contact: {
    fullName: '',
    email: '',
    phone: '',
    company: '',
    reference: '',
  },
  fulfilment: {
    method: 'Delivery',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    authorityToLeave: '',
    deliveryNote: '',
  },
})

const StepIcon = ({ type }) => {
  return (
    <span className="order-step-icon" aria-hidden="true">
      <StepGlyph type={type} size={20} />
    </span>
  )
}

const VideoHelpCard = ({ title, description, thumbnail = constructionImage, onClick }) => (
  <button type="button" className="order-help-card" onClick={onClick}>
    <span className="order-help-thumb">
      <img src={thumbnail} alt="" />
      <span className="order-help-play">▶</span>
    </span>
    <span className="order-help-copy">
      <strong>
        <span>{title}</span>
        <span className="order-help-plus" aria-hidden="true">+</span>
      </strong>
      <small>{description}</small>
    </span>
  </button>
)

const OrderHelpModal = ({ helpKey, onClose }) => {
  const help = orderHelpContent[helpKey]
  if (!help) return null

  return (
    <div
      className="modal-backdrop help-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-help-modal-title"
      onClick={event => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="modal help-modal order-help-modal">
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g opacity="0.7">
              <rect width="40" height="40" rx="20" fill="#E5E7EB" />
              <path d="M25 15L15 25M15 15L25 25" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          </svg>
        </button>
        <h3 id="order-help-modal-title">{help.title}</h3>
        <p>{help.description}</p>
        <div className="help-video">
          <div className="help-video-preview">
            {help.videoEmbedUrl ? (
              <iframe
                src={help.videoEmbedUrl}
                title={help.title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            ) : (
              <img src={help.thumbnail} alt="" />
            )}
          </div>
        </div>
        {help.sections.map(section => (
          <div key={section.title} className="order-help-modal-section">
            <h4 className="help-tips-title">{section.title}</h4>
            {section.ordered ? (
              <ol className="help-steps order-help-steps-numbered">
                {section.items.map(item => <li key={item}>{item}</li>)}
              </ol>
            ) : (
              <ul className="help-steps">
                {section.items.map(item => <li key={item}>{item}</li>)}
              </ul>
            )}
          </div>
        ))}
        {help.note ? <div className="order-help-modal-note">{help.note}</div> : null}
      </div>
    </div>
  )
}

const InputGroup = ({ label, hint, error, suffix, children }) => (
  <label className={`order-input-group${error ? ' has-error' : ''}`}>
    <span className="order-input-label">{label}</span>
    <span className="order-input-frame">
      {children}
      {suffix && <span className="order-input-suffix">{suffix}</span>}
    </span>
    {hint && <small>{hint}</small>}
    {error && <em>{error}</em>}
  </label>
)

const OrderHelpTooltip = ({ text, label, wrapperClassName }) => {
  const [mobileTop, setMobileTop] = useState(null)
  const [mobileLeft, setMobileLeft] = useState(null)
  const [mobileTransform, setMobileTransform] = useState('translate(-50%, -100%)')
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const tooltipRef = useRef(null)

  const positionMobileTooltip = event => {
    if (typeof window === 'undefined' || window.innerWidth > 640) return
    const rect = event.currentTarget.getBoundingClientRect()
    const estimatedTooltipHeight = 150
    const viewportPadding = 12
    const tooltipWidth = Math.min(280, window.innerWidth - 24)
    const gap = 8
    const hasSpaceAbove = rect.top >= estimatedTooltipHeight + gap
    const nextLeft = Math.max(viewportPadding, rect.right - tooltipWidth)

    setMobileLeft(nextLeft)

    if (hasSpaceAbove) {
      setMobileTop(rect.top - gap)
      setMobileTransform('translateY(-100%)')
      return
    }

    setMobileTop(rect.bottom + gap)
    setMobileTransform('none')
  }

  const suppressFieldFocus = event => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleTooltipActivate = event => {
    suppressFieldFocus(event)
    positionMobileTooltip(event)
    setIsTooltipOpen(prev => !prev)
  }

  useEffect(() => {
    if (!isTooltipOpen) return

    const closeOnOutsideTap = event => {
      if (!tooltipRef.current || tooltipRef.current.contains(event.target)) return
      setIsTooltipOpen(false)
    }

    document.addEventListener('touchstart', closeOnOutsideTap)
    document.addEventListener('mousedown', closeOnOutsideTap)
    return () => {
      document.removeEventListener('touchstart', closeOnOutsideTap)
      document.removeEventListener('mousedown', closeOnOutsideTap)
    }
  }, [isTooltipOpen])

  return (
    <span
      ref={tooltipRef}
      className={`${wrapperClassName}${isTooltipOpen ? ' is-open' : ''}`}
    >
      <button
        type="button"
        className="order-quick-apply-help-trigger"
        aria-label={label}
        onMouseEnter={positionMobileTooltip}
        onFocus={event => {
          positionMobileTooltip(event)
          setIsTooltipOpen(true)
        }}
        onBlur={() => setIsTooltipOpen(false)}
        onPointerDown={suppressFieldFocus}
        onClick={handleTooltipActivate}
        onTouchStart={handleTooltipActivate}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleTooltipActivate(event)
          }
        }}
      >
        ?
      </button>
      <span
        className="order-quick-apply-help-tooltip"
        role="tooltip"
        style={mobileTop == null
          ? undefined
          : {
            '--tooltip-mobile-top': `${mobileTop}px`,
            '--tooltip-mobile-left': `${mobileLeft}px`,
            '--tooltip-mobile-transform': mobileTransform,
          }}
      >
        {text}
      </span>
    </span>
  )
}

const QuickApplyHelp = ({ text }) => (
  <OrderHelpTooltip
    text={text}
    label="Quick apply help"
    wrapperClassName="order-quick-apply-help"
  />
)

const CutoutFieldLabel = ({ title, helpText, helpLabel }) => (
  <span className="order-cutout-field-label">
    <span>{title}</span>
    {helpText ? (
      <OrderHelpTooltip
        text={helpText}
        label={helpLabel}
        wrapperClassName="order-section-hint-wrap"
      />
    ) : null}
  </span>
)

const ToggleButtonGroup = ({ options, value, onToggle, multi = false, stretch = false, disabled = false, disabledOptions = [], className = '' }) => (
  <div className={`order-toggle-group${multi ? ' is-multi' : ''}${stretch ? ' is-stretch' : ''}${disabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}>
    {options.map(option => {
      const active = multi ? value.includes(option) : value === option
      const optionDisabled = disabled || disabledOptions.includes(option)
      return (
        <button
          key={option}
          type="button"
          className={`${active ? 'active' : ''}${optionDisabled ? ' is-option-disabled' : ''}`.trim()}
          disabled={optionDisabled}
          onClick={() => onToggle(option)}
        >
          {option}
        </button>
      )
    })}
  </div>
)

const OptionButtonGrid = ({ options, value, onChange, columns = 2 }) => (
  <div className="order-option-grid" style={{ '--option-columns': columns }}>
    {options.map(option => (
      <button
        key={option.value}
        type="button"
        className={value === option.value ? 'active' : ''}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
)

const CheckboxSliderField = ({ label, checked, onToggle, value, onChange }) => (
  <div className="order-checkbox-slider">
    <label className="order-inline-check">
      <input type="checkbox" checked={checked} onChange={event => onToggle(event.target.checked)} />
      <span>{label}</span>
    </label>
    <div className="order-slider-inline">
      <input inputMode="decimal" value={value} onChange={event => onChange(event.target.value)} disabled={!checked} />
      <input
        className="order-range"
        type="range"
        min={0}
        max={1200}
        value={clamp(toNumber(value), 0, 1200)}
        style={getSliderFillStyle(value, 0, 1200)}
        onChange={event => onChange(event.target.value)}
        onWheel={preventScrollValueChange}
        disabled={!checked}
      />
    </div>
  </div>
)

const NumericSliderField = ({ label, value, min, max, onChange, error }) => (
  <div className="order-slider-field">
    <span className="order-input-label">{label}</span>
    <div className="order-slider-inline order-slider-inline-mobile">
      <label className={`order-input-group order-slider-input-group${error ? ' has-error' : ''}`}>
        <span className="order-input-frame">
          <input
            type="number"
            inputMode="decimal"
            value={value}
            onChange={event => onChange(event.target.value)}
            onWheel={preventScrollValueChange}
          />
          <span className="order-input-suffix">mm</span>
        </span>
      </label>
      <div className="order-slider-range-wrap">
        <input
          className="order-range"
          type="range"
          min={min}
          max={max}
          value={clamp(toNumber(value), min, max)}
          style={getSliderFillStyle(value, min, max)}
          onChange={event => onChange(event.target.value)}
          onWheel={preventScrollValueChange}
        />
      </div>
    </div>
    <div className="order-slider-meta">
      <small>{`Valid range: ${min}mm - ${max}mm`}</small>
      {error && <em>{error}</em>}
    </div>
  </div>
)

const InlineSliderField = ({ label, value, min, max, onChange }) => (
  <div className="order-inline-slider-field">
    <span className="order-input-label">{label}</span>
    <div className="order-inline-slider-row">
      <input
        className="order-inline-slider-input"
        type="number"
        inputMode="decimal"
        value={value}
        onChange={event => onChange(event.target.value)}
        onWheel={preventScrollValueChange}
      />
      <input
        className="order-range"
        type="range"
        min={min}
        max={max}
        value={clamp(toNumber(value), min, max)}
        style={getSliderFillStyle(value, min, max)}
        onChange={event => onChange(event.target.value)}
        onWheel={preventScrollValueChange}
      />
    </div>
  </div>
)

const UploadDropzone = ({ onFiles, files, onRemoveFile }) => (
  <>
    <div className="order-upload-dropzone">
      <img src={UploadImage} alt="" />
      <strong>Choose files or drag and drop them here</strong>
      <span>JPEG, PNG, and PDF formats, up to 10MB</span>
      <input
        type="file"
        accept=".jpg,.jpeg,.png,.pdf"
        multiple
        onChange={event => onFiles(Array.from(event.target.files || []))}
      />
    </div>
    {files.length > 0 && (
      <div className="order-upload-previews">
        {files.map(file => (
          <div key={file.id} className="order-upload-preview">
            {file.previewUrl ? (
              <img src={file.previewUrl} alt={file.name} />
            ) : (
              <span className="order-upload-file">{file.name}</span>
            )}
            <button type="button" className="order-upload-remove" onClick={() => onRemoveFile(file.id)}>×</button>
          </div>
        ))}
      </div>
    )}
  </>
)

const OrderThreeScene = ({
  geometry,
  dimensions,
  entrySides,
  squareCorners,
  drain,
  setbacks,
  cutoutOverlays,
  fabrication,
  legPositions,
  legHeights,
  focusedLeg,
  currentStep,
  viewPreset = 'default',
  viewResetToken = 0,
  previewScale = 1,
  drainConflictLegIds = [],
  quickApplyFocused = false,
  onCaptureReady,
}) => {
  const controlsRef = useRef(null)
  const [showUndersideLabels, setShowUndersideLabels] = useState(false)
  const showDrainConflictIndicators = currentStep >= 4 && currentStep < STEP_TOTAL
  const scale = Math.min(0.00082, 0.82 / Math.max(geometry.span, 1)) * previewScale
  const baseMesh = useMemo(() => buildBaseMeshGeometry(geometry), [geometry])
  const baseOutline = useMemo(() => buildBaseOutlineGeometry(geometry), [geometry])
  const entryTriangleGeometry = useMemo(() => createTriangleGeometry(), [])
  const centerX = (geometry.minX + geometry.maxX) / 2
  const centerZ = (geometry.minY + geometry.maxY) / 2
  const baseTopY = 16
  const guideY = -26
  const supportY = 18
  const dotY = baseTopY + 0.6
  const drainWidth = Math.max(20, toNumber(drain.width))
  const drainDepth = Math.max(20, toNumber(drain.depth))
  const drainX = geometry.minX + toNumber(drain.fromLeft) - centerX
  const drainZ = centerZ - (geometry.minY + toNumber(drain.fromFront))
  const floorDots = useMemo(() => buildFloorGridPoints(geometry, 82), [geometry])

  const toLocal = (point, y = 0) => [point.x - centerX, y, centerZ - point.y]
  const toLocalPoint = point => ({ x: point.x - centerX, z: centerZ - point.y })

  const localCorners = useMemo(() => ({
    frontLeft: { x: geometry.frontLeft.x - centerX, z: centerZ - geometry.frontLeft.y },
    frontRight: { x: geometry.frontRight.x - centerX, z: centerZ - geometry.frontRight.y },
    rearRight: { x: geometry.rearRight.x - centerX, z: centerZ - geometry.rearRight.y },
    rearLeft: { x: geometry.rearLeft.x - centerX, z: centerZ - geometry.rearLeft.y },
  }), [geometry, centerX, centerZ])

  const splitVertical = dimensions.frontWidth + dimensions.rearWidth > dimensions.leftDepth + dimensions.rightDepth

  const splitSectionMap = useMemo(
    () => getSplitBaseSectionMap(localCorners, splitVertical),
    [localCorners, splitVertical]
  )

  const sideLabelLanes = useMemo(() => {
    const sides = {
      front: [localCorners.frontLeft, localCorners.frontRight],
      right: [localCorners.frontRight, localCorners.rearRight],
      rear: [localCorners.rearLeft, localCorners.rearRight],
      left: [localCorners.frontLeft, localCorners.rearLeft],
    }

    return Object.fromEntries(
      Object.entries(sides).map(([key, [start, end]]) => {
        const normal = getOutwardNormal(start, end)
        return [key, {
          midpoint: {
            x: (start.x + end.x) / 2,
            z: (start.z + end.z) / 2,
          },
          normal,
          entryDistance: 74,
          setbackDistance: 126,
          dimensionDistance: 188,
        }]
      })
    )
  }, [localCorners])

  const dimensionGuides = useMemo(() => {
    const createGuide = (start, end, sideKey, label, offset = 118, labelOffset = 34) => {
      const dx = end.x - start.x
      const dz = end.z - start.z
      const length = Math.max(Math.hypot(dx, dz), 1)
      let nx = -dz / length
      let nz = dx / length
      const midX = (start.x + end.x) / 2
      const midZ = (start.z + end.z) / 2
      if (midX * nx + midZ * nz < 0) {
        nx *= -1
        nz *= -1
      }

      const offsetStart = { x: start.x + nx * offset, z: start.z + nz * offset }
      const offsetEnd = { x: end.x + nx * offset, z: end.z + nz * offset }
      const lane = sideLabelLanes[sideKey]
      const labelPoint = lane
        ? {
          x: lane.midpoint.x + lane.normal.nx * lane.dimensionDistance,
          z: lane.midpoint.z + lane.normal.nz * lane.dimensionDistance,
        }
        : {
          x: (offsetStart.x + offsetEnd.x) / 2 + nx * labelOffset,
          z: (offsetStart.z + offsetEnd.z) / 2 + nz * labelOffset,
        }

      return {
        sideKey,
        label,
        angle: Math.atan2(dz, dx),
        labelAngle: getParallelTextRotationForSegment(start, end),
        polyline: [
          [offsetStart.x, guideY, offsetStart.z],
          [offsetEnd.x, guideY, offsetEnd.z],
        ],
        arrowStart: [
          [offsetStart.x + (dx / length) * 18 + nx * 9, guideY, offsetStart.z + (dz / length) * 18 + nz * 9],
          [offsetStart.x, guideY, offsetStart.z],
          [offsetStart.x + (dx / length) * 18 - nx * 9, guideY, offsetStart.z + (dz / length) * 18 - nz * 9],
        ],
        arrowEnd: [
          [offsetEnd.x - (dx / length) * 18 + nx * 9, guideY, offsetEnd.z - (dz / length) * 18 + nz * 9],
          [offsetEnd.x, guideY, offsetEnd.z],
          [offsetEnd.x - (dx / length) * 18 - nx * 9, guideY, offsetEnd.z - (dz / length) * 18 - nz * 9],
        ],
        offsetStart,
        offsetEnd,
        labelPoint: [labelPoint.x, baseTopY + 1.2, labelPoint.z],
      }
    }

    return [
      createGuide(localCorners.frontLeft, localCorners.frontRight, 'front', getDimensionGuideLabel('front', dimensions.frontWidth)),
      createGuide(localCorners.frontRight, localCorners.rearRight, 'right', getDimensionGuideLabel('right', dimensions.rightDepth)),
      createGuide(localCorners.rearRight, localCorners.rearLeft, 'rear', getDimensionGuideLabel('rear', dimensions.rearWidth)),
      createGuide(localCorners.rearLeft, localCorners.frontLeft, 'left', getDimensionGuideLabel('left', dimensions.leftDepth)),
    ]
  }, [dimensions, guideY, localCorners, baseTopY, sideLabelLanes])

  const groupedRows = useMemo(() => {
    const rows = legPositions.reduce((accumulator, position) => {
      const rowKey = position.id.replace(/\d+/g, '')
      accumulator[rowKey] = accumulator[rowKey] || []
      accumulator[rowKey].push(position)
      return accumulator
    }, {})
    Object.values(rows).forEach(row => row.sort((a, b) => Number(a.id.replace(/[A-Z]/g, '')) - Number(b.id.replace(/[A-Z]/g, ''))))
    return rows
  }, [legPositions])

  const supportHull = (() => {
    const rowKeys = Object.keys(groupedRows).sort()
    if (!rowKeys.length) return []
    const firstRow = groupedRows[rowKeys[0]] || []
    const lastRow = groupedRows[rowKeys[rowKeys.length - 1]] || []
    if (!firstRow.length || !lastRow.length) return []

    return [
      toLocalPoint(firstRow[0].model),
      toLocalPoint(firstRow[firstRow.length - 1].model),
      toLocalPoint(lastRow[lastRow.length - 1].model),
      toLocalPoint(lastRow[0].model),
    ]
  })()

  const splitLinePoints = useMemo(() => {
    if (!fabrication.splitBase) return null
    return splitVertical
      ? [
        [(localCorners.frontLeft.x + localCorners.frontRight.x) / 2, supportY, localCorners.frontLeft.z],
        [(localCorners.rearLeft.x + localCorners.rearRight.x) / 2, supportY, localCorners.rearLeft.z],
      ]
      : [
        [localCorners.frontLeft.x, supportY, (localCorners.frontLeft.z + localCorners.rearLeft.z) / 2],
        [localCorners.frontRight.x, supportY, (localCorners.frontRight.z + localCorners.rearRight.z) / 2],
      ]
  }, [fabrication.splitBase, splitVertical, localCorners, supportY])

  const fingerHoleMarkers = useMemo(() => {
    if (!fabrication.splitBase) {
      const inset = 22
      return fabrication.fingerHolesSingle.map((side, index) => {
        let point = getInsetPointFromSegment(localCorners.frontLeft, localCorners.frontRight, inset)
        if (side === 'Rear Side') point = getInsetPointFromSegment(localCorners.rearLeft, localCorners.rearRight, inset)
        if (side === 'Left Side') point = getInsetPointFromSegment(localCorners.frontLeft, localCorners.rearLeft, inset)
        if (side === 'Right Side') point = getInsetPointFromSegment(localCorners.frontRight, localCorners.rearRight, inset)
        return {
          key: `single-${side}-${index}`,
          position: [point.x, baseTopY + 5, point.z],
          side,
        }
      })
    }

    return [
      ...fabrication.fingerHolesBase1.map((side, index) => ({ side, key: `base1-${side}-${index}`, baseKey: 'base1' })),
      ...fabrication.fingerHolesBase2.map((side, index) => ({ side, key: `base2-${side}-${index}`, baseKey: 'base2' })),
    ].map(marker => {
      const segment = splitSectionMap[marker.baseKey]?.sides?.[marker.side]
      const point = segment
        ? getInsetPointFromSegment(segment[0], segment[1], 22)
        : { x: 0, z: 0 }
      return {
        key: marker.key,
        position: [point.x, baseTopY + 5, point.z],
        side: marker.side,
      }
    })
  }, [fabrication, baseTopY, splitSectionMap, localCorners])

  const entryAnnotations = useMemo(() => {
    const sidePoints = {
      Front: [localCorners.frontLeft, localCorners.frontRight],
      Left: [localCorners.frontLeft, localCorners.rearLeft],
      Right: [localCorners.frontRight, localCorners.rearRight],
    }

    return entrySides.map(side => {
      const segment = sidePoints[side]
      if (!segment) return null
      const start = {
        x: segment[0].x + (segment[1].x - segment[0].x) * 0.16,
        z: segment[0].z + (segment[1].z - segment[0].z) * 0.16,
      }
      const end = {
        x: segment[0].x + (segment[1].x - segment[0].x) * 0.84,
        z: segment[0].z + (segment[1].z - segment[0].z) * 0.84,
      }
      const midpoint = {
        x: (start.x + end.x) / 2,
        z: (start.z + end.z) / 2,
      }
      const normal = getOutwardNormal(start, end)
      const sideKey = side.toLowerCase()
      const lane = sideLabelLanes[sideKey]
      const offset = 46
      return {
        key: side,
        sideKey,
        normal,
        line: [
          [start.x + normal.nx * offset, baseTopY + 0.8, start.z + normal.nz * offset],
          [end.x + normal.nx * offset, baseTopY + 0.8, end.z + normal.nz * offset],
        ],
        trianglePosition: [midpoint.x + normal.nx * 28, baseTopY + 1.2, midpoint.z + normal.nz * 28],
        textPosition: lane
          ? [lane.midpoint.x + lane.normal.nx * lane.entryDistance, baseTopY + 1, lane.midpoint.z + lane.normal.nz * lane.entryDistance]
          : [midpoint.x + normal.nx * 74, baseTopY + 1, midpoint.z + normal.nz * 74],
        textRotation: normal.angle,
        triangleRotation: normal.angle,
      }
    }).filter(Boolean)
  }, [entrySides, localCorners, baseTopY, sideLabelLanes])

  const setbackAnnotations = useMemo(() => {
    const labelMap = {
      front: 'FRONT EDGE SETBACK',
      right: 'RIGHT EDGE SETBACK',
      rear: 'REAR EDGE SETBACK',
      left: 'LEFT EDGE SETBACK',
    }
    const edges = [
      {
        key: 'front',
        outerStart: localCorners.frontLeft,
        outerEnd: localCorners.frontRight,
        innerStart: supportHull[0],
        innerEnd: supportHull[1],
        value: setbacks.front,
      },
      {
        key: 'right',
        outerStart: localCorners.frontRight,
        outerEnd: localCorners.rearRight,
        innerStart: supportHull[1],
        innerEnd: supportHull[2],
        value: setbacks.right,
      },
      {
        key: 'rear',
        outerStart: localCorners.rearLeft,
        outerEnd: localCorners.rearRight,
        innerStart: supportHull[3],
        innerEnd: supportHull[2],
        value: setbacks.rear,
      },
      {
        key: 'left',
        outerStart: localCorners.frontLeft,
        outerEnd: localCorners.rearLeft,
        innerStart: supportHull[0],
        innerEnd: supportHull[3],
        value: setbacks.left,
      },
    ]

    return edges.map(edge => {
      if (!edge.outerStart || !edge.outerEnd || !edge.innerStart || !edge.innerEnd) return null
      const normal = getOutwardNormal(edge.outerStart, edge.outerEnd)
      const labelRotation = getParallelTextRotationForSegment(edge.outerStart, edge.outerEnd)
      const tangent = {
        x: Math.cos(labelRotation),
        z: Math.sin(labelRotation),
      }
      const lane = sideLabelLanes[edge.key]
      const outerMidpoint = lane
        ? lane.midpoint
        : {
          x: (edge.outerStart.x + edge.outerEnd.x) / 2,
          z: (edge.outerStart.z + edge.outerEnd.z) / 2,
        }
      const innerMidpoint = {
        x: (edge.innerStart.x + edge.innerEnd.x) / 2,
        z: (edge.innerStart.z + edge.innerEnd.z) / 2,
      }
      const gapMidpoint = {
        x: (outerMidpoint.x + innerMidpoint.x) / 2,
        z: (outerMidpoint.z + innerMidpoint.z) / 2,
      }
      const gapArrowStart = {
        x: outerMidpoint.x + normal.nx * 6,
        y: outerMidpoint.z + normal.nz * 6,
      }
      const gapArrowEnd = {
        x: innerMidpoint.x - normal.nx * 6,
        y: innerMidpoint.z - normal.nz * 6,
      }
      const sideLength = Math.max(
        Math.hypot(edge.innerEnd.x - edge.innerStart.x, edge.innerEnd.z - edge.innerStart.z),
        1
      )
      const titleTravel = Math.min(Math.max(sideLength * 0.28, 110), 210)
      const titleOffset = { x: tangent.x * -titleTravel, z: tangent.z * -titleTravel }
      const valueOffset = { x: 0, z: 0 }

      return {
        ...edge,
        title: labelMap[edge.key],
        innerLine: [
          [edge.innerStart.x, supportY, edge.innerStart.z],
          [edge.innerEnd.x, supportY, edge.innerEnd.z],
        ],
        extensionStart: [
          [edge.outerStart.x, supportY, edge.outerStart.z],
          [edge.innerStart.x, supportY, edge.innerStart.z],
        ],
        extensionEnd: [
          [edge.outerEnd.x, supportY, edge.outerEnd.z],
          [edge.innerEnd.x, supportY, edge.innerEnd.z],
        ],
        gapLine: [
          [gapArrowStart.x, supportY, gapArrowStart.y],
          [gapArrowEnd.x, supportY, gapArrowEnd.y],
        ],
        arrowStart: buildArrowPolyline2D(gapArrowStart, gapArrowEnd).map(([x, y]) => [x, supportY, y]),
        arrowEnd: buildArrowPolyline2D(gapArrowEnd, gapArrowStart).map(([x, y]) => [x, supportY, y]),
        labelPosition: [
          gapMidpoint.x + titleOffset.x,
          supportY + 1.5,
          gapMidpoint.z + titleOffset.z,
        ],
        valuePosition: [
          gapMidpoint.x + valueOffset.x,
          supportY + 1.5,
          gapMidpoint.z + valueOffset.z,
        ],
        labelRotation,
        valueRotation: labelRotation,
      }
    }).filter(Boolean)
  }, [setbacks, supportHull, supportY, localCorners, sideLabelLanes])

  const splitPieceLabels = useMemo(() => {
    if (!fabrication.splitBase) return []
    if (splitVertical) {
      return [
        { key: 'base-1', position: [-geometry.width * 0.24, baseTopY + 2, 0], label: 'BASE 1' },
        { key: 'base-2', position: [geometry.width * 0.24, baseTopY + 2, 0], label: 'BASE 2' },
      ]
    }

    return [
      { key: 'base-1', position: [0, baseTopY + 2, -geometry.depth * 0.22], label: 'BASE 1' },
      { key: 'base-2', position: [0, baseTopY + 2, geometry.depth * 0.22], label: 'BASE 2' },
    ]
  }, [fabrication.splitBase, splitVertical, geometry.width, geometry.depth, baseTopY])

  const splitTopOverlays = useMemo(() => {
    if (!fabrication.splitBase || !fabrication.installationOrder) return []
    const selectedBaseKey = fabrication.installationOrder === 'Base 1 First' ? 'base1' : 'base2'
    const dimmedBaseKey = selectedBaseKey === 'base1' ? 'base2' : 'base1'
    const polygon = splitSectionMap[dimmedBaseKey]?.polygon
    if (!polygon?.length) return []

    return [{
      key: dimmedBaseKey,
      geometry: buildTopShapeGeometry(polygon),
    }]
  }, [fabrication.splitBase, fabrication.installationOrder, splitSectionMap])

  const squareCornerAnnotations = useMemo(() => {
    const cornerMap = {
      'Front Left': localCorners.frontLeft,
      'Front Right': localCorners.frontRight,
      'Rear Left': localCorners.rearLeft,
      'Rear Right': localCorners.rearRight,
    }

    return (squareCorners || []).map(corner => {
      const point = cornerMap[corner]
      if (!point) return null

      const xDirection = point.x <= 0 ? 1 : -1
      const zDirection = point.z <= 0 ? 1 : -1
      return {
        key: corner,
        badgePosition: [point.x + (xDirection * 34), baseTopY + 1.05, point.z + (zDirection * 34)],
        labelPosition: [point.x + (xDirection * 34), baseTopY + 1.65, point.z + (zDirection * 34)],
      }
    }).filter(Boolean)
  }, [squareCorners, localCorners, baseTopY])

  return (
    <Canvas camera={{ position: [0.94, 0.74, 1.08], fov: 34 }} gl={{ preserveDrawingBuffer: true }}>
      <PreviewCaptureBridge
        controlsRef={controlsRef}
        exportScale={PDF_PREVIEW_EXPORT_SCALE}
        onCaptureReady={onCaptureReady}
      />
      <PreviewCameraController controlsRef={controlsRef} presetKey={viewPreset} resetToken={viewResetToken} />
      <PreviewCameraOrientation controlsRef={controlsRef} onUpsideDownChange={setShowUndersideLabels} />
      <ambientLight intensity={0.92} />
      <directionalLight position={[1.3, 1.8, 1.2]} intensity={1.05} />
      <directionalLight position={[-1.1, 0.85, -0.9]} intensity={0.42} />
      <group scale={[scale, scale, scale]}>
        {floorDots.map((point, index) => (
          <mesh key={`floor-${index}`} position={toLocal(point, dotY)} rotation={[-Math.PI / 2, 0, 0]}>
            <circleGeometry args={[4.2, 20]} />
            <meshBasicMaterial color="#7c8594" transparent opacity={0.75} />
          </mesh>
        ))}

        <mesh geometry={baseMesh}>
          <meshBasicMaterial color="#ffffff" transparent opacity={0.5} depthWrite={false} />
        </mesh>

        {splitTopOverlays.map(overlay => (
          <mesh key={overlay.key} geometry={overlay.geometry} position={[0, baseTopY + 0.4, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <meshBasicMaterial color="#ffffff" transparent opacity={0.76} side={THREE.DoubleSide} />
          </mesh>
        ))}

        <line geometry={baseOutline}>
          <lineBasicMaterial color="#1f2733" linewidth={2} />
        </line>

        {dimensionGuides.map(guide => (
          <group key={guide.label}>
            <Line points={guide.polyline} color="#169cf6" lineWidth={1.6} />
            <Line points={guide.arrowStart} color="#169cf6" lineWidth={1.6} />
            <Line points={guide.arrowEnd} color="#169cf6" lineWidth={1.6} />
            <TopDownText position={guide.labelPoint} rotation={guide.labelAngle} fontSize={44} color="#111827">
              {guide.label}
            </TopDownText>
          </group>
        ))}

        {squareCornerAnnotations.map(annotation => (
          <group key={annotation.key}>
            <mesh position={annotation.badgePosition} rotation={[-Math.PI / 2, 0, 0]}>
              <planeGeometry args={[34, 34]} />
              <meshBasicMaterial color="#f4b24a" side={THREE.DoubleSide} />
            </mesh>
            <Line
              points={[
                [annotation.badgePosition[0] - 17, baseTopY + 1.08, annotation.badgePosition[2] - 17],
                [annotation.badgePosition[0] + 17, baseTopY + 1.08, annotation.badgePosition[2] - 17],
                [annotation.badgePosition[0] + 17, baseTopY + 1.08, annotation.badgePosition[2] + 17],
                [annotation.badgePosition[0] - 17, baseTopY + 1.08, annotation.badgePosition[2] + 17],
                [annotation.badgePosition[0] - 17, baseTopY + 1.08, annotation.badgePosition[2] - 17],
              ]}
              color="#d48a1a"
              lineWidth={1.8}
            />
            <TopDownText position={annotation.labelPosition} fontSize={26} color="#111827">
              SQ
            </TopDownText>
          </group>
        ))}

        {supportHull.length === 4 && currentStep >= 2 && (
          <>
            {setbackAnnotations.map(annotation => (
              <group key={annotation.key}>
                <Line points={annotation.innerLine} color="#ff2b2b" lineWidth={1.4} />
                <Line points={annotation.gapLine} color="#ff2b2b" lineWidth={1.8} />
                <Line points={annotation.arrowStart} color="#ff2b2b" lineWidth={1.8} />
                <Line points={annotation.arrowEnd} color="#ff2b2b" lineWidth={1.8} />
                <TopDownText
                  position={annotation.labelPosition}
                  rotation={annotation.labelRotation}
                  fontSize={28}
                  color="#111827"
                >
                  {annotation.title}
                </TopDownText>
                <TopDownText
                  position={annotation.valuePosition}
                  rotation={annotation.valueRotation}
                  fontSize={28}
                  color="#111827"
                >
                  {`${annotation.value}mm`}
                </TopDownText>
              </group>
            ))}
          </>
        )}

        {entryAnnotations.map(annotation => (
          <group key={annotation.key}>
            <Line points={annotation.line} color="#1cb861" lineWidth={2.4} />
            <mesh
              geometry={entryTriangleGeometry}
              position={annotation.trianglePosition}
              rotation={[-Math.PI / 2, 0, annotation.triangleRotation]}
            >
              <meshBasicMaterial color="#37c978" transparent opacity={0.52} side={THREE.DoubleSide} />
            </mesh>
            <TopDownText position={annotation.textPosition} rotation={annotation.textRotation} fontSize={40} color="#111827">
              ENTRY
            </TopDownText>
          </group>
        ))}

        {legPositions.map(position => {
          const legHeight = Math.max(LEG_HEIGHT_MIN, toNumber(legHeights[position.id] || position.height || 0))
          const isFocused = focusedLeg === position.id
          const isDrainConflict = showDrainConflictIndicators && drainConflictLegIds.includes(position.id)
          const previewStatus = getLegPreviewStatus(position, legHeights, focusedLeg, currentStep)
          const color = quickApplyFocused ? '#f07a28' : isFocused ? '#f07a28' : isDrainConflict ? '#2a6df5' : previewStatus === 'custom' ? '#35bf78' : '#8e98aa'
          return (
            <group key={position.id}>
              <mesh
                position={[
                  position.model.x - centerX,
                  -(16 + legHeight / 2),
                  centerZ - position.model.y,
                ]}
              >
                <boxGeometry args={[34, legHeight, 34]} />
                <meshStandardMaterial color={color} />
              </mesh>
              {(currentStep !== 3 && isFocused) || (currentStep === 3 && !showUndersideLabels && !quickApplyFocused) ? (
                <TopDownText
                  position={[
                    position.model.x - centerX,
                    baseTopY + 1.4,
                    centerZ - position.model.y,
                  ]}
                  fontSize={previewStatus === 'focused' ? 44 : 32}
                  color={previewStatus === 'focused' ? '#f07a28' : '#667085'}
                >
                  {previewStatus === 'focused' ? `${position.id} · ${legHeight}mm` : position.id}
                </TopDownText>
              ) : null}
              {currentStep === 3 && showUndersideLabels && !quickApplyFocused && (
                <Billboard
                  position={[
                    position.model.x - centerX,
                    -(16 + legHeight + 18),
                    centerZ - position.model.y,
                  ]}
                  follow
                >
                  <Text
                    fontSize={previewStatus === 'focused' ? 38 : 28}
                    color={previewStatus === 'focused' ? '#f07a28' : '#667085'}
                    anchorX="center"
                    anchorY="middle"
                    material-side={THREE.DoubleSide}
                  >
                    {previewStatus === 'focused' ? `${position.id} · ${legHeight}mm` : position.id}
                  </Text>
                </Billboard>
              )}
            </group>
          )
        })}

        {currentStep >= 4 && (
          <>
            <mesh position={[drainX, baseTopY + 4, drainZ]}>
              <boxGeometry args={[drainWidth, 8, drainDepth]} />
              <meshStandardMaterial color={showDrainConflictIndicators && drainConflictLegIds.length ? '#1d4ed8' : '#0b9ff9'} />
            </mesh>
            <TopDownText position={[drainX, baseTopY + 10, drainZ]} fontSize={36} color="#0b9ff9">
              DRAIN
            </TopDownText>
          </>
        )}

        {cutoutOverlays.map(cutout => (
          <group key={cutout.id}>
            <mesh
              position={[
                cutout.center.x - centerX,
                baseTopY + 0.18,
                centerZ - cutout.center.y,
              ]}
              rotation={[-Math.PI / 2, 0, cutout.rotation]}
            >
              <planeGeometry args={[cutout.drawWidth, cutout.drawDepth]} />
              <meshBasicMaterial color="#9b5cff" transparent opacity={0.28} side={THREE.DoubleSide} />
            </mesh>
            <Line
              points={cutout.outlinePoints.map(point => [point.x - centerX, baseTopY + 0.42, centerZ - point.y])}
              color="#8b5cf6"
              lineWidth={1.5}
              dashed
              dashScale={10}
              dashSize={7}
              gapSize={4}
            />
            {currentStep >= 5 && (
              <TopDownText
                position={[cutout.labelPosition.x - centerX, baseTopY + 1.4, centerZ - cutout.labelPosition.y]}
                rotation={cutout.labelRotation}
                fontSize={32}
                color="#8b5cf6"
              >
                {cutout.label}
              </TopDownText>
            )}
          </group>
        ))}

        {splitLinePoints && (
          <>
            <Line points={splitLinePoints} color="#111827" lineWidth={2} dashed dashScale={10} dashSize={10} gapSize={7} />
          </>
        )}

        {splitPieceLabels.map(piece => (
          <TopDownText key={piece.key} position={piece.position} fontSize={36} color="#475467">
            {piece.label}
          </TopDownText>
        ))}

        {fingerHoleMarkers.map(marker => {
          const isVerticalSide = marker.key.includes('Left Side') || marker.key.includes('Right Side')
          const offsets = isVerticalSide ? [[0, -10], [0, 10]] : [[-10, 0], [10, 0]]
          return (
            <group key={marker.key}>
              {offsets.map(([offsetX, offsetZ], index) => (
                <mesh
                  key={`${marker.key}-${index}`}
                  position={[marker.position[0] + offsetX, marker.position[1], marker.position[2] + offsetZ]}
                  rotation={[-Math.PI / 2, 0, 0]}
                >
                  <torusGeometry args={[8, 2.6, 12, 28]} />
                  <meshBasicMaterial color="#111827" />
                </mesh>
              ))}
            </group>
          )
        })}

        {drainConflictLegIds.length > 0 && showDrainConflictIndicators && (
          <TopDownText position={[0, baseTopY + 1.4, -geometry.span * 0.46]} fontSize={48} color="#1d4ed8">
            DRAIN CONFLICT
          </TopDownText>
        )}
      </group>
      <OrbitControls
        ref={controlsRef}
        enablePan={false}
        target={[0, -0.02, 0]}
        minDistance={0.62}
        maxDistance={1.9}
        minPolarAngle={0}
        maxPolarAngle={Math.PI}
      />
    </Canvas>
  )
}

const isPointInPolygon = (point, polygon) => {
  let inside = false
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index, index += 1) {
    const xi = polygon[index].x
    const yi = polygon[index].y
    const xj = polygon[previous].x
    const yj = polygon[previous].y
    const intersects = ((yi > point.y) !== (yj > point.y))
      && (point.x < ((xj - xi) * (point.y - yi)) / Math.max(yj - yi, 0.00001) + xi)
    if (intersects) inside = !inside
  }
  return inside
}

const buildFloorGridPoints = (geometry, spacing = 72) => {
  const polygon = [geometry.frontLeft, geometry.frontRight, geometry.rearRight, geometry.rearLeft]
  const points = []

  for (let y = geometry.minY + spacing / 2; y <= geometry.maxY - spacing / 2; y += spacing) {
    for (let x = geometry.minX + spacing / 2; x <= geometry.maxX - spacing / 2; x += spacing) {
      const point = { x, y }
      if (isPointInPolygon(point, polygon)) {
        points.push(point)
      }
    }
  }

  return points
}

const getLegPreviewStatus = (position, legHeights, focusedLeg, currentStep) => {
  const numeric = toNumber(legHeights[position.id])
  if (currentStep === 3 && focusedLeg === position.id) return 'focused'
  if (currentStep === 3 && numeric >= LEG_HEIGHT_MIN && numeric <= LEG_HEIGHT_MAX) return 'custom'
  return 'default'
}

const OrderPreview = ({
  dimensions,
  squareCorners,
  entrySides,
  drain,
  cutouts,
  setbacks,
  config,
  fabrication,
  focusedLeg,
  legHeights,
  currentStep,
  stepOneSummaryOpen = false,
  quickApplyFocused = false,
  onOpenHelp = () => { },
  onCaptureReady,
}) => {
  const geometry = useMemo(() => createBaseGeometry(dimensions), [dimensions])
  const [previewScalePercent, setPreviewScalePercent] = useState(100)
  const [viewOverride, setViewOverride] = useState('')
  const [viewResetToken, setViewResetToken] = useState(0)
  const previewStickyRef = useRef(null)
  const previewCardRef = useRef(null)
  const legPositions = useMemo(() => {
    const rawPositions = buildLegPositions({ cols: config.cols, rows: config.rows, setbacks, dimensions })
    return rawPositions.map(position => ({
      ...position,
      height: legHeights[position.id],
    }))
  }, [config.cols, config.rows, setbacks, dimensions, legHeights])
  const averageWidth = (dimensions.frontWidth + dimensions.rearWidth) / 2
  const averageDepth = (dimensions.leftDepth + dimensions.rightDepth) / 2

  const cutoutOverlays = cutouts.map(cutout => buildCutoutOverlay(cutout, geometry))
  const previewScale = previewScalePercent / 100
  const effectiveViewPreset = viewOverride || (currentStep === 3 ? 'legHeights' : 'default')
  const effectiveFabrication = useMemo(() => getEffectiveFabrication(fabrication), [fabrication])
  const drainConflictLegIds = useMemo(
    () => legPositions.filter(position => isLegNearDrain(position, drain, averageWidth, averageDepth)).map(position => position.id),
    [legPositions, drain, averageWidth, averageDepth]
  )

  useEffect(() => {
    setViewOverride('')
    setViewResetToken(token => token + 1)
  }, [currentStep])

  useEffect(() => {
    const stickyNode = previewStickyRef.current
    const cardNode = previewCardRef.current
    if (!stickyNode || !cardNode || typeof ResizeObserver === 'undefined') return undefined

    const updateHeight = () => {
      stickyNode.style.setProperty('--preview-card-height', `${cardNode.offsetHeight}px`)
    }

    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(cardNode)

    return () => observer.disconnect()
  }, [currentStep, previewScalePercent, dimensions, cutouts, setbacks, fabrication, legHeights, focusedLeg, config.legs])

  return (
    <section className="order-preview-panel">
      <div ref={previewStickyRef} className="order-preview-sticky">
        <div ref={previewCardRef} className="order-preview-card">
          <header>
            <div className="order-preview-header-row">
              <h3>3D Preview</h3>
              <div className="order-preview-header-actions">
                <div className="order-preview-view-buttons">
                  <button
                    type="button"
                    className="order-preview-view-button"
                    onClick={() => {
                      setViewOverride(currentStep === 3 ? 'legHeights' : 'default')
                      setViewResetToken(token => token + 1)
                    }}
                  >
                    Reset View
                  </button>
                  <button
                    type="button"
                    className="order-preview-view-button"
                    onClick={() => {
                      setViewOverride('bird')
                      setViewResetToken(token => token + 1)
                    }}
                  >
                    Bird&apos;s Eye
                  </button>
                </div>
                <label className="order-preview-scale-control">
                  <span>{`Scale ${previewScalePercent}%`}</span>
                  <input
                    className="order-range"
                    type="range"
                    min={60}
                    max={120}
                    value={previewScalePercent}
                    style={getSliderFillStyle(previewScalePercent, 60, 120)}
                    onChange={event => setPreviewScalePercent(Number(event.target.value))}
                  />
                </label>
              </div>
            </div>
          </header>
          <div className="order-preview-stage">
            <div className="order-preview-plane">
              <OrderThreeScene
                geometry={geometry}
                dimensions={dimensions}
                entrySides={entrySides}
                squareCorners={squareCorners}
                drain={drain}
                setbacks={setbacks}
                cutoutOverlays={cutoutOverlays}
                fabrication={effectiveFabrication}
                legPositions={legPositions}
                legHeights={legHeights}
                focusedLeg={focusedLeg}
                currentStep={currentStep}
                viewPreset={effectiveViewPreset}
                viewResetToken={viewResetToken}
                previewScale={previewScale}
                drainConflictLegIds={drainConflictLegIds}
                quickApplyFocused={quickApplyFocused}
                onCaptureReady={onCaptureReady}
              />
            </div>
          </div>
          <footer>Drag to rotate • {config.legs} leg supports</footer>
        </div>
        {currentStep >= 1 && currentStep <= 6 && (
          <div className="order-preview-aux">
            <VideoHelpCard
              title={getPreviewHelpTitle(currentStep, stepOneSummaryOpen)}
              description={getPreviewHelpDescription(currentStep, stepOneSummaryOpen)}
              thumbnail={orderHelpContent[getPreviewHelpKey(currentStep, stepOneSummaryOpen)]?.thumbnail || constructionImage}
              onClick={() => {
                const helpKey = getPreviewHelpKey(currentStep, stepOneSummaryOpen)
                if (helpKey) onOpenHelp(helpKey)
              }}
            />
          </div>
        )}
      </div>
    </section>
  )
}

const ReviewMetric = ({ label, value }) => (
  <div className="order-review-metric">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
)

const ReviewInlinePair = ({ label, value }) => (
  <div className="order-review-inline-pair">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
)

const ReviewSectionTitle = ({ title, onEdit }) => (
  <div className="order-review-section-title">
    <span>{title}</span>
    {onEdit && (
      <button type="button" className="order-review-edit inline icon-only" onClick={onEdit} aria-label={`Edit ${title}`}>
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M8.86 1.39a1.35 1.35 0 0 1 1.91 0l.84.84a1.35 1.35 0 0 1 0 1.91L5.26 10.5l-2.55.64.64-2.55L8.86 1.39Z" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
          <path d="m7.97 2.28 2.75 2.75" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        </svg>
      </button>
    )}
  </div>
)

const ReviewUploadList = ({ uploads }) => {
  if (!uploads.length) {
    return <p>No uploads provided.</p>
  }

  return (
    <div className="order-review-upload-list">
      {uploads.map(file => (
        <div key={file.id} className="order-review-upload-item">
          {file.previewUrl ? <img src={file.previewUrl} alt={file.name} /> : <span>{file.name}</span>}
          <small>{file.name}</small>
        </div>
      ))}
    </div>
  )
}

const CutoutEditIcon = () => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M4.25 6.38 8.5 10.63l4.25-4.25" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const CutoutCollapseIcon = () => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M4.25 10.63 8.5 6.38l4.25 4.25" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const CutoutRemoveIcon = () => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M5.67 2.83h5.66" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M7.08 2.83h2.84a.71.71 0 0 1 .7.71v.71H6.38v-.71a.71.71 0 0 1 .7-.71Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M4.25 4.25h8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="m5.67 4.25.48 7.26a.71.71 0 0 0 .71.67h3.28a.71.71 0 0 0 .71-.67l.48-7.26" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.79 6.38v3.54M9.92 6.38v3.54" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
)

const CutoutTypeIcon = ({ type, active = false }) => {
  const stroke = active ? '#ffffff' : '#6c737f'

  if (type === 'screen-door') {
    return (
      <svg className="cutout-type-glyph cutout-type-glyph-screen-door" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M10.8333 3.33333H13.3333C13.7754 3.33333 14.1993 3.50893 14.5118 3.82149C14.8244 4.13405 15 4.55797 15 5V16.6667" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1.66667 16.6667H4.16667" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10.8333 16.6667H18.3333" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.33333 10V10.0083" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10.8333 3.80167V17.2658C10.8333 17.3924 10.8044 17.5173 10.7489 17.6311C10.6934 17.7448 10.6127 17.8445 10.5129 17.9224C10.4132 18.0003 10.297 18.0544 10.1732 18.0807C10.0493 18.107 9.92115 18.1048 9.79833 18.0742L4.16667 16.6667V4.635C4.16672 4.26334 4.291 3.90236 4.51975 3.60944C4.74851 3.31652 5.0686 3.10846 5.42917 3.01833L8.7625 2.185C9.00813 2.1236 9.26451 2.11897 9.51219 2.17146C9.75987 2.22395 9.99234 2.33218 10.192 2.48793C10.3916 2.64368 10.5531 2.84286 10.6642 3.07035C10.7753 3.29783 10.8332 3.54848 10.8333 3.80167Z" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }

  if (type === 'screen') {
    return (
      <svg className="cutout-type-glyph cutout-type-glyph-screen" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M18.3333 5H1.66667" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.3333 15H1.66667" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 1.66667V18.3333" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15 1.66667V18.3333" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }

  if (type === 'pipes') {
    return (
      <svg className="cutout-type-glyph cutout-type-glyph-pipes" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M12.25 5.25C12.0973 5.40577 12.0118 5.61521 12.0118 5.83333C12.0118 6.05146 12.0973 6.26089 12.25 6.41667L13.5833 7.75C13.7391 7.90269 13.9485 7.98821 14.1667 7.98821C14.3848 7.98821 14.5942 7.90269 14.75 7.75L17.8917 4.60833C18.3107 5.53432 18.4376 6.56603 18.2554 7.56595C18.0732 8.56588 17.5906 9.48654 16.8719 10.2052C16.1532 10.9239 15.2325 11.4065 14.2326 11.5887C13.2327 11.7709 12.201 11.644 11.275 11.225L5.51667 16.9833C5.18515 17.3149 4.73551 17.5011 4.26667 17.5011C3.79783 17.5011 3.34819 17.3149 3.01667 16.9833C2.68515 16.6518 2.4989 16.2022 2.4989 15.7333C2.4989 15.2645 2.68515 14.8149 3.01667 14.4833L8.775 8.725C8.35597 7.79901 8.2291 6.76731 8.41129 5.76738C8.59347 4.76745 9.07607 3.8468 9.79477 3.1281C10.5135 2.40941 11.4341 1.92681 12.434 1.74462C13.434 1.56243 14.4657 1.6893 15.3917 2.10833L12.2583 5.24167L12.25 5.25Z" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }

  return (
    <svg className="cutout-type-glyph cutout-type-glyph-corner-channel" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M15.8333 2.5H4.16667C3.24619 2.5 2.5 3.24619 2.5 4.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V4.16667C17.5 3.24619 16.7538 2.5 15.8333 2.5Z" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 7.5H17.5" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 12.5H17.5" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.5 2.5V17.5" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.5 2.5V17.5" stroke={stroke} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ShowerBaseOrder() {
  const [state, setState] = useState(buildInitialState)
  const [addressPredictions, setAddressPredictions] = useState([])
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false)
  const [addressLoading, setAddressLoading] = useState(false)
  const [addressNoResults, setAddressNoResults] = useState(false)
  const [addressActiveIndex, setAddressActiveIndex] = useState(-1)
  const [addressLookupUnavailable, setAddressLookupUnavailable] = useState(false)
  const [activeHelpKey, setActiveHelpKey] = useState('')
  const addressAutocompleteRequestIdRef = useRef(0)
  const deliveryAutocompleteContainerRef = useRef(null)
  const skipNextAutocompleteFetchRef = useRef(false)
  const placesSessionTokenRef = useRef(createPlacesSessionToken())
  const saveDraftTimeoutRef = useRef(null)
  const [quickApplyFocused, setQuickApplyFocused] = useState(false)

  useEffect(() => {
    if (!activeHelpKey) return undefined

    const handleKeyDown = event => {
      if (event.key === 'Escape') setActiveHelpKey('')
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [activeHelpKey])

  const pricingTier = useMemo(
    () => matchPricingTier({
      width: { front: state.dimensions.frontWidth, rear: state.dimensions.rearWidth },
      depth: { left: state.dimensions.leftDepth, right: state.dimensions.rightDepth },
    }),
    [state.dimensions]
  )

  const baseShape = getBaseShape(state.dimensions)
  const averageWidth = (state.dimensions.frontWidth + state.dimensions.rearWidth) / 2
  const averageDepth = (state.dimensions.leftDepth + state.dimensions.rightDepth) / 2
  const irregularShape = baseShape !== 'Rectangle'
  const maxWidth = Math.max(toNumber(state.dimensions.frontWidth), toNumber(state.dimensions.rearWidth))
  const maxDepth = Math.max(toNumber(state.dimensions.leftDepth), toNumber(state.dimensions.rightDepth))
  const summaryReferenceMatch = summaryReferenceRows.find(row => row.matches({
    width: averageWidth,
    depth: averageDepth,
    cols: pricingTier.cols,
    rows: pricingTier.rows,
  }))?.id || ''
  const combinedOversize = maxWidth > COMBINED_LIMIT_MAX && maxDepth > COMBINED_LIMIT_MAX
  const dimensionsOverLimit = Object.values(state.dimensions).some(value => toNumber(value) > MM_MAX)
  const dimensionFieldErrors = useMemo(() => getDimensionFieldErrors(state.dimensions), [state.dimensions])
  const deliveryPrice = state.fulfilment.method === 'Delivery' ? DELIVERY_FEE : 0
  const cutoutTotal = state.cutoutsEnabled ? state.cutouts.length * CUTOUT_FEE : 0
  const splitBaseFee = state.fabrication.splitBase ? SPLIT_BASE_FEE : 0
  const displayTotal = pricingTier.price + deliveryPrice + cutoutTotal + splitBaseFee
  const estimatedWeight = (((averageWidth / 1000) * (averageDepth / 1000) * 13.5) + 1).toFixed(1)
  const effectiveFabrication = useMemo(() => getEffectiveFabrication(state.fabrication), [state.fabrication])

  const legPositions = useMemo(
    () => buildLegPositions({ cols: pricingTier.cols, rows: pricingTier.rows, setbacks: state.setbacks, dimensions: state.dimensions }),
    [pricingTier, state.setbacks, state.dimensions]
  )
  const legHeightsCompletion = useMemo(
    () => getLegHeightsCompletion(state.legHeights, legPositions),
    [state.legHeights, legPositions]
  )

  const cornerLegIds = useMemo(() => {
    const lastRow = String.fromCharCode(64 + pricingTier.rows)
    const lastCol = pricingTier.cols
    return [`A1`, `A${lastCol}`, `${lastRow}1`, `${lastRow}${lastCol}`]
  }, [pricingTier.cols, pricingTier.rows])

  const clearAddressErrors = () => {
    setState(previous => {
      if (!previous.submitError) return previous
      return { ...previous, submitError: '' }
    })
  }

  const draftPayload = useMemo(() => ({
    draftId: state.draftId,
    orderId: state.orderId,
    mode: state.mode,
    currentStep: state.currentStep,
    contact: sanitizeForTransport(state.contact),
    fulfilment: sanitizeForTransport(state.fulfilment),
    totals: {
      basePrice: pricingTier.price,
      cutoutTotal,
      deliveryPrice,
      splitBaseFee,
      displayTotal,
      estimatedWeight,
      legCount: pricingTier.legs,
      gridLabel: `${pricingTier.cols} × ${pricingTier.rows} (${pricingTier.legs} legs)`,
    },
    configuration: sanitizeForTransport({
      dimensions: state.dimensions,
      entrySides: state.entrySides,
      roundedCornersEnabled: state.roundedCornersEnabled,
      roundedCorners: state.roundedCorners,
      squareCorners: state.squareCorners,
      setbacks: state.setbacks,
      legHeights: state.legHeights,
      focusedLeg: state.focusedLeg,
      drain: state.drain,
      cutoutsEnabled: state.cutoutsEnabled,
      cutouts: state.cutouts,
      fabrication: effectiveFabrication,
      uploads: state.uploads.map(toUploadPayload),
      additionalInfo: state.additionalInfo,
    }),
  }), [
    state.draftId,
    state.orderId,
    state.mode,
    state.currentStep,
    state.contact,
    state.fulfilment,
    pricingTier.price,
    pricingTier.cols,
    pricingTier.rows,
    pricingTier.legs,
    cutoutTotal,
    deliveryPrice,
    splitBaseFee,
    displayTotal,
    estimatedWeight,
    state.dimensions,
    state.entrySides,
    state.roundedCornersEnabled,
    state.roundedCorners,
    state.squareCorners,
    state.setbacks,
    state.legHeights,
    state.focusedLeg,
    state.drain,
    state.cutoutsEnabled,
    state.cutouts,
    effectiveFabrication,
    state.uploads,
    state.additionalInfo,
  ])

  const buildOrderSubmissionPayload = ({ previewImageDataUrl = '' } = {}) => ({
    draftId: state.draftId,
    orderId: state.orderId,
    currentStep: state.currentStep,
    contact: sanitizeForTransport(state.contact),
    fulfilment: sanitizeForTransport(state.fulfilment),
    totals: {
      basePrice: pricingTier.price,
      cutoutTotal,
      deliveryPrice,
      splitBaseFee,
      displayTotal,
      estimatedWeight,
      legCount: pricingTier.legs,
      gridLabel: `${pricingTier.cols} × ${pricingTier.rows} (${pricingTier.legs} legs)`,
      tierCode: pricingTier.code,
    },
    fileUploads: state.uploads.filter(file => file.url).map(toUploadPayload),
    configuration: sanitizeForTransport({
      dimensions: state.dimensions,
      baseShape,
      entrySides: state.entrySides,
      roundedCornersEnabled: state.roundedCornersEnabled,
      roundedCorners: state.roundedCorners,
      squareCorners: state.squareCorners,
      setbacks: state.setbacks,
      legHeights: state.legHeights,
      drain: state.drain,
      cutoutsEnabled: state.cutoutsEnabled,
      cutouts: state.cutoutsEnabled ? state.cutouts : [],
      fabrication: effectiveFabrication,
      additionalInfo: state.additionalInfo,
      flags: {
        drainConflict: drainOverlap,
      },
    }),
    ...(previewImageDataUrl ? { previewImageDataUrl } : {}),
  })

  const previewCaptureRef = useRef(null)

  const capturePreviewImage = async () => {
    await waitForPaint()
    if (typeof previewCaptureRef.current !== 'function') return ''
    try {
      return await previewCaptureRef.current()
    } catch (_) {
      return ''
    }
  }

  useEffect(() => {
    const nextHeights = {}
    legPositions.forEach(position => {
      nextHeights[position.id] = state.legHeights[position.id] || ''
    })
    const currentKeys = Object.keys(state.legHeights)
    const nextKeys = Object.keys(nextHeights)
    const changed =
      currentKeys.length !== nextKeys.length ||
      nextKeys.some(key => state.legHeights[key] !== nextHeights[key])

    if (!changed) return

    setState(previous => ({ ...previous, legHeights: nextHeights }))
  }, [legPositions, state.legHeights])

  useEffect(() => {
    if (state.currentStep !== 3 || state.focusedLeg) return
    const firstCorner = cornerLegIds.find(id => state.legHeights[id] !== undefined) || Object.keys(state.legHeights)[0]
    if (!firstCorner) return
    setState(previous => ({ ...previous, focusedLeg: firstCorner }))
  }, [state.currentStep, state.focusedLeg, state.legHeights, cornerLegIds])

  useEffect(() => {
    if (irregularShape || state.squareCorners.length === 0) return
    setState(previous => ({ ...previous, squareCorners: [] }))
  }, [irregularShape, state.squareCorners.length])

  useEffect(() => {
    if (!state.fabrication.splitBase) return
    if (!state.fabrication.installationOrder) return

    setState(previous => {
      const installationOrder = previous.fabrication.installationOrder
      const inactiveKey = installationOrder === 'Base 1 First' ? 'fingerHolesBase1' : 'fingerHolesBase2'
      const activeKey = installationOrder === 'Base 1 First' ? 'fingerHolesBase2' : 'fingerHolesBase1'
      const defaultSide = installationOrder === 'Base 1 First' ? 'Right Side' : 'Left Side'
      const nextInactive = []
      const nextActive = previous.fabrication[activeKey].length > 0 ? previous.fabrication[activeKey] : [defaultSide]

      const unchanged =
        previous.fabrication[inactiveKey].length === 0 &&
        previous.fabrication[activeKey].length === nextActive.length &&
        previous.fabrication[activeKey].every((value, index) => value === nextActive[index])

      if (unchanged) return previous

      return {
        ...previous,
        fabrication: {
          ...previous.fabrication,
          [inactiveKey]: nextInactive,
          [activeKey]: nextActive,
        },
      }
    })
  }, [state.fabrication.splitBase, state.fabrication.installationOrder])

  useEffect(() => {
    if (state.mode === 'landing' || state.mode === 'confirmation') return

    if (saveDraftTimeoutRef.current) {
      clearTimeout(saveDraftTimeoutRef.current)
    }

    setState(previous => (previous.isSavingDraft ? previous : { ...previous, isSavingDraft: true }))

    saveDraftTimeoutRef.current = setTimeout(() => {
      fetch(SAVE_DRAFT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draftPayload),
      })
        .then(async response => {
          if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}))
            throw new Error(errorBody.error || 'Failed to save draft.')
          }
          return response.json()
        })
        .then(response => {
          setState(previous => ({
            ...previous,
            draftId: response?.draftId || previous.draftId,
            lastSavedAt: response?.updatedAt || new Date().toISOString(),
            isSavingDraft: false,
          }))
        })
        .catch(() => {
          setState(previous => ({ ...previous, isSavingDraft: false }))
        })
    }, 700)

    return () => {
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current)
      }
    }
  }, [
    state.mode,
    draftPayload,
  ])

  useEffect(() => {
    if (state.mode !== 'confirmation') return

    try {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'shower_base_order_complete',
        order_id: state.submittedReference || state.orderId,
        value: displayTotal,
      })
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'purchase', {
          transaction_id: state.submittedReference || state.orderId,
          value: displayTotal,
          currency: 'AUD',
        })
      }
    } catch (_) {
      // Analytics hooks are optional when trackers are unavailable.
    }

    try {
      window.sessionStorage.removeItem('shower-base-order-state')
      window.localStorage.removeItem('shower-base-order-state')
    } catch (_) {
      // Ignore storage clearing issues.
    }
  }, [state.mode, state.submittedReference, state.orderId, displayTotal])

  useEffect(() => {
    if (state.currentStep !== 8 || state.fulfilment.method !== 'Delivery') return
    if (!GOOGLE_MAPS_API_KEY) {
      setAddressLookupUnavailable(true)
      return
    }
    if (skipNextAutocompleteFetchRef.current) {
      skipNextAutocompleteFetchRef.current = false
      return
    }

    const inputValue = (state.fulfilment.address || '').trim()
    if (inputValue.length < 2) {
      setAddressPredictions([])
      setAddressDropdownOpen(false)
      setAddressLoading(false)
      setAddressNoResults(false)
      setAddressActiveIndex(-1)
      return
    }

    const currentRequestId = ++addressAutocompleteRequestIdRef.current
    const timeoutId = setTimeout(() => {
      setAddressLoading(true)
      setAddressNoResults(false)
      fetch(GOOGLE_PLACES_AUTOCOMPLETE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text',
        },
        body: JSON.stringify({
          input: inputValue,
          languageCode: 'en-AU',
          regionCode: 'AU',
          includedRegionCodes: ['AU'],
          sessionToken: placesSessionTokenRef.current,
        }),
      })
        .then(async response => {
          if (!response.ok) {
            const errorBody = await response.text().catch(() => '')
            throw new Error(errorBody || `Autocomplete request failed (${response.status})`)
          }
          return response.json()
        })
        .then(data => {
          if (currentRequestId !== addressAutocompleteRequestIdRef.current) return
          const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : []
          const items = suggestions
            .map(item => {
              const prediction = item?.placePrediction
              return prediction?.placeId
                ? {
                  place_id: prediction.placeId,
                  description: prediction?.text?.text || '',
                }
                : null
            })
            .filter(Boolean)

          setAddressLookupUnavailable(false)
          setAddressPredictions(items)
          setAddressDropdownOpen(true)
          setAddressLoading(false)
          setAddressNoResults(items.length === 0)
          setAddressActiveIndex(items.length ? 0 : -1)
        })
        .catch(() => {
          if (currentRequestId !== addressAutocompleteRequestIdRef.current) return
          setAddressLookupUnavailable(true)
          setAddressPredictions([])
          setAddressDropdownOpen(false)
          setAddressLoading(false)
          setAddressNoResults(false)
          setAddressActiveIndex(-1)
        })
    }, 220)

    return () => clearTimeout(timeoutId)
  }, [state.currentStep, state.fulfilment.method, state.fulfilment.address])

  useEffect(() => {
    if (state.currentStep !== 8 || state.fulfilment.method !== 'Delivery') return undefined

    const closeDropdown = event => {
      const root = deliveryAutocompleteContainerRef.current
      if (!root || root.contains(event.target)) return
      setAddressDropdownOpen(false)
      setAddressActiveIndex(-1)
    }

    document.addEventListener('mousedown', closeDropdown)
    document.addEventListener('touchstart', closeDropdown)
    return () => {
      document.removeEventListener('mousedown', closeDropdown)
      document.removeEventListener('touchstart', closeDropdown)
    }
  }, [state.currentStep, state.fulfilment.method])

  const hardErrors = useMemo(() => {
    const errors = {}

    if (combinedOversize) {
      errors.combinedLimit = 'Width and depth cannot both exceed 1200mm in this configurator.'
    }

    if (Object.keys(dimensionFieldErrors).length > 0) {
      errors.dimensions = 'Enter valid dimensions before proceeding.'
    }

    if (dimensionsOverLimit) {
      errors.customQuote = CUSTOM_QUOTE_MESSAGE
    }

    if (state.currentStep === 3) {
      if (legHeightsCompletion.allEntered && legHeightsCompletion.hasOutOfRangeValue) {
        errors.legHeights = `Every leg must be completed with a height between ${LEG_HEIGHT_MIN}mm and ${LEG_HEIGHT_MAX}mm.`
      }
    }

    if (state.currentStep === 8) {
      if (!state.contact.fullName.trim()) errors.fullName = 'Full name is required.'
      if (!/\S+@\S+\.\S+/.test(state.contact.email)) errors.email = 'A valid email address is required.'
      if (!state.contact.phone.trim()) errors.phone = 'Phone number is required.'
      if (state.fulfilment.method === 'Delivery') {
        if (!state.fulfilment.address.trim()) errors.address = 'Address is required for delivery.'
        if (!state.fulfilment.suburb.trim()) errors.suburb = 'Suburb is required for delivery.'
        if (!state.fulfilment.state.trim()) errors.state = 'State is required for delivery.'
        if (!state.fulfilment.postcode.trim()) errors.postcode = 'Postcode is required for delivery.'
        if (!state.fulfilment.authorityToLeave.trim()) errors.authorityToLeave = 'Select whether delivery can be left unattended.'
      }
    }

    return errors
  }, [combinedOversize, dimensionFieldErrors, dimensionsOverLimit, state.currentStep, legHeightsCompletion, state.contact, state.fulfilment])

  const showContactValidation = state.currentStep === 8 && state.contactSubmitAttempted

  const softWarnings = useMemo(() => {
    const warnings = []

    if (irregularShape && state.squareCorners.length < 1) {
      warnings.push('Your dimensions suggest a non-rectangular base. Select one square corner to ensure proper fit.')
    }

    if (Object.values(state.setbacks).some(value => toNumber(value) > 50)) {
      warnings.push(SETBACK_WARNING_MESSAGE)
    }

    // const heights = Object.values(state.legHeights).map(toNumber).filter(Boolean)
    // if (heights.length > 0 && new Set(heights).size === 1) {
    //   warnings.push('All leg heights are identical. A perfectly flat base is unusual for drainage.')
    // }

    return warnings
  }, [irregularShape, state.squareCorners, state.setbacks])

  const drainOverlap = useMemo(() => {
    return legPositions.some(position => isLegNearDrain(position, state.drain, averageWidth, averageDepth))
  }, [state.drain, legPositions, averageWidth, averageDepth])

  const updateState = updater => {
    setState(previous => (typeof updater === 'function' ? updater(previous) : { ...previous, ...updater }))
  }

  const updateDimension = (key, value) => {
    const numeric = value === '' ? '' : clamp(toNumber(value), 0, 2200)
    updateState(previous => ({
      ...previous,
      dimensions: {
        ...previous.dimensions,
        [key]: numeric,
      },
    }))
  }

  const updateSetback = (key, value) => {
    updateState(previous => ({
      ...previous,
      setbacks: {
        ...previous.setbacks,
        [key]: value === '' ? '' : Math.max(0, toNumber(value)),
      },
    }))
  }

  const toggleEntrySide = option => {
    updateState(previous => ({
      ...previous,
      entrySides: previous.entrySides.includes(option)
        ? previous.entrySides.filter(item => item !== option)
        : [...previous.entrySides, option],
    }))
  }

  const toggleSquareCorner = option => {
    updateState(previous => ({
      ...previous,
      squareCorners: previous.squareCorners[0] === option ? [] : [option],
    }))
  }

  const updateRoundedCorner = (key, value) => {
    updateState(previous => ({
      ...previous,
      roundedCorners: {
        ...previous.roundedCorners,
        [key]: value,
      },
    }))
  }

  const applyRoundedQuickValue = () => {
    if (!state.roundedCorners.quickApply) return
    const value = state.roundedCorners.quickApply
    updateState(previous => ({
      ...previous,
      roundedCorners: {
        ...previous.roundedCorners,
        frontLeftA: previous.roundedCorners.frontLeftNotRounded ? previous.roundedCorners.frontLeftA : value,
        frontLeftB: previous.roundedCorners.frontLeftNotRounded ? previous.roundedCorners.frontLeftB : value,
        frontRightA: previous.roundedCorners.frontRightNotRounded ? previous.roundedCorners.frontRightA : value,
        frontRightB: previous.roundedCorners.frontRightNotRounded ? previous.roundedCorners.frontRightB : value,
        rearLeftA: previous.roundedCorners.rearLeftNotRounded ? previous.roundedCorners.rearLeftA : value,
        rearLeftB: previous.roundedCorners.rearLeftNotRounded ? previous.roundedCorners.rearLeftB : value,
        rearRightA: previous.roundedCorners.rearRightNotRounded ? previous.roundedCorners.rearRightA : value,
        rearRightB: previous.roundedCorners.rearRightNotRounded ? previous.roundedCorners.rearRightB : value,
      },
    }))
  }

  const applyQuickHeights = () => {
    updateState(previous => {
      const next = {}
      Object.keys(previous.legHeights).forEach(key => {
        next[key] = previous.quickHeight
      })
      return {
        ...previous,
        legHeights: next,
      }
    })
  }

  const updateCutout = (id, key, value) => {
    updateState(previous => ({
      ...previous,
      cutouts: previous.cutouts.map(cutout => (
        cutout.id === id
          ? { ...cutout, [key]: value }
          : cutout
      )),
    }))
  }

  const switchCutoutType = (id, type) => {
    updateState(previous => ({
      ...previous,
      cutouts: previous.cutouts.map(cutout => (
        cutout.id === id
          ? { ...createCutout(type), id: cutout.id }
          : cutout
      )),
    }))
  }

  const getCutoutPositionOptions = cutout => {
    if (cutout.side === 'Left' || cutout.side === 'Right') {
      return [
        { label: 'Bottom Side', value: 'Left' },
        { label: 'Top Side', value: 'Right' },
      ]
    }

    return [
      { label: 'Left Hand Side', value: 'Left' },
      { label: 'Right Hand Side', value: 'Right' },
    ]
  }

  const getScreenDoorDistanceLabels = cutout => (
    cutout.side === 'Left' || cutout.side === 'Right'
      ? {
        primary: 'Distance from front edge to center of door frame (mm)',
        secondary: 'Distance from back edge to center of door frame (mm)',
      }
      : {
        primary: 'Distance from left edge to center of door frame (mm)',
        secondary: 'Distance from right edge to center of door frame (mm)',
      }
  )

  const getPipeFixtureDistanceLabel = cutout => (
    cutout.side === 'Left' || cutout.side === 'Right'
      ? 'Distance from bottom to center of pipe/fixture (mm)'
      : 'Distance from left to center of pipe/fixture (mm)'
  )

  const getReviewEditConfig = target => {
    if (target === 'dimensions') {
      return {
        title: 'Warning: Changing Dimensions',
        body: 'Changing the base dimensions will require you to re-do the entire configuration flow.',
        continueLabel: 'CONTINUE TO EDIT',
        step: 1,
      }
    }

    if (target === 'setbacks') {
      return {
        title: 'Warning: Changing Leg Setback Distance',
        body: 'Changing the leg setback distance may require you to re-measure and record your leg heights.',
        continueLabel: 'CONTINUE TO EDIT',
        step: 2,
      }
    }

    return null
  }

  const beginReviewEdit = target => {
    const config = getReviewEditConfig(target)

    if (config) {
      updateState(previous => ({ ...previous, reviewEditTarget: target }))
      return
    }

    const stepByTarget = {
      legHeights: 3,
      drainage: 4,
      cutouts: 5,
      fabrication: 6,
      contact: 8,
    }

    const nextStep = stepByTarget[target] || 8
    updateState(previous => ({
      ...previous,
      mode: 'configurator',
      currentStep: nextStep,
      mobileSectionOpen: nextStep,
    }))
  }

  const continueReviewEdit = () => {
    const config = getReviewEditConfig(state.reviewEditTarget)
    if (!config) return

    updateState(previous => {
      if (previous.reviewEditTarget === 'dimensions') {
        return {
          ...previous,
          reviewEditTarget: null,
          mode: 'configurator',
          currentStep: 1,
          mobileSectionOpen: 1,
          stepOneSummaryOpen: false,
          setbacks: defaultSetbacks(),
          legHeights: {},
          focusedLeg: '',
        }
      }

      if (previous.reviewEditTarget === 'setbacks') {
        return {
          ...previous,
          reviewEditTarget: null,
          mode: 'configurator',
          currentStep: 2,
          mobileSectionOpen: 2,
          legHeights: {},
          focusedLeg: '',
        }
      }

      return {
        ...previous,
        reviewEditTarget: null,
        mode: 'configurator',
        currentStep: config.step,
        mobileSectionOpen: config.step,
        stepOneSummaryOpen: config.step === 1 ? false : previous.stepOneSummaryOpen,
      }
    })
  }

  const selectDeliveryPrediction = prediction => {
    if (!prediction) return
    if (!GOOGLE_MAPS_API_KEY) {
      setAddressLookupUnavailable(true)
      return
    }

    fetch(
      `${GOOGLE_PLACES_DETAILS_URL}/${encodeURIComponent(prediction.place_id)}?languageCode=en-AU&regionCode=AU&sessionToken=${encodeURIComponent(placesSessionTokenRef.current)}`,
      {
        headers: {
          'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask': 'formattedAddress,addressComponents',
        },
      }
    )
      .then(async response => {
        if (!response.ok) {
          const errorBody = await response.text().catch(() => '')
          throw new Error(errorBody || `Place details request failed (${response.status})`)
        }
        return response.json()
      })
      .then(placeResult => {
        const nextAddress = placeResult?.formattedAddress || prediction.description || ''
        const components = Array.isArray(placeResult?.addressComponents) ? placeResult.addressComponents : []
        const suburb = getAddressComponent(components, 'locality') || getAddressComponent(components, 'sublocality')
        const stateCode = getAddressComponent(components, 'administrative_area_level_1')
        const postcode = getAddressComponent(components, 'postal_code')
        skipNextAutocompleteFetchRef.current = true
        updateState(previous => ({
          ...previous,
          fulfilment: {
            ...previous.fulfilment,
            address: nextAddress,
            suburb: suburb || previous.fulfilment.suburb,
            state: stateCode || previous.fulfilment.state,
            postcode: postcode || previous.fulfilment.postcode,
          },
        }))
        setAddressLookupUnavailable(false)
        clearAddressErrors()
        setAddressDropdownOpen(false)
        setAddressPredictions([])
        setAddressNoResults(false)
        setAddressActiveIndex(-1)
        placesSessionTokenRef.current = createPlacesSessionToken()
      })
      .catch(() => {
        setAddressLookupUnavailable(true)
        skipNextAutocompleteFetchRef.current = true
        updateState(previous => ({
          ...previous,
          fulfilment: { ...previous.fulfilment, address: prediction.description || '' },
        }))
        clearAddressErrors()
        setAddressDropdownOpen(false)
      })
  }

  const handleDeliveryAddressKeyDown = event => {
    if (!addressDropdownOpen || !addressPredictions.length) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setAddressActiveIndex(previous => (previous + 1) % addressPredictions.length)
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setAddressActiveIndex(previous => (previous <= 0 ? addressPredictions.length - 1 : previous - 1))
      return
    }
    if (event.key === 'Enter' && addressActiveIndex >= 0) {
      event.preventDefault()
      selectDeliveryPrediction(addressPredictions[addressActiveIndex])
      return
    }
    if (event.key === 'Escape') {
      setAddressDropdownOpen(false)
      setAddressActiveIndex(-1)
    }
  }

  const captureContactLead = async () => {
    updateState(previous => ({ ...previous, isCapturingLead: true, submitError: '' }))

    try {
      const response = await fetch(CAPTURE_LEAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildOrderSubmissionPayload()),
      })

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}))
        const message = errorBody.stage
          ? `${errorBody.error || 'Failed to save contact details.'} (${errorBody.stage})`
          : (errorBody.error || 'Failed to save contact details.')
        throw new Error(message)
      }

      const result = await response.json()
      updateState(previous => ({
        ...previous,
        isCapturingLead: false,
        submitError: '',
        orderId: result?.orderId || previous.orderId,
      }))
      return result
    } catch (error) {
      updateState(previous => ({
        ...previous,
        isCapturingLead: false,
        submitError: error.message || 'Failed to save contact details.',
      }))
      throw error
    }
  }

  const startCheckout = async () => {
    const pendingUploads = state.uploads.some(file => file.status === 'uploading')
    if (pendingUploads) {
      updateState(previous => ({ ...previous, submitError: 'Please wait for file uploads to finish before proceeding to checkout.' }))
      return
    }

    updateState(previous => ({ ...previous, isStartingCheckout: true, submitError: '' }))

    try {
      const previewImageDataUrl = await capturePreviewImage()
      const response = await fetch(SUBMIT_ORDER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...buildOrderSubmissionPayload({ previewImageDataUrl }),
          submissionType: 'checkout',
        }),
      })

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}))
        const message = errorBody.stage
          ? `${errorBody.error || 'Failed to start checkout.'} (${errorBody.stage})`
          : (errorBody.error || 'Failed to start checkout.')
        throw new Error(message)
      }

      const result = await response.json()
      updateState(previous => ({
        ...previous,
        isStartingCheckout: false,
        orderId: result?.orderId || previous.orderId,
      }))

      if (result?.paymentUrl) {
        window.location.assign(result.paymentUrl)
        return
      }

      throw new Error('Payment gateway URL is not configured yet.')
    } catch (error) {
      updateState(previous => ({
        ...previous,
        isStartingCheckout: false,
        submitError: error.message || 'Failed to start checkout.',
      }))
    }
  }

  const addCutout = () => {
    const nextCutout = createCutout('screen-door')
    updateState(previous => ({
      ...previous,
      cutouts: [
        ...previous.cutouts,
        nextCutout,
      ],
      activeCutoutId: nextCutout.id,
    }))
  }

  const handleFiles = async files => {
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf']
    const validFiles = files.filter(file => allowedTypes.includes(file.type) && file.size <= 10 * 1024 * 1024)
    const remainingSlots = Math.max(5 - state.uploads.length, 0)
    const uploadQueue = validFiles.slice(0, remainingSlots)

    const pendingEntries = uploadQueue.map(file => ({
      id: uid('file'),
      name: file.name,
      size: file.size,
      type: file.type,
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
      url: '',
      storagePath: '',
      status: 'uploading',
    }))

    if (!pendingEntries.length) return

    updateState(previous => ({
      ...previous,
      uploads: [...previous.uploads, ...pendingEntries].slice(0, 5),
      submitError: '',
    }))

    await Promise.all(pendingEntries.map(async pending => {
      try {
        const safeName = pending.name.replace(/[^a-zA-Z0-9._-]/g, '-')
        const storagePath = `shower-base-orders/${state.draftId}/${Date.now()}-${pending.id}-${safeName}`
        const fileRef = ref(storage, storagePath)
        const uploadTask = uploadBytesResumable(fileRef, pending.file, { contentType: pending.type })

        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed', undefined, reject, resolve)
        })

        const url = await getDownloadURL(fileRef)
        updateState(previous => ({
          ...previous,
          uploads: previous.uploads.map(file => (
            file.id === pending.id
              ? { ...file, url, storagePath, status: 'uploaded' }
              : file
          )),
        }))
      } catch (_) {
        updateState(previous => ({
          ...previous,
          uploads: previous.uploads.map(file => (
            file.id === pending.id
              ? { ...file, status: 'failed' }
              : file
          )),
          submitError: 'One or more files failed to upload. Remove the failed file and try again.',
        }))
      }
    }))
  }

  const removeUpload = id => {
    const target = state.uploads.find(file => file.id === id)
    if (target?.previewUrl) {
      URL.revokeObjectURL(target.previewUrl)
    }
    updateState(previous => ({
      ...previous,
      uploads: previous.uploads.filter(file => file.id !== id),
    }))
  }

  const removeCutout = id => {
    updateState(previous => {
      const remaining = previous.cutouts.filter(cutout => cutout.id !== id)
      return {
        ...previous,
        cutouts: remaining.length > 0 ? remaining : [createCutout('screen-door')],
        activeCutoutId: remaining.length > 0 ? remaining[0].id : null,
      }
    })
  }

  const stepBlocked = step => {
    if (Object.keys(dimensionFieldErrors).length > 0 || dimensionsOverLimit) return true
    if (step === 1 && (combinedOversize || (irregularShape && state.squareCorners.length !== 1))) return true
    if (step === 3 && !legHeightsCompletion.allValid) return true
    return false
  }

  const isMobileLayout = useMediaQuery('(max-width: 1100px)')
  const mobileAccordionRef = useRef(null)
  const nextStepLabel = state.currentStep === STEP_TOTAL
    ? 'REVIEW ORDER'
    : footerStepLabelOverrides[state.currentStep] || `STEP ${state.currentStep + 1}: ${stepLabels[state.currentStep].toUpperCase()}`
  const previousStepLabel = state.currentStep <= 2
    ? 'STEP 1'
    : `STEP ${Math.max(1, state.currentStep - 1)}`
  const hideConfiguratorPreview = (state.currentStep === 1 && !state.stepOneSummaryOpen) || state.currentStep === 8
  const showMobileFixedPreview = state.mode === 'configurator' && state.currentStep <= 6
  const mobileAccordionOpen = state.mobileSectionOpen === state.currentStep
  const activeCutouts = state.cutoutsEnabled ? state.cutouts : []
  const latestCutoutId = state.cutouts[state.cutouts.length - 1]?.id
  const fabricationReviewRows = getFabricationReviewRows(effectiveFabrication)
  const drainDistanceFromRight = getDrainDistanceFromRight(state.dimensions, state.drain)

  useEffect(() => {
    if (!isMobileLayout || state.mode !== 'configurator') return
    const scrollToTop = () => {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      }
      if (typeof document !== 'undefined' && document.scrollingElement) {
        document.scrollingElement.scrollTop = 0
      }
    }

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      let secondFrameId = 0
      const firstFrameId = window.requestAnimationFrame(() => {
        scrollToTop()
        secondFrameId = window.requestAnimationFrame(scrollToTop)
      })
      return () => {
        window.cancelAnimationFrame(firstFrameId)
        if (secondFrameId) window.cancelAnimationFrame(secondFrameId)
      }
    }

    scrollToTop()
    return undefined
  }, [isMobileLayout, state.mode, state.currentStep, state.stepOneSummaryOpen, state.mobileSectionOpen])

  const goNext = async () => {
    if (state.currentStep === 1 && !state.stepOneSummaryOpen) {
      updateState(previous => ({ ...previous, stepOneSummaryOpen: true }))
      return
    }
    if (state.currentStep === 1 && state.stepOneSummaryOpen) {
      updateState(previous => ({ ...previous, showLockModal: true }))
      return
    }
    if (state.currentStep === 8) {
      updateState(previous => ({ ...previous, contactSubmitAttempted: true }))
      if (Object.keys(hardErrors).length > 0) return
    }
    if (stepBlocked(state.currentStep)) return
    if (state.currentStep === STEP_TOTAL) {
      try {
        await captureContactLead()
      } catch (_) {
        return
      }
    }
    if (state.currentStep < STEP_TOTAL) {
      updateState(previous => ({
        ...previous,
        stepOneSummaryOpen: false,
        currentStep: previous.currentStep + 1,
        mobileSectionOpen: previous.currentStep + 1,
      }))
      return
    }
    updateState(previous => ({ ...previous, mode: 'review' }))
  }

  const goPrev = () => {
    if (state.mode === 'review') {
      updateState(previous => ({
        ...previous,
        mode: 'configurator',
        currentStep: STEP_TOTAL,
        mobileSectionOpen: STEP_TOTAL,
      }))
      return
    }

    if (state.currentStep === 1 && state.stepOneSummaryOpen) {
      updateState(previous => ({
        ...previous,
        stepOneSummaryOpen: false,
      }))
      return
    }

    updateState(previous => ({
      ...previous,
      stepOneSummaryOpen: previous.currentStep === 2,
      currentStep: Math.max(1, previous.currentStep - 1),
      mobileSectionOpen: Math.max(1, previous.currentStep - 1),
    }))
  }

  const beginConfiguration = () => {
    updateState(previous => ({
      ...previous,
      mode: 'configurator',
      currentStep: 1,
      stepOneSummaryOpen: false,
      mobileSectionOpen: 1,
    }))
  }

  const renderLegCard = position => {
    const value = state.legHeights[position.id] || ''
    const numeric = toNumber(value)
    const hasValue = Boolean(value)
    const invalid = hasValue && (numeric < LEG_HEIGHT_MIN || numeric > LEG_HEIGHT_MAX)
    const valid = hasValue && !invalid
    const isFocused = !quickApplyFocused && state.focusedLeg === position.id
    const isQuickApplyActive = quickApplyFocused
    return (
      <div
        key={position.id}
        className={`order-leg-card${isFocused || isQuickApplyActive ? ' focused' : ''}${invalid ? ' invalid' : ''}${valid ? ' valid' : ''}`}
        onClick={() => {
          setQuickApplyFocused(false)
          updateState(previous => ({ ...previous, focusedLeg: position.id }))
        }}
        role="button"
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setQuickApplyFocused(false)
            updateState(previous => ({ ...previous, focusedLeg: position.id }))
          }
        }}
      >
        <span>{position.id}</span>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onFocus={() => {
            setQuickApplyFocused(false)
            updateState(previous => ({ ...previous, focusedLeg: position.id }))
          }}
          onChange={event => updateState(previous => ({
            ...previous,
            focusedLeg: position.id,
            legHeights: { ...previous.legHeights, [position.id]: event.target.value },
          }))}
        />
        <small>mm</small>
      </div>
    )
  }

  // Group leg positions by row letter (A, B, C…) for the mobile "Section" panels.
  const legSections = []
  {
    const indexByLetter = {}
    legPositions.forEach(position => {
      const letter = position.id.charAt(0)
      if (indexByLetter[letter] === undefined) {
        indexByLetter[letter] = legSections.length
        legSections.push({ letter, positions: [] })
      }
      legSections[indexByLetter[letter]].positions.push(position)
    })
  }

  const contentByStep = {
    1: (
      <div className="order-step-body order-step-one">
        {state.stepOneSummaryOpen ? (
          <>
            <div className="order-step-heading">
              <div className="order-step-title">
                <h2>Configuration Summary</h2>
              </div>
              <p>Review your shower base dimensions and calculated leg configuration before proceeding to specify leg setbacks.</p>
            </div>
            <section className="order-summary-card">
              <div className="order-summary-title-row">
                <div className="order-summary-icon"><StepGlyph type="baseDimensions" size={30} /></div>
                <h3>Base Dimensions</h3>
              </div>
              <div className="order-summary-metrics">
                <div><span>Front Width</span><strong>{state.dimensions.frontWidth}mm</strong></div>
                <div><span>Rear Width</span><strong>{state.dimensions.rearWidth}mm</strong></div>
                <div><span>Left Depth</span><strong>{state.dimensions.leftDepth}mm</strong></div>
                <div><span>Right Depth</span><strong>{state.dimensions.rightDepth}mm</strong></div>
              </div>
              <div className="order-summary-shape">Base Shape: <strong>{baseShape}</strong></div>
              <div className="order-summary-sq">Square Corners (90°): {state.squareCorners.join(', ') || '-'}</div>
            </section>
            <section className="order-leg-config-card">
              <div className="order-leg-config-head">
                <div className="order-leg-config-badge"><StepGlyph type="grid" size={22} /></div>
                <div>
                  <h3>Calculated Leg Configuration</h3>
                  <p>Automatically determined based on your shower base dimensions</p>
                </div>
              </div>
              <div className="order-leg-config-hero">
                <span>Grid Layout</span>
                <strong>{pricingTier.cols} × {pricingTier.rows}</strong>
                <small>(columns × rows)</small>
              </div>
              <div className="order-leg-config-total">
                <span>Total Leg Supports Required</span>
                <strong>{pricingTier.legs} <small>legs</small></strong>
              </div>
            </section>
            <section className="order-reference-card">
              <div className="order-reference-title-row">
                <span className="order-reference-icon" aria-hidden="true">
                  <StepGlyph type="summary" size={18} />
                </span>
                <h3>Leg Configuration Reference Table</h3>
              </div>
              <div className="order-reference-table-wrap">
                <div className="order-reference-table">
                  <div className="order-reference-head">
                    <span>Width</span>
                    <span>Depth</span>
                    <span>Legs</span>
                  </div>
                  {summaryReferenceRows.map(row => (
                    <div key={row.id} className={`order-reference-row${row.id === summaryReferenceMatch ? ' active' : ''}`}>
                      <span>{row.width}</span>
                      <span>{row.depth}</span>
                      <strong>{row.legs}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            <div className="order-step-heading">
              <div className="order-step-title"><StepIcon type="ruler" /><h2>Base Dimensions</h2></div>
              <p>Enter the width and depth of your shower base. The leg configuration will be automatically calculated.</p>
            </div>
            <div className="order-step-one-measurements">
              <NumericSliderField label="Front Width (mm)" value={state.dimensions.frontWidth} min={MM_MIN} max={MM_MAX} error={dimensionFieldErrors.frontWidth} onChange={value => updateDimension('frontWidth', value)} />
              <NumericSliderField label="Rear Width (mm)" value={state.dimensions.rearWidth} min={MM_MIN} max={MM_MAX} error={dimensionFieldErrors.rearWidth} onChange={value => updateDimension('rearWidth', value)} />
              <NumericSliderField label="Left Depth (mm)" value={state.dimensions.leftDepth} min={MM_MIN} max={MM_MAX} error={dimensionFieldErrors.leftDepth} onChange={value => updateDimension('leftDepth', value)} />
              <NumericSliderField label="Right Depth (mm)" value={state.dimensions.rightDepth} min={MM_MIN} max={MM_MAX} error={dimensionFieldErrors.rightDepth} onChange={value => updateDimension('rightDepth', value)} />
            </div>
            {hardErrors.combinedLimit && <div className="order-inline-alert danger">{hardErrors.combinedLimit}</div>}
            {hardErrors.customQuote && <div className="order-inline-alert danger">{hardErrors.customQuote}</div>}
            {irregularShape && (
              <p className="order-irregular-copy">
                Your dimensions suggest an out of square shower base. Please select the one corner that is exactly 90°. This 'square' corner allows us to align your shower base perfectly against your walls.
              </p>
            )}
            {irregularShape && (
              <>
                <VideoHelpCard
                  title="How to identify square corners"
                  description="Watch how to check if your shower corners are exactly 90° to ensure the base aligns perfectly with your walls."
                  thumbnail={orderHelpContent['square-corners'].thumbnail}
                  onClick={() => setActiveHelpKey('square-corners')}
                />
                <section className="order-square-corners-box">
                  <h3>Square Corner Indicators (SQ)</h3>
                  <span>Select which corner has a 90° angle:</span>
                  <div className="order-square-corner-grid">
                    {squareCornerSelectorOptions.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={state.squareCorners.includes(option.value) ? 'active' : ''}
                        onClick={() => toggleSquareCorner(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </section>
              </>
            )}
            <section className="order-divider-section">
              <div className="order-accordion-head">
                <div className="order-accordion-title">
                  <StepGlyph type="entry" className="order-section-title-icon" size={20} />
                  <h3>Entry Side</h3>
                  <SectionHintIcon
                    label="Entry side help"
                    text="Choose all sides that will remain open for entry. You can select multiple sides depending on your bathroom layout."
                  />
                </div>
                <SectionChevron />
              </div>
              <div className="order-section-head compact">
                <span>Select which sides you will enter the shower from.</span>
              </div>
              <ToggleButtonGroup options={sideOptions} value={state.entrySides} onToggle={toggleEntrySide} multi stretch />
            </section>
            <section className="order-divider-section">
              <div className="order-accordion-head">
                <div className="order-accordion-title">
                  <StepGlyph type="corner" className="order-section-title-icon" size={20} />
                  <h3>Rounded Corners</h3>
                  <SectionHintIcon
                    label="Rounded corners help"
                    text="If any part of your shower base is rounded, please provide the radius for those specific corners."
                  />
                </div>
                <SectionChevron />
              </div>
              <div className="order-section-head compact">
                <span>Does your shower base have rounded corners? Select below to enter the radius measurements for each corner.</span>
              </div>
              <ToggleButtonGroup
                options={['Yes', 'No']}
                value={state.roundedCornersEnabled ? 'Yes' : 'No'}
                onToggle={option => updateState(previous => ({ ...previous, roundedCornersEnabled: option === 'Yes' }))}
                stretch
              />
              {state.roundedCornersEnabled && (
                <>
                  <VideoHelpCard
                    title="How to measure rounded corners"
                    description="Watch how to measure the rounded corners of your shower base."
                    thumbnail={orderHelpContent['rounded-corners'].thumbnail}
                    onClick={() => setActiveHelpKey('rounded-corners')}
                  />
                  <div className="order-quick-row order-rounded-quick-row">
                    <div className="order-rounded-quick-head">
                      <span className="order-rounded-quick-title">Quick Apply</span>
                      <SectionHintIcon
                        label="Quick apply help"
                        text="Set the same radius for all corners."
                      />
                    </div>
                    <div className="order-rounded-quick-controls">
                      <InputGroup suffix="mm">
                        <input inputMode="decimal" value={state.roundedCorners.quickApply} onChange={event => updateRoundedCorner('quickApply', event.target.value)} />
                      </InputGroup>
                      <button type="button" className="order-pill-cta order-rounded-quick-cta" onClick={applyRoundedQuickValue}>Apply to All</button>
                    </div>
                    <p className="order-rounded-quick-copy">Set the same radius for all corners</p>
                  </div>
                  <div className="order-rounded-grid">
                    {roundedCornerFields.map(corner => {
                      const notRoundedKey = `${corner.key}NotRounded`
                      return (
                        <div key={corner.key} className="order-rounded-card">
                          <strong>{corner.label}</strong>
                          <div className="order-rounded-inputs">
                            <InputGroup label="A (mm)">
                              <input
                                inputMode="decimal"
                                value={state.roundedCorners[`${corner.key}A`]}
                                disabled={state.roundedCorners[notRoundedKey]}
                                onChange={event => updateRoundedCorner(`${corner.key}A`, event.target.value)}
                              />
                            </InputGroup>
                            <InputGroup label="B (mm)">
                              <input
                                inputMode="decimal"
                                value={state.roundedCorners[`${corner.key}B`]}
                                disabled={state.roundedCorners[notRoundedKey]}
                                onChange={event => updateRoundedCorner(`${corner.key}B`, event.target.value)}
                              />
                            </InputGroup>
                          </div>
                          <label className="order-inline-checkbox">
                            <input
                              type="checkbox"
                              checked={state.roundedCorners[notRoundedKey]}
                              onChange={event => updateState(previous => ({
                                ...previous,
                                roundedCorners: {
                                  ...previous.roundedCorners,
                                  [notRoundedKey]: event.target.checked,
                                  [`${corner.key}A`]: event.target.checked ? '' : previous.roundedCorners[`${corner.key}A`],
                                  [`${corner.key}B`]: event.target.checked ? '' : previous.roundedCorners[`${corner.key}B`],
                                },
                              }))}
                            />
                            <span>This corner is not rounded</span>
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    ),
    2: (
      <div className="order-step-body">
        <div className="order-step-heading">
          <div className="order-step-title"><StepIcon type="ruler" /><h2>Leg Setback Distance</h2></div>
          <p>Distance from the edge of the shower base to the edge of perimeter legs. This applies only to legs on the perimeter.</p>
        </div>
        <div className="order-step-content-shell order-step-content-shell-narrow">
          <div className="order-grid-two">
            {Object.entries(state.setbacks).map(([key, value]) => (
              <div key={key} className="order-setback-field">
                <InputGroup label={`${key.charAt(0).toUpperCase()}${key.slice(1)} Setback`} suffix="mm">
                  <input inputMode="decimal" value={value} onChange={event => updateSetback(key, event.target.value)} />
                </InputGroup>
              </div>
            ))}
            {Object.values(state.setbacks).some(value => toNumber(value) > 50) && (
              <div className="order-inline-alert warn">{SETBACK_WARNING_MESSAGE}</div>
            )}
          </div>
          <section className="order-divider-section">
            <div className="order-section-head compact">
              <h3><span className="order-optional-pill">Optional</span> Additional Information</h3>
            </div>
            <p className="order-optional-copy">Please share any additional details, special requirements, or questions you may have.</p>
            <textarea
              className="order-large-textarea"
              placeholder="E.g. My left middle leg is 75mm setback. All the remaining leg setbacks are 50mm."
              value={state.additionalInfo}
              onChange={event => updateState(previous => ({ ...previous, additionalInfo: event.target.value }))}
              rows={6}
            />
          </section>
        </div>
      </div>
    ),
    3: (
      <div className="order-step-body">
        <div className="order-step-heading">
          <div className="order-step-title"><StepIcon type="ruler" /><h2>Leg Heights</h2></div>
          <p>Enter the total height for each leg support (Leg height + 15mm shower base thickness). This accounts for the sloped shower floor.</p>
        </div>
        <div className="order-step-content-shell order-step-content-shell-narrow">
          <div className="order-quick-apply-card accent">
            <InputGroup
              label={(
                <span className="order-quick-apply-label">
                  <span>Quick Apply</span>
                  <QuickApplyHelp text="Apply a uniform height across all legs. Perfect for builders who prefer to trim on-site for a perfectly level fit." />
                </span>
              )}
              hint="Set the same height for all legs to start"
            >
              <input
                type="text"
                inputMode="decimal"
                value={state.quickHeight}
                onChange={event => updateState(previous => ({ ...previous, quickHeight: event.target.value }))}
                onFocus={() => {
                  setQuickApplyFocused(true)
                  updateState(previous => ({ ...previous, focusedLeg: '' }))
                }}
                onBlur={() => setQuickApplyFocused(false)}
              />
            </InputGroup>
            <button type="button" className="order-primary-button compact" onClick={applyQuickHeights}>Apply to All</button>
          </div>
          <div className="order-leg-grid-meta">Leg Grid: {pricingTier.cols} × {pricingTier.rows} ({pricingTier.legs} total)</div>
          {hardErrors.legHeights && legHeightsCompletion.allEntered && (
            <div className="order-inline-alert danger" style={{ marginBottom: '1rem' }}>{hardErrors.legHeights}</div>
          )}

          <div className="order-leg-grid-shell">
            {isMobileLayout ? (
              <div className="order-leg-sections-mobile">
                {legSections.map((group, index) => (
                  <div key={group.letter} className="order-leg-section-panel">
                    <h4 className="order-leg-section-title">Section {index + 1}</h4>
                    <div className="order-leg-section-rows">
                      {group.positions.map(renderLegCard)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={`order-leg-grid order-leg-grid-${pricingTier.cols}${pricingTier.cols === 7 && averageWidth > averageDepth ? ' is-horizontal-wide' : ''}`}
                style={{ '--leg-grid-cols': pricingTier.cols }}
              >
                {legPositions.map(renderLegCard)}
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    4: (
      <div className="order-step-body">
        <div className="order-step-heading">
          <div className="order-step-title"><StepIcon type="droplet" /><h2>Drainage Information</h2></div>
          <p>Enter your drain size and position measurements so we can place the outlet correctly within the shower base.</p>
        </div>
        <div className="order-step-content-shell order-step-content-shell-narrow">
          <div className="order-grid-two order-drain-top">
            <InputGroup label="Width (mm)"><input inputMode="decimal" value={state.drain.width} onChange={event => updateState(previous => ({ ...previous, drain: { ...previous.drain, width: event.target.value } }))} /></InputGroup>
            <InputGroup label="Depth (mm)"><input inputMode="decimal" value={state.drain.depth} onChange={event => updateState(previous => ({ ...previous, drain: { ...previous.drain, depth: event.target.value } }))} /></InputGroup>
          </div>
          {drainOverlap && (
            <div className="order-drain-notice">
              <span className="order-drain-notice-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" />
                  <path d="M4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12Z" stroke="currentColor" strokeWidth="2" />
                </svg>
              </span>
              <span className="order-drain-notice-copy">
                <strong>Drain Positioning Notice</strong>
                <span>Your drain is positioned directly under or very close to a leg support. Don&apos;t worry! Our team will automatically add an additional leg to ensure there is proper support on either side of the drain.</span>
              </span>
            </div>
          )}
          <div className="order-drain-sliders">
            <InlineSliderField label="Drain distance from Front to Center of Drain (mm)" value={state.drain.fromFront} min={0} max={1800} onChange={value => updateState(previous => ({ ...previous, drain: { ...previous.drain, fromFront: value } }))} />
            <InlineSliderField label="Drain distance from Left to Center of Drain (mm)" value={state.drain.fromLeft} min={0} max={1800} onChange={value => updateState(previous => ({ ...previous, drain: { ...previous.drain, fromLeft: value } }))} />
          </div>
          <section className="order-divider-section">
            <div className="order-section-head compact">
              <h3><span className="order-optional-pill">Optional</span> Additional Information</h3>
            </div>
            <p className="order-optional-copy">Please share any additional details, special requirements, or questions you may have.</p>
            <textarea
              className="order-large-textarea order-drain-textarea"
              placeholder="E.g. Existing plumbing location, preferred drain placement, or any site constraints we should be aware of."
              value={state.drain.note}
              onChange={event => updateState(previous => ({ ...previous, drain: { ...previous.drain, note: event.target.value } }))}
              rows={6}
            />
          </section>
        </div>
      </div>
    ),
    5: (
      <div className="order-step-body">
        <div className="order-step-heading">
          <div className="order-step-title"><StepIcon type="cut" /><h2>Cut-out Information</h2></div>
          <p>Any other cut-outs required? (e.g., for shower screen, pipes, fixtures)</p>
        </div>
        <ToggleButtonGroup
          options={['Yes', 'No']}
          value={state.cutoutsEnabled ? 'Yes' : 'No'}
          onToggle={option => updateState(previous => ({ ...previous, cutoutsEnabled: option === 'Yes' }))}
          stretch
        />
        {state.cutoutsEnabled && (
          <>
            {state.cutouts.map((cutout, index) => {
              const hasMultipleCutouts = state.cutouts.length > 1
              const isCollapsed = state.activeCutoutId !== cutout.id && state.cutouts.length > 1
              const isRevisitCutout = cutout.id !== latestCutoutId

              return (
                <div
                  key={cutout.id}
                  className={`order-cutout-card${isCollapsed ? ' is-collapsed' : ''}${isRevisitCutout && !isCollapsed ? ' is-revisit-edit' : ''}`}
                >
                  <div className="order-cutout-card-head">
                    <h3>Cut-out {index + 1}</h3>
                    {hasMultipleCutouts && (
                      <div className="order-cutout-card-actions">
                        {state.activeCutoutId === cutout.id ? (
                          <button type="button" className="order-cutout-link with-icon" onClick={() => updateState(previous => ({ ...previous, activeCutoutId: null }))}>
                            <CutoutCollapseIcon />
                            <span>Collapse</span>
                          </button>
                        ) : (
                          <button type="button" className="order-cutout-link with-icon" onClick={() => updateState(previous => ({ ...previous, activeCutoutId: cutout.id }))}>
                            <CutoutEditIcon />
                            <span>Edit</span>
                          </button>
                        )}
                        <button type="button" className="order-cutout-link with-icon danger" onClick={() => removeCutout(cutout.id)}>
                          <CutoutRemoveIcon />
                          <span>Remove</span>
                        </button>
                      </div>
                    )}
                  </div>
                  {isCollapsed ? null : (
                    <>
                      <div className="order-cutout-section-title">Cut-out type</div>
                      <div className={`order-cutout-type-grid${isRevisitCutout ? ' compact' : ''}`}>
                        {cutoutTypes.map(type => (
                          <button
                            key={type.id}
                            type="button"
                            className={`order-cutout-type${isRevisitCutout ? ' compact' : ''}${cutout.type === type.id ? ' active' : ''}`}
                            onClick={() => switchCutoutType(cutout.id, type.id)}
                          >
                            {isRevisitCutout ? (
                              <>
                                <span className={`order-cutout-type-icon-wrap type-${type.id}`} aria-hidden="true">
                                  <CutoutTypeIcon type={type.id} active={cutout.type === type.id} />
                                </span>
                                <strong>{type.title}</strong>
                              </>
                            ) : (
                              <>
                                {type.mediaType === 'dark' ? (
                                  <span className="order-cutout-type-media is-dark" aria-hidden="true">
                                    <span className="order-cutout-type-dot" />
                                  </span>
                                ) : (
                                  <span className={`order-cutout-type-media${type.mediaType === 'cover' ? ' is-cover' : ''}`}>
                                    <img src={type.image} alt="" />
                                  </span>
                                )}
                                <strong>{type.title}</strong>
                                <span>{type.description}</span>
                              </>
                            )}
                          </button>
                        ))}
                      </div>
                      {cutout.type === 'screen-door' && (
                        <>
                          <div className="order-cutout-section-title">Cut-out side</div>
                          <OptionButtonGrid
                            options={[
                              { label: 'Front Side', value: 'Front' },
                              { label: 'Left Side', value: 'Left' },
                              { label: 'Right Side', value: 'Right' },
                            ]}
                            value={cutout.side}
                            onChange={value => updateCutout(cutout.id, 'side', value)}
                            columns={3}
                          />
                          <div className="order-grid-two order-cutout-dimensions-grid">
                            <InputGroup label={<CutoutFieldLabel title="Width of cut-out (mm)" helpLabel="Shower screen door width help" helpText={cutoutFieldHelpText['screen-door'].width} />} suffix="mm"><input inputMode="decimal" value={cutout.width} onChange={event => updateCutout(cutout.id, 'width', event.target.value)} /></InputGroup>
                            <InputGroup label={<CutoutFieldLabel title="Depth of cut-out (mm)" helpLabel="Shower screen door depth help" helpText={cutoutFieldHelpText['screen-door'].depth} />} suffix="mm"><input inputMode="decimal" value={cutout.depth} onChange={event => updateCutout(cutout.id, 'depth', event.target.value)} /></InputGroup>
                          </div>
                          <div className="order-grid-two order-screen-door-distance-grid">
                            <CheckboxSliderField
                              label={getScreenDoorDistanceLabels(cutout).primary}
                              checked={cutout.leftEdgeEnabled}
                              onToggle={checked => updateState(previous => ({
                                ...previous,
                                cutouts: previous.cutouts.map(item => (
                                  item.id === cutout.id
                                    ? {
                                      ...item,
                                      leftEdgeEnabled: checked,
                                      rightEdgeEnabled: checked ? false : item.rightEdgeEnabled,
                                    }
                                    : item
                                )),
                              }))}
                              value={cutout.leftEdgeDistance}
                              onChange={value => updateCutout(cutout.id, 'leftEdgeDistance', value)}
                            />
                            <CheckboxSliderField
                              label={getScreenDoorDistanceLabels(cutout).secondary}
                              checked={cutout.rightEdgeEnabled}
                              onToggle={checked => updateState(previous => ({
                                ...previous,
                                cutouts: previous.cutouts.map(item => (
                                  item.id === cutout.id
                                    ? {
                                      ...item,
                                      rightEdgeEnabled: checked,
                                      leftEdgeEnabled: checked ? false : item.leftEdgeEnabled,
                                    }
                                    : item
                                )),
                              }))}
                              value={cutout.rightEdgeDistance}
                              onChange={value => updateCutout(cutout.id, 'rightEdgeDistance', value)}
                            />
                          </div>
                        </>
                      )}
                      {cutout.type === 'screen' && (
                        <>
                          <div className="order-cutout-section-title">Cut-out side</div>
                          <OptionButtonGrid
                            options={[
                              { label: 'Front Side', value: 'Front' },
                              { label: 'Rear Side', value: 'Rear' },
                              { label: 'Left Side', value: 'Left' },
                              { label: 'Right Side', value: 'Right' },
                            ]}
                            value={cutout.side}
                            onChange={value => updateCutout(cutout.id, 'side', value)}
                          />
                          <div className="order-cutout-section-title">Cut-out position</div>
                          <div className="order-inline-check-grid">
                            {getCutoutPositionOptions(cutout).map(option => (
                              <label key={option.value} className="order-inline-check">
                                <input
                                  type="checkbox"
                                  checked={cutout.hand === option.value}
                                  onChange={() => updateCutout(cutout.id, 'hand', option.value)}
                                />
                                <span>{option.label}</span>
                              </label>
                            ))}
                          </div>
                          <div className="order-grid-two order-cutout-dimensions-grid">
                            <InputGroup label={<CutoutFieldLabel title="Width of cut-out (mm)" helpLabel="Shower screen width help" helpText={cutoutFieldHelpText.screen.width} />} suffix="mm"><input inputMode="decimal" value={cutout.width} onChange={event => updateCutout(cutout.id, 'width', event.target.value)} /></InputGroup>
                            <InputGroup label={<CutoutFieldLabel title="Depth of cut-out (mm)" helpLabel="Shower screen depth help" helpText={cutoutFieldHelpText.screen.depth} />} suffix="mm"><input inputMode="decimal" value={cutout.depth} onChange={event => updateCutout(cutout.id, 'depth', event.target.value)} /></InputGroup>
                          </div>
                        </>
                      )}
                      {cutout.type === 'pipes' && (
                        <>
                          <div className="order-cutout-section-title">Cut-out side</div>
                          <OptionButtonGrid
                            options={[
                              { label: 'Front Side', value: 'Front' },
                              { label: 'Rear Side', value: 'Rear' },
                              { label: 'Left Side', value: 'Left' },
                              { label: 'Right Side', value: 'Right' },
                            ]}
                            value={cutout.side}
                            onChange={value => updateCutout(cutout.id, 'side', value)}
                          />
                          <div className="order-cutout-slider-row">
                            <InlineSliderField
                              label={getPipeFixtureDistanceLabel(cutout)}
                              value={cutout.side === 'Left' || cutout.side === 'Right' ? cutout.fixtureOffsetFromFront : cutout.fixtureOffsetFromLeft}
                              min={0}
                              max={1800}
                              onChange={value => updateCutout(cutout.id, cutout.side === 'Left' || cutout.side === 'Right' ? 'fixtureOffsetFromFront' : 'fixtureOffsetFromLeft', value)}
                            />
                          </div>
                          <div className="order-grid-two order-cutout-dimensions-grid">
                            <InputGroup label={<CutoutFieldLabel title="Width of cut-out (mm)" helpLabel="Pipes and fixtures width help" helpText={cutoutFieldHelpText.pipes.width} />} suffix="mm"><input inputMode="decimal" value={cutout.width} onChange={event => updateCutout(cutout.id, 'width', event.target.value)} /></InputGroup>
                            <InputGroup label={<CutoutFieldLabel title="Depth of cut-out (mm)" helpLabel="Pipes and fixtures depth help" helpText={cutoutFieldHelpText.pipes.depth} />} suffix="mm"><input inputMode="decimal" value={cutout.depth} onChange={event => updateCutout(cutout.id, 'depth', event.target.value)} /></InputGroup>
                          </div>
                        </>
                      )}
                      {cutout.type === 'corner-channel' && (
                        <>
                          <div className="order-cutout-section-title">Cut-out corner</div>
                          <OptionButtonGrid
                            options={[
                              { label: 'Front-Left', value: 'Front Left' },
                              { label: 'Front-Right', value: 'Front Right' },
                              { label: 'Top-Left', value: 'Rear Left' },
                              { label: 'Top-Right', value: 'Rear Right' },
                            ]}
                            value={cutout.corner}
                            onChange={value => updateCutout(cutout.id, 'corner', value)}
                          />
                          <div className="order-grid-two order-cutout-dimensions-grid">
                            <InputGroup label={<CutoutFieldLabel title="Width of cut-out (mm)" helpLabel="Corner channel width help" helpText={cutoutFieldHelpText['corner-channel'].width} />} suffix="mm"><input inputMode="decimal" value={cutout.width} onChange={event => updateCutout(cutout.id, 'width', event.target.value)} /></InputGroup>
                            <InputGroup label={<CutoutFieldLabel title="Depth of cut-out (mm)" helpLabel="Corner channel depth help" helpText={cutoutFieldHelpText['corner-channel'].depth} />} suffix="mm"><input inputMode="decimal" value={cutout.depth} onChange={event => updateCutout(cutout.id, 'depth', event.target.value)} /></InputGroup>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )
            })}
            <button type="button" className="order-primary-button full order-add-cutout-button" onClick={addCutout}>+ Add Cut-out</button>
            <section className="order-additional-block order-cutout-additional-block">
              <div className="order-step-title compact"><StepIcon type="summary" /><h3>Additional Information</h3></div>
              <div className="order-upload-section">
                <h3>Upload Photos</h3>
                <p className="order-optional-copy">Upload photos of your current shower base or bathroom space to help us better understand your requirements.</p>
                <UploadDropzone onFiles={handleFiles} files={state.uploads} onRemoveFile={removeUpload} />
              </div>
              <div className="order-upload-section">
                <h3><span className="order-optional-pill">Optional</span> Additional Information</h3>
                <p className="order-optional-copy">Please share any additional details, special requirements, or questions you may have.</p>
                <textarea
                  className="order-large-textarea"
                  placeholder="E.g. Installation timeline, specific measurements, access restrictions, or any special requirements..."
                  value={state.additionalInfo}
                  onChange={event => updateState(previous => ({ ...previous, additionalInfo: event.target.value }))}
                  rows={6}
                />
              </div>
            </section>
          </>
        )}
      </div>
    ),
    6: (
      <div className="order-step-body">
        <div className="order-step-heading">
          <div className="order-step-title"><StepIcon type="cut" /><h2>Fabrication Details</h2></div>
          <p>Specify any cut-outs required for your shower base installation.</p>
        </div>
        <section className="order-fabrication-weight">
          <div className="order-fabrication-weight-row">
            <span>Estimated Weight</span>
            <strong>{Math.round(Number(estimatedWeight))} kg</strong>
          </div>
          {(averageWidth > 1200 || averageDepth > 1200) && <div className="order-fabrication-warning">2-piece base recommended for easier installation.</div>}
        </section>
        <section className="order-split-card">
          <label className="order-checkbox-row compact">
            <input
              type="checkbox"
              checked={state.fabrication.splitBase}
              onChange={event => updateState(previous => ({
                ...previous,
                fabrication: {
                  ...previous.fabrication,
                  splitBase: event.target.checked,
                  installationOrder: event.target.checked ? previous.fabrication.installationOrder : '',
                  fingerHolesSingle: event.target.checked ? previous.fabrication.fingerHolesSingle : previous.fabrication.fingerHolesSingle,
                  fingerHolesBase1: event.target.checked ? previous.fabrication.fingerHolesBase1 : [],
                  fingerHolesBase2: event.target.checked ? previous.fabrication.fingerHolesBase2 : [],
                },
              }))}
            />
            <span>Create base in two pieces</span>
          </label>
          <p className="order-split-card-copy">Check this if you need the shower base to be manufactured in two separate pieces for easier installation or transport.</p>
        </section>
        {state.fabrication.splitBase && (
          <section className="order-install-card">
            <div className="order-install-card-title">
              <span className="order-install-card-icon" aria-hidden="true">!</span>
              <h3>Which piece will be installed first?</h3>
            </div>
            <p>The piece installed first goes to the far end of the shower floor and does not require finger holes. The second piece needs finger holes for removal.</p>
            <ToggleButtonGroup
              options={['Base 1 First', 'Base 2 First']}
              value={state.fabrication.installationOrder}
              onToggle={option => updateState(previous => ({ ...previous, fabrication: { ...previous.fabrication, installationOrder: option } }))}
              stretch
              className="order-install-toggle"
            />
          </section>
        )}
        <div className="order-step-title compact order-finger-holes-title"><StepIcon type="fingerHoles" /><h3>Finger Holes (required)</h3></div>
        {!state.fabrication.splitBase ? (
          <ToggleButtonGroup
            options={['Front Side', 'Rear Side', 'Left Side', 'Right Side']}
            value={state.fabrication.fingerHolesSingle}
            onToggle={option => updateState(previous => ({
              ...previous,
              fabrication: {
                ...previous.fabrication,
                fingerHolesSingle: previous.fabrication.fingerHolesSingle.includes(option)
                  ? previous.fabrication.fingerHolesSingle.filter(item => item !== option)
                  : [...previous.fabrication.fingerHolesSingle, option],
              },
            }))}
            multi
            stretch
            className="order-finger-toggle"
          />
        ) : (
          <div className="order-finger-grid">
            {[
              {
                key: 'fingerHolesBase1',
                title: 'Base 1 - Finger Holes',
                description: 'Select finger hole locations for the first piece.',
                active: state.fabrication.installationOrder === 'Base 2 First',
                installedFirst: state.fabrication.installationOrder === 'Base 1 First',
              },
              {
                key: 'fingerHolesBase2',
                title: 'Base 2 - Finger Holes',
                description: 'Select finger hole locations for the second piece.',
                active: state.fabrication.installationOrder === 'Base 1 First',
                installedFirst: state.fabrication.installationOrder === 'Base 2 First',
              },
            ].map(section => {
              const disabled = !section.active
              return (
                <div
                  key={section.key}
                  className={`order-finger-card${section.key === 'fingerHolesBase2' ? ' order-finger-card-base-2' : ''}${section.active ? ' is-active' : ''}${disabled ? ' is-disabled' : ''}${section.installedFirst ? ' is-installed-first' : ''}`}
                >
                  <strong className="order-finger-card-title">{section.title}</strong>
                  {section.installedFirst && <span className="order-finger-card-tag">Installed 1st - No holes needed</span>}
                  <p>{section.description}</p>
                  <ToggleButtonGroup
                    options={['Front Side', 'Rear Side', 'Left Side', 'Right Side']}
                    value={state.fabrication[section.key]}
                    onToggle={option => {
                      if (disabled) return
                      updateState(previous => ({
                        ...previous,
                        fabrication: {
                          ...previous.fabrication,
                          [section.key]: previous.fabrication[section.key].includes(option)
                            ? previous.fabrication[section.key].filter(item => item !== option)
                            : [...previous.fabrication[section.key], option],
                        },
                      }))
                    }}
                    multi
                    stretch
                    disabled={disabled}
                    className="order-finger-toggle order-finger-toggle-split"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    ),
    7: (
      <div className="order-step-body order-additional-step">
        <div className="order-step-heading">
          <div className="order-step-title"><StepIcon type="upload" /><h2>Additional Information</h2></div>
        </div>
        <div className="order-upload-section order-upload-section-main">
          <h3>Upload Photos</h3>
          <p className="order-optional-copy">Upload photos of your current shower base or bathroom space to help us better understand your requirements.</p>
          <UploadDropzone onFiles={handleFiles} files={state.uploads} onRemoveFile={removeUpload} />
        </div>
        <div className="order-upload-section order-upload-section-additional">
          <h3><span className="order-optional-pill">Optional</span> Additional Information</h3>
          <p className="order-optional-copy">Please share any additional details, special requirements, or questions you may have.</p>
          <textarea
            className="order-large-textarea order-additional-textarea"
            placeholder="E.g. Installation timeline, specific measurements, access restrictions, or any special requirements..."
            value={state.additionalInfo}
            onChange={event => updateState(previous => ({ ...previous, additionalInfo: event.target.value }))}
            rows={6}
          />
        </div>
      </div>
    ),
    8: (
      <div className="order-step-body order-contact-step">
        <div className="order-step-heading">
          <div className="order-step-title order-step-title-plain"><h2>Contact Details</h2></div>
          <p>We&apos;ll send your PDF quote to this email</p>
        </div>
        <div className="order-grid-two order-contact-grid order-contact-forms figma-contact-forms">
          <section className="order-contact-card required">
            <h3><span className="order-optional-pill required">Required</span> Essential Information</h3>
            <InputGroup label="Contact Name *" error={showContactValidation ? hardErrors.fullName : ''}><input placeholder="John Smith" value={state.contact.fullName} onChange={event => updateState(previous => ({ ...previous, contact: { ...previous.contact, fullName: event.target.value } }))} /></InputGroup>
            <InputGroup label="Email address *" hint="Your PDF quote will be sent here" error={showContactValidation ? hardErrors.email : ''}><input placeholder="youremail@example.com" value={state.contact.email} onChange={event => updateState(previous => ({ ...previous, contact: { ...previous.contact, email: event.target.value } }))} /></InputGroup>
            <InputGroup label="Phone number *" hint="In case we need to clarify details" error={showContactValidation ? hardErrors.phone : ''}><input value={state.contact.phone} onChange={event => updateState(previous => ({ ...previous, contact: { ...previous.contact, phone: event.target.value } }))} /></InputGroup>
          </section>
          <section className="order-contact-card">
            <h3><span className="order-optional-pill">Optional</span> Optional Information</h3>
            <InputGroup label="Company"><input value={state.contact.company} onChange={event => updateState(previous => ({ ...previous, contact: { ...previous.contact, company: event.target.value } }))} /></InputGroup>
            <InputGroup label="Order Reference"><input value={state.contact.reference} onChange={event => updateState(previous => ({ ...previous, contact: { ...previous.contact, reference: event.target.value } }))} /></InputGroup>
          </section>
        </div>
        <section className="order-delivery-section figma-delivery-section">
          <div className="order-step-title compact order-step-title-plain"><h3>Delivery or Pickup?</h3></div>
          <p className="order-optional-copy">Delivery costs will be added as part of the quote.</p>
          <div className="order-fulfilment-cards">
            <button type="button" className={`order-fulfilment-card${state.fulfilment.method === 'Delivery' ? ' active' : ''}`} onClick={() => updateState(previous => ({ ...previous, fulfilment: { ...previous.fulfilment, method: 'Delivery' } }))}>
              <img src={deliveryCoverImage} alt="" />
              <div className="order-fulfilment-copy">
                <div className="order-fulfilment-title-row">
                  <span className="order-fulfilment-icon"><img src={deliveryImage} alt="" /></span>
                  <strong>Delivery to Site</strong>
                  <span className="order-fee-badge">{formatMoney(DELIVERY_FEE)}</span>
                </div>
                <p>We&apos;ll deliver directly to your address</p>
                <small>Flat rate delivery fee<br />Estimated 2-5 business days (varies by location)</small>
              </div>
            </button>
            <button type="button" className={`order-fulfilment-card${state.fulfilment.method === 'Pickup' ? ' active' : ''}`} onClick={() => updateState(previous => ({ ...previous, fulfilment: { ...previous.fulfilment, method: 'Pickup' } }))}>
              <img src={pickupCoverImage} alt="" />
              <div className="order-fulfilment-copy">
                <div className="order-fulfilment-title-row">
                  <span className="order-fulfilment-icon"><img src={pickupImage} alt="" /></span>
                  <strong>Pickup / Click &amp; Collect</strong>
                  <span className="order-fee-badge free">FREE</span>
                </div>
                <p>Collect from our warehouse when your order is ready</p>
                <small>49a Quinn St Preston VIC 3072<br />Mon-Fri 8:00 AM - 4:00 PM</small>
              </div>
            </button>
          </div>
          {state.fulfilment.method === 'Delivery' && (
            <div className="order-delivery-details">
              <h3 className="order-delivery-details-title">Delivery Details</h3>
              <InputGroup label="Delivery Address *" hint="Enter your full street address including suburb and postcode" error={showContactValidation ? hardErrors.address : ''}>
                <div ref={deliveryAutocompleteContainerRef} className="delivery-autocomplete-widget">
                  <input
                    role="combobox"
                    placeholder="Start typing your address..."
                    value={state.fulfilment.address}
                    onChange={event => updateState(previous => ({ ...previous, fulfilment: { ...previous.fulfilment, address: event.target.value } }))}
                    onFocus={() => {
                      if (addressPredictions.length) setAddressDropdownOpen(true)
                    }}
                    onKeyDown={handleDeliveryAddressKeyDown}
                    aria-autocomplete="list"
                    aria-expanded={addressDropdownOpen}
                    aria-controls="delivery-address-suggestions"
                    autoComplete="street-address"
                  />
                  {addressDropdownOpen && (
                    <div className="delivery-autocomplete-dropdown" role="listbox" id="delivery-address-suggestions">
                      {addressLoading ? (
                        <div className="delivery-autocomplete-status">Searching addresses...</div>
                      ) : addressPredictions.length ? (
                        addressPredictions.map((prediction, index) => (
                          <button
                            key={prediction.place_id}
                            type="button"
                            role="option"
                            aria-selected={index === addressActiveIndex}
                            className={`delivery-autocomplete-item${index === addressActiveIndex ? ' active' : ''}`}
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => selectDeliveryPrediction(prediction)}
                          >
                            {prediction.description}
                          </button>
                        ))
                      ) : addressNoResults ? (
                        <div className="delivery-autocomplete-status">No matching addresses found.</div>
                      ) : null}
                    </div>
                  )}
                </div>
              </InputGroup>
              {addressLookupUnavailable && <p className="order-field-hint">Google address lookup is unavailable right now. You can still enter the address manually.</p>}
              <div className="order-grid-two">
                <InputGroup label="Suburb *" error={showContactValidation ? hardErrors.suburb : ''}>
                  <input value={state.fulfilment.suburb} onChange={event => updateState(previous => ({ ...previous, fulfilment: { ...previous.fulfilment, suburb: event.target.value } }))} />
                </InputGroup>
                <InputGroup label="State *" error={showContactValidation ? hardErrors.state : ''}>
                  <input value={state.fulfilment.state} onChange={event => updateState(previous => ({ ...previous, fulfilment: { ...previous.fulfilment, state: event.target.value } }))} />
                </InputGroup>
              </div>
              <div className="order-grid-two">
                <InputGroup label="Postcode *" error={showContactValidation ? hardErrors.postcode : ''}>
                  <input value={state.fulfilment.postcode} onChange={event => updateState(previous => ({ ...previous, fulfilment: { ...previous.fulfilment, postcode: event.target.value } }))} />
                </InputGroup>
              </div>
              <div className="order-delivery-authority">
                <span className="order-input-label">Authority to Leave? *</span>
                <ToggleButtonGroup
                  options={['Yes', 'No']}
                  value={state.fulfilment.authorityToLeave}
                  onToggle={option => updateState(previous => ({ ...previous, fulfilment: { ...previous.fulfilment, authorityToLeave: option } }))}
                  stretch
                />
                <small>Can we leave the delivery if you&apos;re not home?</small>
                {showContactValidation && hardErrors.authorityToLeave && <em className="order-inline-error">{hardErrors.authorityToLeave}</em>}
              </div>
              <InputGroup label="Delivery Notes (Optional)">
                <input placeholder="e.g. Call on arrival, Gate code: 1234, Deliver to rear of property" value={state.fulfilment.deliveryNote} onChange={event => updateState(previous => ({ ...previous, fulfilment: { ...previous.fulfilment, deliveryNote: event.target.value } }))} />
              </InputGroup>
            </div>
          )}
          {state.fulfilment.method === 'Pickup' && (
            <div className="order-delivery-details">
              <h3 className="order-delivery-details-title">Pickup Address</h3>
              <p className="order-optional-copy">49a Quinn Street, Preston VIC 3072. Pickup is available Monday to Friday, 8:00 AM to 4:00 PM.</p>
            </div>
          )}
        </section>
      </div>
    ),
  }

  if (state.mode === 'landing') {
    return (
      <div className="order-route-shell">
        <div className="order-intro">
          <section className="order-intro-block">
            <h1>Choose Your Base Shape</h1>
            <p>Select a compatible base configuration to get started with your custom shower design.</p>
            <article className="order-shape-card">
              <div className="order-shape-image-wrap">
                <img src={rectangularBaseImage} alt="Rectangular shower base" />
                <span className="order-shape-badge">Available</span>
              </div>
              <div className="order-shape-copy">
                <h2>Rectangular & Square</h2>
                <p>Standard rectangular and square shower bases in custom dimensions. Available with sharp or rounded corners to match your design requirements.</p>
                <div className="order-shape-tags">
                  {landingShapeTags.map(tag => (
                    <span key={tag}>
                      <i aria-hidden="true" />
                      {tag}
                    </span>
                  ))}
                </div>
                <button type="button" className="order-primary-button order-shape-cta" onClick={beginConfiguration}>
                  <span>Configure Base</span>
                  <ButtonChevron />
                </button>
              </div>
            </article>
          </section>
          <section className="order-intro-guidance">
            <h2>Get Expert Guidance</h2>
            <p>For complex or specialty shapes, our sales team provides technical support to help you plan your on-site cuts and installation.</p>
            <div className="order-intro-grid">
              <article className="order-triangle-card">
                <img src={triangularBaseImage} alt="Triangular shower base" />
                <strong>Triangular</strong>
                <span>Corner or triangular shower configurations</span>
              </article>
              <article className="order-guidance-card">
                <h3>The Perfect Fit for Any Space</h3>
                <p>Don't let a unique layout slow you down. Our shower bases are fully trimmable on-site, allowing you to create custom triangular or specialty shapes right where you need them.</p>
                <a href="mailto:sales@equipsy.com.au" className="order-primary-button secondary">
                  <span>Contact Sales Team</span>
                  <ButtonChevron />
                </a>
              </article>
            </div>
          </section>
        </div>
      </div>
    )
  }

  if (state.mode === 'review') {
    return (
      <div className="order-route-shell is-review">
        <div className="order-mobile-preview-fixed">
          <OrderPreview
            dimensions={state.dimensions}
            squareCorners={state.squareCorners}
            entrySides={state.entrySides}
            drain={state.drain}
            cutouts={state.cutoutsEnabled ? state.cutouts : []}
            setbacks={state.setbacks}
            config={pricingTier}
            fabrication={state.fabrication}
            focusedLeg={state.focusedLeg}
            legHeights={state.legHeights}
            currentStep={state.currentStep}
            stepOneSummaryOpen={state.stepOneSummaryOpen}
            quickApplyFocused={quickApplyFocused}
            onOpenHelp={setActiveHelpKey}
          />
        </div>
        <div className="order-configurator-layout review-layout">
          <div className="order-main-column">
            <div className="order-review-card figma-summary">
              <header className="order-review-header figma-summary-head">
                <h1>Order Summary</h1>
              </header>
              <div className="order-review-product-card">
                <h2>Custom Shower Base Insert</h2>

                <div className="order-review-block figma-block">
                  <ReviewSectionTitle title="Dimensions" onEdit={() => beginReviewEdit('dimensions')} />
                  <div className="order-review-inline-grid">
                    <ReviewInlinePair label="Front Width:" value={`${state.dimensions.frontWidth}mm`} />
                    <ReviewInlinePair label="Rear Width:" value={`${state.dimensions.rearWidth}mm`} />
                    <ReviewInlinePair label="Left Depth:" value={`${state.dimensions.leftDepth}mm`} />
                    <ReviewInlinePair label="Right Depth:" value={`${state.dimensions.rightDepth}mm`} />
                  </div>
                  <div className="order-review-inline-grid three">
                    <ReviewInlinePair label="Square Corner" value={state.squareCorners.join(', ') || 'None'} />
                    <ReviewInlinePair label="Entry Side" value={state.entrySides.join(', ') || 'None'} />
                    <ReviewInlinePair label="Leg Configuration" value={`${pricingTier.cols} × ${pricingTier.rows} (${pricingTier.legs} legs)`} />
                  </div>
                </div>

                <div className="order-review-block figma-block">
                  <ReviewSectionTitle title="Leg Setback Distance" onEdit={() => beginReviewEdit('setbacks')} />
                  <div className="order-review-inline-grid">
                    <ReviewInlinePair label="Front:" value={`${state.setbacks.front}mm`} />
                    <ReviewInlinePair label="Rear:" value={`${state.setbacks.rear}mm`} />
                    <ReviewInlinePair label="Right:" value={`${state.setbacks.right}mm`} />
                    <ReviewInlinePair label="Left:" value={`${state.setbacks.left}mm`} />
                  </div>
                </div>

                <div className="order-review-block figma-block">
                  <ReviewSectionTitle title="Leg Heights (Total Height)" onEdit={() => beginReviewEdit('legHeights')} />
                  <p className="order-review-copy">
                    Min entered height: {Math.min(...Object.values(state.legHeights).map(value => toNumber(value) || 999)).toString().replace('999', '0')}mm | Max entered height: {Math.max(...Object.values(state.legHeights).map(value => toNumber(value)))}mm
                  </p>
                  <button type="button" className="order-review-link" onClick={() => updateState(previous => ({ ...previous, reviewLegHeightsExpanded: !previous.reviewLegHeightsExpanded }))}>
                    {state.reviewLegHeightsExpanded ? 'Hide leg heights' : 'View all leg heights'}
                  </button>
                  {state.reviewLegHeightsExpanded && (
                    <div className="order-review-grid compact">
                      {Object.entries(state.legHeights).map(([key, value]) => <ReviewMetric key={key} label={key} value={`${value || 0}mm`} />)}
                    </div>
                  )}
                </div>

                <div className="order-review-block figma-block">
                  <ReviewSectionTitle title="Drainage" onEdit={() => beginReviewEdit('drainage')} />
                  <div className="order-review-inline-stack">
                    <ReviewInlinePair label={toNumber(state.drain.depth) > 0 && toNumber(state.drain.depth) !== toNumber(state.drain.width) ? 'Drain Size:' : 'Drain Diameter:'} value={toNumber(state.drain.depth) > 0 && toNumber(state.drain.depth) !== toNumber(state.drain.width) ? `${state.drain.width}mm × ${state.drain.depth}mm` : `${state.drain.width}mm`} />
                    <ReviewInlinePair label="Distance from Front:" value={`${state.drain.fromFront}mm`} />
                    <ReviewInlinePair label="Distance from Right:" value={`${drainDistanceFromRight}mm`} />
                  </div>
                </div>

                <div className="order-review-block figma-block">
                  <ReviewSectionTitle title="Cut-outs" onEdit={() => beginReviewEdit('cutouts')} />
                  <div className="order-review-variation">
                    <span>Cut-outs required?</span>
                    <strong>{activeCutouts.length ? 'Yes' : 'No'}</strong>
                  </div>
                  {!activeCutouts.length && (
                    <div className="order-review-cutout-summary">
                      <ReviewInlinePair label="Shower Screen Cut-out required?" value="No" />
                      <ReviewInlinePair label="Additional Cut-outs required?" value="No" />
                    </div>
                  )}
                  {activeCutouts.map((cutout, index) => (
                    <div key={cutout.id} className="order-review-cutout-summary">
                      <div className="order-review-variation secondary">
                        <span>{`Cut-out ${index + 1}`}</span>
                        <strong>{getCutoutReviewLabel(cutout)}</strong>
                      </div>
                      <div className="order-review-inline-grid">
                        <ReviewInlinePair label="Width:" value={`${cutout.width}mm`} />
                        <ReviewInlinePair label="Depth:" value={`${cutout.depth}mm`} />
                      </div>
                      <ReviewInlinePair label="Cut-out side:" value={cutout.side || cutout.corner || '-'} />
                      <ReviewInlinePair label="Distance from edge (mm)" value={`${getCutoutDistanceValue(cutout)}mm`} />
                    </div>
                  ))}
                </div>

                <div className="order-review-block figma-block">
                  <ReviewSectionTitle title="Fabrication Details" onEdit={() => beginReviewEdit('fabrication')} />
                  <div className="order-review-inline-grid two-tight">
                    <ReviewInlinePair label="Construction" value={getConstructionLabel(state.fabrication)} />
                    <ReviewInlinePair label="Approx. Weight" value={`${Math.round(Number(estimatedWeight))} kg`} />
                  </div>
                  <div className="order-review-inline-grid two-tight">
                    {fabricationReviewRows.slice(1).map(row => (
                      <ReviewInlinePair key={row.label} label={`${row.label}:`} value={row.value} />
                    ))}
                  </div>
                </div>

                <div className="order-review-totals figma-totals">
                  <ReviewInlinePair label="Base Fabrication" value={formatMoney(pricingTier.price)} />
                  <ReviewInlinePair label="Delivery" value={deliveryPrice ? formatMoney(deliveryPrice) : '$0.00'} />
                  <ReviewInlinePair label="Total (Inc. GST)" value={formatMoney(displayTotal)} />
                </div>

                <div className="order-review-contact-box">
                  <div className="order-review-contact-block">
                    <span>Contact</span>
                    <strong>{state.contact.fullName || '-'}</strong>
                    <strong>{state.contact.email || '-'}</strong>
                    <strong>{state.contact.phone || '-'}</strong>
                  </div>
                  <div className="order-review-contact-block">
                    <span>Delivery Address</span>
                    <strong>{getFulfilmentAddressSummary(state.fulfilment)}</strong>
                  </div>
                  <div className="order-review-contact-block">
                    <span>Authority to leave?</span>
                    <strong>{state.fulfilment.method === 'Delivery' ? (state.fulfilment.authorityToLeave || '-') : '-'}</strong>
                  </div>
                </div>

                {softWarnings.length > 0 && (
                  <div className="order-inline-alert warn">
                    {softWarnings.map(warning => <div key={warning}>{warning}</div>)}
                  </div>
                )}
                {state.submitError && <div className="order-inline-alert danger">{state.submitError}</div>}
              </div>
            </div>
          </div>
          <div className="order-preview-column">
            <OrderPreview
              dimensions={state.dimensions}
              squareCorners={state.squareCorners}
              entrySides={state.entrySides}
              drain={state.drain}
              cutouts={state.cutoutsEnabled ? state.cutouts : []}
              setbacks={state.setbacks}
              config={pricingTier}
              fabrication={state.fabrication}
              focusedLeg={state.focusedLeg}
              legHeights={state.legHeights}
              currentStep={state.currentStep}
              stepOneSummaryOpen={state.stepOneSummaryOpen}
              quickApplyFocused={quickApplyFocused}
              onOpenHelp={setActiveHelpKey}
              onCaptureReady={capture => {
                previewCaptureRef.current = capture
              }}
            />
          </div>
        </div>
        <footer className="order-footer">
          <div className="order-footer-summary">
            <strong>Cost Summary</strong>
            <span>Estimated Total: <em>{formatMoney(displayTotal)}</em> <small>(incl. 10% tax)</small></span>
          </div>
          <div className="order-footer-actions">
            <button type="button" className="order-back-button" onClick={goPrev}>
              <ButtonChevron direction="left" />
              <span>Contact Details</span>
            </button>
            <button type="button" className="order-primary-button footer" onClick={startCheckout} disabled={state.isStartingCheckout}>
              <span>{state.isStartingCheckout ? 'Redirecting…' : 'Checkout'}</span>
              <ButtonChevron />
            </button>
          </div>
        </footer>
        {state.reviewEditTarget && getReviewEditConfig(state.reviewEditTarget) && (
          <div className="order-modal-backdrop">
            <div className="order-modal order-review-warning-modal">
              <button
                type="button"
                className="order-modal-close order-review-warning-close"
                onClick={() => updateState(previous => ({ ...previous, reviewEditTarget: null }))}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M14.166 5.8335L5.83268 14.1668M5.83268 5.8335L14.166 14.1668" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="order-review-warning-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#F04438" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="order-review-warning-title">{getReviewEditConfig(state.reviewEditTarget).title}</h3>
              <p className="order-review-warning-body">{getReviewEditConfig(state.reviewEditTarget).body}</p>
              <p className="order-review-warning-confirm">Are you sure you want to continue?</p>
              <div className="order-review-warning-actions">
                <button type="button" className="order-primary-button full" onClick={continueReviewEdit}>
                  <span>{getReviewEditConfig(state.reviewEditTarget).continueLabel}</span>
                </button>
                <button type="button" className="order-back-button full" onClick={() => updateState(previous => ({ ...previous, reviewEditTarget: null }))}>
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (state.mode === 'confirmation') {
    return (
      <div className="order-route-shell is-review">
        <div className="order-main-column order-confirmation-layout">
          <section className="order-confirmation-banner">
            <h1>Your order was successfully placed!</h1>
            <p>An order confirmation has been sent to {state.contact.email || 'your email address'}.</p>
            <strong>Order Reference: {state.submittedReference}</strong>
          </section>
          <div className="order-review-card confirmation">
            <header className="order-review-header">
              <div>
                <h2>Order Summary</h2>
              </div>
            </header>
            <div className="order-review-block">
              <h3>Custom Shower Base Insert</h3>
              <div className="order-review-grid">
                <ReviewMetric label="Front Width" value={`${state.dimensions.frontWidth}mm`} />
                <ReviewMetric label="Rear Width" value={`${state.dimensions.rearWidth}mm`} />
                <ReviewMetric label="Left Depth" value={`${state.dimensions.leftDepth}mm`} />
                <ReviewMetric label="Right Depth" value={`${state.dimensions.rightDepth}mm`} />
                <ReviewMetric label="Square Corner" value={state.squareCorners.join(', ') || 'None'} />
                <ReviewMetric label="Entry Side" value={state.entrySides.join(', ') || 'None'} />
                <ReviewMetric label="Leg Configuration" value={`${pricingTier.cols} × ${pricingTier.rows} (${pricingTier.legs} legs)`} />
                <ReviewMetric label="Drain Size" value={toNumber(state.drain.depth) > 0 && toNumber(state.drain.depth) !== toNumber(state.drain.width) ? `${state.drain.width}mm × ${state.drain.depth}mm` : `${state.drain.width}mm`} />
                <ReviewMetric label="Distance from Front" value={`${state.drain.fromFront}mm`} />
                <ReviewMetric label="Distance from Right" value={`${drainDistanceFromRight}mm`} />
                <ReviewMetric label="Construction" value={getConstructionLabel(state.fabrication)} />
              </div>
            </div>
            <div className="order-review-block">
              <h3>Totals</h3>
              <div className="order-review-totals">
                <ReviewMetric label="Base Fabrication" value={formatMoney(pricingTier.price)} />
                <ReviewMetric label="Delivery" value={deliveryPrice ? formatMoney(deliveryPrice) : '$0.00'} />
                <ReviewMetric label="Total (Inc. GST)" value={formatMoney(displayTotal)} />
              </div>
            </div>
            <div className="order-review-block">
              <h3>Contact</h3>
              <div className="order-review-grid">
                <ReviewMetric label="Contact" value={state.contact.fullName || '-'} />
                <ReviewMetric label="Fulfilment" value={state.fulfilment.method} />
                <ReviewMetric label="Delivery Address" value={getFulfilmentAddressSummary(state.fulfilment)} />
                <ReviewMetric label="Authority to leave?" value={state.fulfilment.method === 'Delivery' ? state.fulfilment.authorityToLeave || '-' : '-'} />
              </div>
            </div>
            <div className="order-review-block">
              <h3>Additional Information</h3>
              <div className="order-review-grid compact">
                <ReviewMetric label="Additional Information" value={state.additionalInfo || '-'} />
                <ReviewMetric label="Cut-outs" value={activeCutouts.length ? activeCutouts.map(getCutoutReviewLabel).join(', ') : 'None'} />
                <ReviewMetric label="Finger Holes" value={fabricationReviewRows.slice(1).map(row => `${row.label}: ${row.value}`).join(' | ')} />
              </div>
              <ReviewUploadList uploads={state.uploads} />
            </div>
            <div className="order-confirmation-note">
              <strong>Please Note</strong>
              <p>Our team will review your specifications within 24 hours. Once manufacturing starts, changes can no longer be made.</p>
            </div>
            <div className="order-confirmation-note">
              <strong>Complete Your Setup</strong>
              <p>Need a ramp for your new shower base? Customise your ramp dimensions to ensure a perfect fit.</p>
              <button
                type="button"
                className="order-primary-button"
                onClick={() => window.open('https://equipsy.com.au/product-category/ramp/rubber-ramps/', '_blank', 'noopener,noreferrer')}
              >
                <span>Customise Ramp</span>
                <ButtonChevron />
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`order-route-shell${isMobileLayout ? ' order-route-shell-mobile' : ''}`}>
      {isMobileLayout ? (
        <>
          {showMobileFixedPreview && (
            <div className="order-mobile-preview-fixed">
              <OrderPreview
                dimensions={state.dimensions}
                squareCorners={state.squareCorners}
                entrySides={state.entrySides}
                drain={state.drain}
                cutouts={state.cutoutsEnabled ? state.cutouts : []}
                setbacks={state.setbacks}
                config={pricingTier}
                fabrication={state.fabrication}
                focusedLeg={state.focusedLeg}
                legHeights={state.legHeights}
                currentStep={state.currentStep}
                stepOneSummaryOpen={state.stepOneSummaryOpen}
                quickApplyFocused={quickApplyFocused}
                onOpenHelp={setActiveHelpKey}
              />
            </div>
          )}
          <div className={`order-mobile-configurator${showMobileFixedPreview ? ' has-preview' : ' no-preview'}`}>
            {showMobileFixedPreview ? (
              <section ref={mobileAccordionRef} className={`order-mobile-accordion${mobileAccordionOpen ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="order-step-mobile-head"
                  onClick={() => updateState(previous => ({
                    ...previous,
                    mobileSectionOpen: previous.mobileSectionOpen === previous.currentStep ? 0 : previous.currentStep,
                  }))}
                >
                  <span><StepIcon type={getStepIconType(state.currentStep)} /> {mobileAccordionOpen ? stepLabels[state.currentStep - 1] : mobileAccordionClosedLabels[state.currentStep]}</span>
                  <span className={`order-chevron${mobileAccordionOpen ? ' open' : ''}`}>⌄</span>
                </button>
                {mobileAccordionOpen && (
                  <div className="order-mobile-accordion-panel">
                    {state.currentStep >= 1 && state.currentStep <= 6 && (
                      <VideoHelpCard
                        title={getPreviewHelpTitle(state.currentStep, state.stepOneSummaryOpen)}
                        description={getPreviewHelpDescription(state.currentStep, state.stepOneSummaryOpen)}
                        thumbnail={orderHelpContent[getPreviewHelpKey(state.currentStep, state.stepOneSummaryOpen)]?.thumbnail || constructionImage}
                        onClick={() => {
                          const helpKey = getPreviewHelpKey(state.currentStep, state.stepOneSummaryOpen)
                          if (helpKey) setActiveHelpKey(helpKey)
                        }}
                      />
                    )}
                    {contentByStep[state.currentStep]}
                    {softWarnings.length > 0 && (
                      <div className="order-mobile-inline-warnings">
                        {softWarnings.map(warning => <span key={warning}>{warning}</span>)}
                      </div>
                    )}
                    <div className="order-mobile-inline-nav">
                      {(state.currentStep > 1 || state.stepOneSummaryOpen) && (
                        <button type="button" className="order-back-button order-mobile-nav-button" onClick={goPrev}>
                          <ButtonChevron direction="left" />
                          <span>{previousStepLabel}</span>
                        </button>
                      )}
                      <button type="button" className="order-primary-button order-mobile-nav-button" onClick={goNext} disabled={stepBlocked(state.currentStep) || state.isCapturingLead}>
                        <span>{state.isCapturingLead ? 'Saving…' : state.currentStep === STEP_TOTAL ? 'Review Order' : nextStepLabel}</span>
                        <ButtonChevron />
                      </button>
                    </div>
                  </div>
                )}
              </section>
            ) : (
              <section ref={mobileAccordionRef} className="order-mobile-plain-step">
                {contentByStep[state.currentStep]}
                {softWarnings.length > 0 && (
                  <div className="order-mobile-inline-warnings">
                    {softWarnings.map(warning => <span key={warning}>{warning}</span>)}
                  </div>
                )}
                <div className="order-mobile-inline-nav">
                  {(state.currentStep > 1 || state.stepOneSummaryOpen) && (
                    <button type="button" className="order-back-button order-mobile-nav-button" onClick={goPrev}>
                      <ButtonChevron direction="left" />
                      <span>{previousStepLabel}</span>
                    </button>
                  )}
                  <button type="button" className="order-primary-button order-mobile-nav-button" onClick={goNext} disabled={stepBlocked(state.currentStep) || state.isCapturingLead}>
                    <span>{state.isCapturingLead ? 'Saving…' : state.currentStep === STEP_TOTAL ? 'Review Order' : nextStepLabel}</span>
                    <ButtonChevron />
                  </button>
                </div>
              </section>
            )}
          </div>
        </>
      ) : (
        <>
          {!hideConfiguratorPreview && (
            <div className="order-mobile-preview-fixed">
              <OrderPreview
                dimensions={state.dimensions}
                squareCorners={state.squareCorners}
                entrySides={state.entrySides}
                drain={state.drain}
                cutouts={state.cutoutsEnabled ? state.cutouts : []}
                setbacks={state.setbacks}
                config={pricingTier}
                fabrication={state.fabrication}
                focusedLeg={state.focusedLeg}
                legHeights={state.legHeights}
                currentStep={state.currentStep}
                stepOneSummaryOpen={state.stepOneSummaryOpen}
                quickApplyFocused={quickApplyFocused}
                onOpenHelp={setActiveHelpKey}
              />
            </div>
          )}
          <div className={`order-configurator-layout${hideConfiguratorPreview ? ' no-preview' : ''}${state.currentStep === 8 ? ' contact-layout' : ''}`}>
            <div className="order-main-column">
              <section className="order-step-single">
                <div className="order-step-mobile-head">
                  <span><StepIcon type={getStepIconType(state.currentStep)} /> {stepLabels[state.currentStep - 1]}</span>
                  <span className="order-chevron">⌄</span>
                </div>
                {contentByStep[state.currentStep]}
              </section>
            </div>
            {!hideConfiguratorPreview && (
              <div className="order-preview-column">
                <OrderPreview
                  dimensions={state.dimensions}
                  squareCorners={state.squareCorners}
                  entrySides={state.entrySides}
                  drain={state.drain}
                  cutouts={state.cutoutsEnabled ? state.cutouts : []}
                  setbacks={state.setbacks}
                  config={pricingTier}
                  fabrication={state.fabrication}
                  focusedLeg={state.focusedLeg}
                  legHeights={state.legHeights}
                  currentStep={state.currentStep}
                  stepOneSummaryOpen={state.stepOneSummaryOpen}
                  quickApplyFocused={quickApplyFocused}
                  onOpenHelp={setActiveHelpKey}
                />
              </div>
            )}
          </div>
          <footer className="order-footer">
            <div className="order-footer-summary">
              <strong>Cost Summary</strong>
              <span>Estimated Total: <em>{formatMoney(displayTotal)}</em> <small>(incl. 10% tax)</small></span>
            </div>
            <div className="order-footer-actions">
              {(state.currentStep > 1 || state.stepOneSummaryOpen) && (
                <button type="button" className="order-back-button" onClick={goPrev}>
                  <ButtonChevron direction="left" />
                  <span>{previousStepLabel}</span>
                </button>
              )}
              <button type="button" className="order-primary-button footer" onClick={goNext} disabled={stepBlocked(state.currentStep) || state.isCapturingLead}>
                <span>{state.isCapturingLead ? 'Saving…' : state.currentStep === STEP_TOTAL ? 'Review Order' : nextStepLabel}</span>
                <ButtonChevron />
              </button>
            </div>
          </footer>
        </>
      )}
      {state.showLockModal && (
        <div className="order-modal-backdrop">
          <div className="order-modal">
            <div className="order-modal-head">
              <div className="order-modal-head-row">
                <span className="order-modal-warning" aria-hidden="true">
                  <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9.93258 6.99975V8.99975M9.93258 12.9998H9.94258M3.00458 16.9998H16.8606C18.4006 16.9998 19.3626 15.3328 18.5926 13.9998L11.6646 1.99975C10.8946 0.66675 8.97058 0.66675 8.20058 1.99975L1.27258 13.9998C0.502576 15.3328 1.46458 16.9998 3.00458 16.9998Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <div className="order-modal-head-copy">
                  <h3>Before You Proceed</h3>
                  <p>Please review your dimensions</p>
                </div>
              </div>
            </div>
            <div className="order-modal-copy">
              Make sure you have entered your dimensions correctly before proceeding, because the <strong>number of legs is determined by base size.</strong>
            </div>
            <div className="order-modal-note">
              <span className="order-modal-note-icon" aria-hidden="true">💡</span>
              <span>Once you proceed, the leg configuration will be locked based on your current dimensions.</span>
            </div>
            <div className="order-modal-actions">
              <button type="button" className="order-back-button" onClick={() => updateState(previous => ({ ...previous, showLockModal: false }))}>
                <span>Go Back</span>
              </button>
              <button
                type="button"
                className="order-primary-button"
                onClick={() => updateState(previous => ({
                  ...previous,
                  showLockModal: false,
                  stepOneSummaryOpen: false,
                  currentStep: 2,
                  mobileSectionOpen: 2,
                }))}
              >
                <span>Continue to Leg Setbacks</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {activeHelpKey ? <OrderHelpModal helpKey={activeHelpKey} onClose={() => setActiveHelpKey('')} /> : null}
      {!isMobileLayout && softWarnings.length > 0 && (
        <div className="order-floating-warnings">
          {softWarnings.map(warning => <span key={warning}>{warning}</span>)}
        </div>
      )}
    </div>
  )
}

export default ShowerBaseOrder
